import { DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import * as userApi from '@apis/admin-user';
import * as api from '@apis/product';
import * as feedApi from '@apis/feeds-v2';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { DaumPostcodeModal } from '@components/class-invoice/DaumPostcodeModal';
import FileUploadButton from '@components/common/FileUploadButton';
import LoadingButton from '@components/common/LoadingButton';
import { TextArea, TextInput } from '@components/common/TextInput';
import { ClassDetailEdit } from '@components/product/ClassDetailEdit';
import DistrictSelect from '@components/product/DistrictSelect/DistrictSelect';
import ProductDetailView from '@components/product/ProductDetailView';
import { OptionDescriptionSetting } from '@components/product/OptionDescriptionSetting';
import { ProductOptionEditView } from '@components/product/ProductOptionEditView';
import { DEFAULT_PREFERRED_SCHEDULE, PreferredScheduleConfigPanel } from '@components/product/PreferredScheduleConfig';
import { SelectUser } from '@components/product/SelectUser';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAppSocket } from '@hooks/appSocket';
import { UserProfile } from '@models/auth';
import { ClassBadge } from '@models/class';
import {
  Concept,
  LinkedSalesChannelStatus,
  ProductActivityType,
  ProductApproveStatus,
  ProductModel,
  ProductStatus,
  PRODUCT_ACTIVITY_TYPE_TEXT,
  QuestionOption,
  LabelInfo,
} from '@models/product';
import { IntelligenceType, INTELLIGENCE_TYPE_LABEL } from '@models/programReport';
import {
  NaverCommerceProduct,
  NaverCommerceProductDisplayStatusLabel,
  NaverCommerceProductStatusLabel,
} from '@models/sales-channels';
import { useAuthStore } from '@stores/authStore';
import { useContentLoaderStore } from '@stores/contentLoaderStore';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CKEditorIgogoImageUploader } from '@utils/ckeditorImageUpload';
import { isAdmin } from '@utils/common';
import { removeTempProperties } from '@utils/dataTransform';
import { cdnImageUrl, resizeImage, splitImage } from '@utils/image';
import { extractFileName } from '@utils/string';
import { WEB_URL } from '@variables';
import { Button, Checkbox, Collapse, DatePicker, Input, Modal, Select, Switch, Tooltip } from 'antd';
import { RcFile } from 'antd/es/upload';
import axios from 'axios';
import * as cheerio from 'cheerio';
import dayjs from 'dayjs';
import domtoimage from 'dom-to-image';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { v4 } from 'uuid';
import ProductDetailPreview from './ProductDetailPreview';
import { ProductInformation } from './ProductInformation';
import { ProductRefundPolicy } from './RefundPolicy';

const QUESTION_OPTION_LABELS: { [key: string]: string } = {
  name: '참가자 이름',
  birthYear: '출생연도',
  schoolYear: '학년',
  friends: '함께 신청한 친구(없을 경우 X)',
  gender: '성별',
  preferredSchedule: '희망 일정',
  none: '직접입력',
};

const QUESTION_OPTION_DEFAULT_QUESTION: { [key: string]: string } = {
  name: '참가자 이름',
  birthYear: '출생연도',
  schoolYear: '학년',
  friends: '함께 신청한 친구(없을 경우 X)',
  gender: '성별',
  preferredSchedule: '희망 일정',
  none: '',
};

// 쿠팡 링크 파싱 함수
const parseCoupangLink = (htmlString: string) => {
  try {
    const $ = cheerio.load(htmlString);
    const linkElement = $('a[href*="link.coupang.com"]');
    const imgElement = linkElement.find('img');

    if (linkElement.length === 0 || imgElement.length === 0) {
      throw new Error('쿠팡 링크 형식이 올바르지 않습니다.');
    }

    const linkUrl = linkElement.attr('href') || '';
    const thumbnailImage = imgElement.attr('src') || '';
    const thunbnailAltText = imgElement.attr('alt') || '';
    const width = parseInt(imgElement.attr('width') || '0');
    const height = parseInt(imgElement.attr('height') || '0');

    return {
      linkUrl,
      thumbnailImage,
      thunbnailAltText,
      width,
      height,
    };
  } catch (error) {
    throw new Error('쿠팡 링크를 파싱할 수 없습니다.');
  }
};

// 쿠팡 준비물 관리 컴포넌트
interface CoupangPreparationItem {
  linkUrl: string;
  thumbnailImage: string;
  thunbnailAltText: string;
  width: number;
  height: number;
}

interface CoupangProductsManagerProps {
  coupangProduct:
    | {
        title: string;
        products: CoupangPreparationItem[];
      }
    | undefined;
  onAddPreparation: (preparation: CoupangPreparationItem) => void;
  onRemovePreparation: (index: number) => void;
  onUpdateTitle: (title: string) => void;
  onReorderProducts: (newProducts: CoupangPreparationItem[]) => void;
}

const CoupangProductsManager: React.FC<CoupangProductsManagerProps> = ({
  coupangProduct,
  onAddPreparation,
  onRemovePreparation,
  onUpdateTitle,
  onReorderProducts,
}) => {
  const [linkInput, setLinkInput] = useState('');
  const sensors = useSensors(useSensor(PointerSensor));

  const handleAddPreparation = () => {
    if (!linkInput.trim()) {
      toast.error('쿠팡 링크를 입력해주세요.');
      return;
    }

    try {
      const parsedData = parseCoupangLink(linkInput);
      onAddPreparation(parsedData);
      setLinkInput('');
      toast.success('쿠팡 준비물이 추가되었습니다.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '쿠팡 링크를 파싱할 수 없습니다.');
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const preparations = coupangProduct?.products || [];
      const oldIndex = preparations.findIndex((_, index) => index.toString() === active.id);
      const newIndex = preparations.findIndex((_, index) => index.toString() === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newProducts = arrayMove(preparations, oldIndex, newIndex);
        onReorderProducts(newProducts);
      }
    }
  };

  const preparations = coupangProduct?.products || [];
  const currentTitle = coupangProduct?.title || '';

  return (
    <div style={{ marginTop: '12px' }}>
      <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>제목 설정</div>
      <TextInput
        style={{ marginBottom: '12px' }}
        placeholder="제목을 입력하세요..."
        value={currentTitle}
        onChangeValue={onUpdateTitle}
      />

      <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
        {`쿠팡 상품 추가하기 (쿠팡파트너스 > 링크 생성 >블로그용 태그 붙여넣기)`}
      </div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        <TextArea
          style={{ flex: 1 }}
          placeholder="쿠팡 링크를 붙여넣으세요..."
          value={linkInput}
          onChangeValue={setLinkInput}
          rows={2}
        />
        <Button type="primary" onClick={handleAddPreparation}>
          추가
        </Button>
      </div>

      {preparations.length > 0 && (
        <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
          상품 목록 (드래그하여 순서 변경)
        </div>
      )}

      {preparations.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={preparations.map((_, index) => index.toString())}
            strategy={horizontalListSortingStrategy}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {preparations.map((preparation, index) => (
                <SortableCoupangProduct
                  key={`coupang-product-${index}`}
                  id={index.toString()}
                  preparation={preparation}
                  onRemove={() => onRemovePreparation(index)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

// 드래그 가능한 쿠팡 상품 컴포넌트
interface SortableCoupangProductProps {
  id: string;
  preparation: CoupangPreparationItem;
  onRemove: () => void;
}

const SortableCoupangProduct: React.FC<SortableCoupangProductProps> = ({ id, preparation, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        padding: '8px',
        border: '1px solid #eaeaea',
        borderRadius: '8px',
        backgroundColor: '#fafafa',
        cursor: 'grab',
      }}
      {...attributes}>
      <div
        style={{
          width: '20px',
          height: '20px',
          marginRight: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999',
          fontSize: '12px',
          cursor: 'grab',
        }}
        {...listeners}>
        ⋮⋮
      </div>
      <img
        src={preparation.thumbnailImage}
        alt={preparation.thunbnailAltText}
        style={{
          width: '60px',
          height: '60px',
          objectFit: 'cover',
          borderRadius: '4px',
          marginRight: '12px',
        }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>{preparation.thunbnailAltText}</div>
        <div style={{ fontSize: '11px', color: '#666', wordBreak: 'break-all' }}>{preparation.linkUrl}</div>
        <div style={{ fontSize: '11px', color: '#999' }}>
          크기: {preparation.width} x {preparation.height}
        </div>
      </div>
      <Button
        type="text"
        danger
        icon={<DeleteOutlined />}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRemove();
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        style={{ marginLeft: '8px' }}
      />
    </div>
  );
};

interface SortableDetailImageProps {
  onDeleteDetailImage?: (event: any) => void;
  value?: any;
  key: any;
  id: any;
  imageUrl: string;
}

export const SortableDetailImage: React.FC<SortableDetailImageProps> = (props) => {
  const [hover, setHover] = useState<boolean>();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: props.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const { onDeleteDetailImage, imageUrl } = props;
  const onClickRemove = (event: any) => {
    event.preventDefault();
    onDeleteDetailImage && onDeleteDetailImage(event);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onMouseEnter={() => {
        setHover(true);
      }}
      onMouseLeave={() => {
        setHover(false);
      }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          width: '184px',
        }}>
        <div style={{ width: '184px', height: '184px', position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
          <img src={cdnImageUrl(imageUrl)} style={{ width: '184px', height: '184px', objectFit: 'cover' }}></img>
          {hover && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                left: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
              <button className="link-button" style={{ width: '48px', height: '48px' }} onClick={onClickRemove}>
                <div style={{ color: 'white' }}>
                  <DeleteOutlined style={{ color: 'white' }} size={48} />
                  <div>삭제하기</div>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface SortableNaverTopLinkBannerProps {
  id: string;
  imageUrl: string;
  linkUrl: string;
  onChangeUrl: (url: string) => void;
  onDelete: () => void;
}

const SortableNaverTopLinkBanner: React.FC<SortableNaverTopLinkBannerProps> = (props) => {
  const [hover, setHover] = useState<boolean>();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: props.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
        padding: '8px',
        border: '1px solid #eaeaea',
        borderRadius: '8px',
      }}>
      <div
        {...attributes}
        {...listeners}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          width: '120px',
          height: '120px',
          position: 'relative',
          borderRadius: '8px',
          overflow: 'hidden',
          flexShrink: 0,
          cursor: 'grab',
        }}>
        <img src={cdnImageUrl(props.imageUrl)} style={{ width: '120px', height: '120px', objectFit: 'cover' }} />
        {hover && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            <button
              className="link-button"
              style={{ width: '48px', height: '48px' }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                props.onDelete();
              }}>
              <div style={{ color: 'white' }}>
                <DeleteOutlined style={{ color: 'white' }} size={48} />
                <div>삭제</div>
              </div>
            </button>
          </div>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>클릭 URL</div>
        <Input
          value={props.linkUrl}
          placeholder="https://smartstore.naver.com/..."
          onChange={(e) => props.onChangeUrl(e.target.value)}
        />
      </div>
    </div>
  );
};

interface ProductEditViewProps {
  productId: string;
  onClose?: () => void;
  needRefetch?: () => void;
}

interface ProductEditModel extends ProductModel {
  classIntroductionImage?: string;
  thumbnailImageTemp?: string;
  detailImagesTemp?: string[];
  originData?: Partial<ProductModel>;
  productChanges?: Partial<ProductModel>;
}

const BADGES = {
  이벤트: {
    type: 'blue',
    isActive: true,
    value: '이벤트',
  },
  업데이트: {
    type: 'green',
    isActive: true,
    value: '업데이트',
  },
  할인특가: {
    type: 'red',
    isActive: true,
    value: '할인특가',
  },
  얼리버드: {
    type: 'red',
    isActive: true,
    value: '얼리버드',
  },
};

const PERIOD_DISCOUNT_EXCLUDE_CHANNEL_OPTIONS: { value: string; label: string }[] = [
  // { value: 'naver', label: '네이버 스마트스토어' },
  // { value: 'kakao', label: '카카오톡 예약하기' },
  // { value: 'benepia', label: '베네피아' },
  { value: 'app', label: '앱' },
];

const NAVER_TOP_LINK_BANNER_MAX = 10;

type NaverTopLinkBanner = { image: string; url: string };

const getNaverTopLinkBanners = (product: {
  naverTopLinkBannersTemp?: NaverTopLinkBanner[];
  naverTopLinkBanners?: NaverTopLinkBanner[];
}): NaverTopLinkBanner[] => product.naverTopLinkBannersTemp || product.naverTopLinkBanners || [];

const naverLinkBannerKey = (banners?: { image?: string; url?: string }[] | null) =>
  JSON.stringify(
    (banners || []).map((item) => ({
      image: item?.image || '',
      url: (item?.url || '').trim(),
    })),
  );

const isNaverTopLinkMissingOnStore = (banners: NaverTopLinkBanner[], detailContent?: string | null) => {
  const html = detailContent || '';
  return banners.some((banner) => {
    const url = (banner.url || '').trim();
    return !!url && !html.includes(url);
  });
};

const appendNaverTopLinkImagesToFormData = async (formData: FormData, banners: NaverTopLinkBanner[]) => {
  const items = banners.slice(0, NAVER_TOP_LINK_BANNER_MAX).filter((item) => item?.image);
  const urls: string[] = [];
  for (let i = 0; i < items.length; i++) {
    const imageUrl = cdnImageUrl(items[i].image);
    const response = await axios.get(imageUrl!, { responseType: 'blob' });
    const result = await resizeImage(response.data, {
      maxWidth: 1200,
      format: 'image/png',
    });
    const file = new File([result.image], `naverTopLink_${i}.png`) as RcFile;
    formData.append('detailContentImages', file);
    urls.push((items[i].url || '').trim());
  }
  if (urls.length > 0) {
    formData.append('naverTopLinkUrls', JSON.stringify(urls));
  }
};

const capturePreviewElementToFiles = async (
  elementId: string,
  namePrefix: string,
  options?: { maxWidth?: number; scale?: number },
): Promise<File[]> => {
  const node = document.getElementById(elementId);
  if (!node) return [];
  const scale = options?.scale ?? 3;
  const maxWidth = options?.maxWidth ?? 1200;
  const detailContentImageUrl = await domtoimage.toPng(node, {
    width: node.offsetWidth * scale,
    height: node.offsetHeight * scale,
    style: {
      transform: `scale(${scale})`,
      transformOrigin: 'top left',
    },
  });
  const response = await axios.get(detailContentImageUrl!, { responseType: 'blob' });
  const result = await resizeImage(response.data, {
    format: 'image/png',
    maxWidth,
  });
  const results = await splitImage(result.image);
  const files: File[] = [];
  results.forEach((item, index) => {
    if (!item.image) return;
    files.push(new File([item.image], `${namePrefix}_${index}.png`) as RcFile);
  });
  return files;
};

const appendEzwelSandwichImagesToFormData = async (formData: FormData) => {
  const infoFiles = await capturePreviewElementToFiles('product-information-preview', 'ezwel_product_info');
  infoFiles.forEach((file) => formData.append('ezwelProductInfoImages', file));
  const refundFiles = await capturePreviewElementToFiles('product-refund-policy-preview', 'ezwel_refund_policy');
  refundFiles.forEach((file) => formData.append('ezwelRefundPolicyImages', file));
};

const appendEzwelExclusiveImagesToFormData = (
  formData: FormData,
  pendingFiles: { [key: string]: File },
  imageNames: string[],
) => {
  imageNames.forEach((name) => {
    const file = pendingFiles[name] || Object.values(pendingFiles).find((item) => item.name === name);
    if (file) {
      formData.append('ezwelDetailTopImages', file);
    }
  });
};

const EZWEL_FIXED_TOP_IMAGE_URL = 'https://cdn.igogo.kr/ezwel/refund-guide-top.png';
const EZWEL_DEFAULT_MARGIN_RATE = 8;
const EZWEL_DEFAULT_SELL_END_DTM = '20991231';

const ezwelYyyymmddToDayjs = (value?: string) => {
  if (!value || !/^\d{8}$/.test(value)) return undefined;
  return dayjs(`${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`);
};

const formatEzwelDateLabel = (value?: string) => {
  const parsed = ezwelYyyymmddToDayjs(value);
  return parsed ? parsed.format('YYYY-MM-DD') : '-';
};

const kstTodayYyyymmdd = () => dayjs().format('YYYYMMDD');

const calcEzwelPurchasePrice = (sellPrice: number, marginRate = EZWEL_DEFAULT_MARGIN_RATE) => {
  if (!sellPrice || sellPrice <= 0) return 0;
  const rate = Number.isFinite(marginRate) ? marginRate : EZWEL_DEFAULT_MARGIN_RATE;
  const purchase = Math.round(sellPrice * (1 - rate / 100));
  if (purchase >= sellPrice) {
    return Math.max(1, sellPrice - 1);
  }
  return Math.max(1, purchase);
};

const calcEzwelMarginRate = (purchasePrice: number, sellPrice: number) => {
  if (!sellPrice || sellPrice <= 0 || !purchasePrice || purchasePrice <= 0) return EZWEL_DEFAULT_MARGIN_RATE;
  return Math.round((1 - purchasePrice / sellPrice) * 1000) / 10;
};

const parseEzwelPriceInput = (value: string) => {
  if (value === '') return '';
  const next = Number(value);
  return Number.isFinite(next) ? next : 0;
};

const buildEzwelFormPayload = (ezwelInfo: any, productId?: string) => {
  const sellPrice = Number(ezwelInfo?.sellPrice) || 0;
  const marginRate =
    ezwelInfo?.marginRate != null && Number.isFinite(Number(ezwelInfo.marginRate))
      ? Number(ezwelInfo.marginRate)
      : EZWEL_DEFAULT_MARGIN_RATE;
  const purchasePrice =
    ezwelInfo?.purchasePrice != null && Number(ezwelInfo.purchasePrice) > 0
      ? Number(ezwelInfo.purchasePrice)
      : calcEzwelPurchasePrice(sellPrice, marginRate);
  return {
    exclusive: ezwelInfo?.exclusive,
    brandCd: ezwelInfo?.brandCd,
    brandNm: ezwelInfo?.brandNm,
    useCustomPrice: !!ezwelInfo?.useCustomPrice,
    useSalePeriod: !!ezwelInfo?.useSalePeriod,
    sellStrtDtm: ezwelInfo?.sellStrtDtm || '',
    sellEndDtm: ezwelInfo?.sellEndDtm || EZWEL_DEFAULT_SELL_END_DTM,
    sellPrice,
    marginRate,
    purchasePrice,
    cspGdsCd: `${ezwelInfo?.cspGdsCd || productId || ''}`.trim(),
  };
};

const EzwelAppliedImageThumbs: React.FC<{ title: string; urls: string[] }> = ({ title, urls }) => {
  if (!urls.length) return null;
  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>{title}</div>
      <div style={{ display: 'flex', gap: '4px', overflow: 'auto', maxWidth: '600px' }}>
        {urls.map((url) => (
          <img
            key={url}
            src={cdnImageUrl(url)}
            alt={title}
            style={{
              width: '120px',
              height: '120px',
              objectFit: 'cover',
              borderRadius: '8px',
              border: '1px solid #eee',
            }}
          />
        ))}
      </div>
    </div>
  );
};

export const ProductEditView: React.FC<ProductEditViewProps> = (props) => {
  const loader = useContentLoaderStore();
  const appSocket = useAppSocket();
  const authStore = useAuthStore();
  const [files, setFiles] = useState<{ [key: string]: File }>({});
  const [modifiedProduct, setModifiedProduct] = useState<ProductEditModel>({} as ProductEditModel);
  const [naverStoreProduct, setNaverStoreProduct] = useState<NaverCommerceProduct>();
  const [changedData, setChangedData] = useState<Partial<ProductEditModel>>({});
  const [showOptionEdit, setShowOptionEdit] = useState<boolean>(false);
  const dialog = useOkCancelDialog();
  const [showPostcodeModal, setShowPostcodeModal] = useState<boolean>(false);
  const [changeCustomerCenter, setChangeCustomerCenter] = useState<Partial<UserProfile>>({});
  const [loadingEditorImageUpload, setLoadingEditorImageUpload] = useState<boolean>(false);
  const editorTimerRef = useRef<any>();
  const optionsDictRef = useRef<{ [key: string]: number }>({});
  const [naverProductInprogress, setNaverProductInprogress] = useState<boolean>(false);
  const [ezwelProductInprogress, setEzwelProductInprogress] = useState<boolean>(false);
  const [deployInprogress, setDeployInprogress] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const [downloading, setDownloading] = useState<boolean>(false);
  const [showAddLabelModal, setShowAddLabelModal] = useState<boolean>(false);
  const [showOptionUploadModal, setShowOptionUploadModal] = useState<boolean>(false);

  const getTempProductOptionsQuery = useQuery({
    queryKey: [`temp-product-options-${props.productId}`],
    queryFn: () => api.getTempProductOptions(props.productId),
    enabled: false,
  });

  const partnerUserQuery = useQuery({
    queryKey: [`user-${modifiedProduct.sellerId}`],
    queryFn: () => userApi.getUser(modifiedProduct.sellerId),
    enabled: !!modifiedProduct.sellerId,
  });

  const partnerData = partnerUserQuery.data?.data;

  const mutaionUpdatePartnerProfile = useMutation({
    mutationFn: userApi.updateUserProfile,
    onSuccess: async () => {
      dialog.open({
        content: '대표고객센터 정보가 변경되었습니다.',
        type: 'ok',
      });
    },
  });

  const { data: productRootCategories } = useQuery({
    queryKey: ['product-root-categories'],
    queryFn: feedApi.getProductRootCategories,
  });

  const tempProductOptions = getTempProductOptionsQuery.data?.data;

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: [`temp-product-options-${props.productId}`] });
    appSocket.socket.addSocketEventListener('upload_excel_status', 'upload_excel_status_listner_in_edit', (data) => {
      if (data.type === 'product_options' || data.type === 'product_options_add') {
        if (data.status.toLowerCase() === 'done') {
          getTempProductOptionsQuery.refetch();
        }

        if (data.status) {
          if (data.status === 'error_duplicate_step') {
            dialog.open({
              type: 'ok',
              title: '중복된 옵션이 있습니다.',
              content: `${data.message}`,
            });
          } else {
            toast.error(data.message, {
              position: 'top-center',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              pauseOnFocusLoss: false,
              draggable: true,
              progress: undefined,
              theme: 'light',
            });
          }
          loader.off();
        }
      }
    });
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 },
    }),
  );

  const draftProductQuery = useQuery({
    queryKey: [`product-item-${props.productId}`],
    queryFn: () => api.getDraftProduct(props.productId!),
    enabled: !!props.productId,
  });

  const productQuery = useQuery({
    queryKey: [`current-product-item-${props.productId}`],
    queryFn: () => api.getProduct(props.productId!),
    enabled: !!props.productId,
  });

  const mutationUpdateProduct = useMutation({
    mutationFn: api.updateProduct,
    onSuccess: async (res) => {
      setChangedData({});
      setDeployInprogress(false);
      setModifiedProduct(res.data);
      props.needRefetch && props.needRefetch();
      toast.dark('저장되었습니다', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        pauseOnFocusLoss: false,
        draggable: true,
        progress: undefined,
        bodyStyle: {
          color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
          textAlign: 'center',
          fontFamily: 'Pretendard',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '20px',
        },
        theme: 'dark',
      });
      draftProductQuery.refetch();
    },
    onError: (error: any) => {
      setDeployInprogress(false);
      if (error?.response?.data?.message) {
        let message = error?.response?.data?.message;

        if (error?.response?.data?.invalidInputs?.[0]?.message) {
          message = `${error?.response?.data?.message}\n${error?.response?.data?.invalidInputs?.[0]?.message}`;
        }
        dialog.open({
          title: '업데이트 오류',
          content: message,
          type: 'ok',
        });
      }
    },
  });

  const refreshNaverProductMutation = useMutation({
    mutationFn: api.refreshNaverProduct,
    onSuccess: () => {
      draftProductQuery.refetch();
      setNaverProductInprogress(false);
    },
    onError: () => {
      setNaverProductInprogress(false);
    },
  });

  const createNaverProductMutation = useMutation({
    mutationFn: api.createNaverProduct,
    onSuccess: () => {
      draftProductQuery.refetch();
      setNaverProductInprogress(false);
    },
    onError: (error: any) => {
      dialog.open({
        title: '네이버 상품 생성 오류',
        content: `${JSON.stringify(error.response.data)}`,
        type: 'ok',
      });
      setNaverProductInprogress(false);
    },
  });

  const createEzwelProductMutation = useMutation({
    mutationFn: api.createEzwelProduct,
    onSuccess: (res: any) => {
      draftProductQuery.refetch();
      setEzwelProductInprogress(false);
      const hint = res?.data?.approvalHint || res?.approvalHint;
      toast.dark(
        hint
          ? `이지웰 상품 등록 및 승인요청 완료 (gdsCd: ${res?.data?.gdsCd || res?.gdsCd || '-'})`
          : '이지웰 상품이 등록되었습니다',
        { bodyStyle: { textAlign: 'center' } },
      );
    },
    onError: (error: any) => {
      const data = error?.response?.data;
      const missing = data?.missing || data?.message?.missing;
      dialog.open({
        title: '이지웰 상품 생성 오류',
        content: missing?.length ? `필수값 누락:\n- ${missing.join('\n- ')}` : `${JSON.stringify(data || error)}`,
        type: 'ok',
      });
      setEzwelProductInprogress(false);
    },
  });

  const requestEzwelProductApprovalMutation = useMutation({
    mutationFn: api.requestEzwelProductApproval,
    onSuccess: (res: any) => {
      draftProductQuery.refetch();
      setEzwelProductInprogress(false);
      const status = res?.data?.gdsApvStCd || res?.gdsApvStCd || 'REQS';
      toast.dark(`이지웰 상품 승인요청이 완료되었습니다 (${status})`, {
        bodyStyle: { textAlign: 'center' },
      });
    },
    onError: (error: any) => {
      dialog.open({
        title: '이지웰 상품 승인요청 오류',
        content: `${JSON.stringify(error?.response?.data || error)}`,
        type: 'ok',
      });
      setEzwelProductInprogress(false);
    },
  });

  const syncEzwelProductMutation = useMutation({
    mutationFn: api.syncEzwelProduct,
    onSuccess: () => {
      draftProductQuery.refetch();
      setEzwelProductInprogress(false);
      toast.dark('이지웰 상품이 동기화되었습니다', { bodyStyle: { textAlign: 'center' } });
    },
    onError: (error: any) => {
      dialog.open({
        title: '이지웰 상품 동기화 오류',
        content: `${JSON.stringify(error?.response?.data || error)}`,
        type: 'ok',
      });
      setEzwelProductInprogress(false);
    },
  });

  const updateEzwelProductStatusMutation = useMutation({
    mutationFn: ({ productId, status }: { productId: string; status: 'open' | 'close' }) =>
      api.updateEzwelProductStatus(productId, status),
    onSuccess: () => {
      draftProductQuery.refetch();
      setEzwelProductInprogress(false);
      toast.dark('이지웰 판매상태가 변경되었습니다', { bodyStyle: { textAlign: 'center' } });
    },
    onError: (error: any) => {
      dialog.open({
        title: '이지웰 상태 변경 오류',
        content: `${JSON.stringify(error?.response?.data || error)}`,
        type: 'ok',
      });
      setEzwelProductInprogress(false);
    },
  });

  const unlinkEzwelProductMutation = useMutation({
    mutationFn: api.unlinkEzwelProduct,
    onSuccess: (res: any) => {
      draftProductQuery.refetch();
      productQuery.refetch();
      setEzwelProductInprogress(false);
      const unlinkedGdsCd = res?.data?.unlinkedGdsCd || res?.unlinkedGdsCd || '-';
      toast.dark(`이지웰 연동이 해제되었습니다 (gdsCd: ${unlinkedGdsCd})`, {
        bodyStyle: { textAlign: 'center' },
      });
    },
    onError: (error: any) => {
      dialog.open({
        title: '이지웰 연동 해제 오류',
        content: `${JSON.stringify(error?.response?.data || error)}`,
        type: 'ok',
      });
      setEzwelProductInprogress(false);
    },
  });

  const rotateEzwelCspGdsCdMutation = useMutation({
    mutationFn: api.rotateEzwelCspGdsCd,
    onSuccess: (res: any) => {
      const cspGdsCd = res?.data?.cspGdsCd || res?.cspGdsCd || '';
      if (cspGdsCd) {
        onChangeProperty('linkedSalesChannel.ezwel.cspGdsCd', cspGdsCd);
      }
      draftProductQuery.refetch();
      productQuery.refetch();
      setEzwelProductInprogress(false);
      toast.dark(`협력사상품코드가 변경되었습니다 (${cspGdsCd || '-'})`, {
        bodyStyle: { textAlign: 'center' },
      });
    },
    onError: (error: any) => {
      dialog.open({
        title: '협력사상품코드 변경 오류',
        content: `${JSON.stringify(error?.response?.data || error)}`,
        type: 'ok',
      });
      setEzwelProductInprogress(false);
    },
  });

  const mutationDeleteDraftProduct = useMutation({
    mutationFn: api.deleteDraftProduct,
    onSuccess: () => {
      toast.dark('임시데이터가 삭제되었습니다', { bodyStyle: { textAlign: 'center' } });
      props.needRefetch && props.needRefetch();
      draftProductQuery.refetch();
    },
  });

  const onChangeProperty = (key: string, inputValue: any) => {
    let value = inputValue;
    if (typeof value === 'string') {
      value = value.trim();
    }

    setChangedData((prev: any) => {
      const newProperties = { ...prev };
      const isKeyDepth = key.split('.').length > 1 ? true : false;
      if (isKeyDepth) {
        const keys = key.split('.');
        if (keys.length === 2) {
          if (!newProperties[keys[0]]) {
            if (!isNaN(Number(keys[1]))) {
              newProperties[keys[0]] = modifiedProduct[keys[0] as 'refundPolicies'] || [];
            } else {
              const isArray = Array.isArray(modifiedProduct[keys[0] as 'refundPolicies']);
              newProperties[keys[0]] = isArray ? {} : modifiedProduct[keys[0] as 'refundPolicies'] || {};
            }
          }
          newProperties[keys[0]][keys[1]] = value;
        } else if (keys.length === 3) {
          if (!newProperties[keys[0]]) {
            if (!isNaN(Number(keys[1]))) {
              newProperties[keys[0]] = modifiedProduct[keys[0] as 'refundPolicies'] || [];
            } else {
              newProperties[keys[0]] = modifiedProduct[keys[0] as 'refundPolicies'] || {};
            }
          }
          if (!newProperties[keys[0]]) {
            newProperties[keys[0]] = {};
          }
          if (!newProperties[keys[0]][keys[1]]) {
            const nested = (modifiedProduct[keys[0] as keyof ProductEditModel] as Record<string, any> | undefined)?.[
              keys[1]
            ];
            newProperties[keys[0]][keys[1]] = Array.isArray(nested) ? [...nested] : { ...(nested || {}) };
          }
          newProperties[keys[0]][keys[1]][keys[2]] = value;
        } else if (keys.length === 5) {
          // linkedSalesChannel.kakaoReservation.optionNamePrices.${index}.value
          if (!newProperties[keys[0]]) {
            newProperties[keys[0]] = modifiedProduct[keys[0] as 'refundPolicies'] || {};
          }
          if (!newProperties[keys[0]]) {
            newProperties[keys[0]] = {};
          }
          if (!newProperties[keys[0]][keys[1]]) {
            newProperties[keys[0]][keys[1]] = {};
          }
          if (!newProperties[keys[0]][keys[1]][keys[2]]) {
            newProperties[keys[0]][keys[1]][keys[2]] = [];
          }
          if (!newProperties[keys[0]][keys[1]][keys[2]][keys[3]]) {
            newProperties[keys[0]][keys[1]][keys[2]][keys[3]] = {};
          }
          newProperties[keys[0]][keys[1]][keys[2]][keys[3]][keys[4]] = value;
        }
      } else {
        newProperties[key] = value;
      }

      return newProperties;
    });
  };

  const onChangeBadgeChecked = (key: '이벤트' | '업데이트' | '할인특가' | '얼리버드', checked: boolean) => {
    let badges = (changedData?.badges || modifiedProduct.badges || []).filter((badge: ClassBadge) => badge.isActive);

    if (checked) {
      badges.push(BADGES[key]);
    } else {
      badges = badges.filter((badge: ClassBadge) => badge.value !== key);
    }
    onChangeProperty('badges', badges);
  };

  const onChangeTemplateSectionUsage = (section: string, checked: boolean) => {
    const disableKey = `disable${section.charAt(0).toUpperCase() + section.slice(1)}` as
      | 'disableDetailImage'
      | 'disableClassIntroduction'
      | 'disableRecommendText'
      | 'disableConcepts'
      | 'disablePreparationText';

    onChangeProperty(`details.${disableKey}`, !checked);
  };

  useEffect(() => {
    if (tempProductOptions) {
      onChangeProperty('productOptions', tempProductOptions.productOptions);
      onChangeProperty('optionLabels', tempProductOptions.optionLabels);
    }
  }, [tempProductOptions]);

  // 쿠팡 준비물 관리 함수들
  const addCoupangPreparation = (preparation: CoupangPreparationItem) => {
    const currentProduct = changedData?.coupangProduct || modifiedProduct.coupangProduct;
    const currentProducts = currentProduct?.products || [];
    const newProducts = [...currentProducts, preparation];
    const newCoupangProduct = {
      title: currentProduct?.title || '체험에서 배움으로 경험을 완성해봐요',
      products: newProducts,
    };
    onChangeProperty('coupangProduct', newCoupangProduct);
  };

  const removeCoupangPreparation = (index: number) => {
    const currentProduct = changedData?.coupangProduct || modifiedProduct.coupangProduct;
    const currentProducts = currentProduct?.products || [];
    const newProducts = currentProducts.filter((_, i) => i !== index);
    const newCoupangProduct = {
      title: currentProduct?.title || '체험에서 배움으로 경험을 완성해봐요',
      products: newProducts,
    };
    onChangeProperty('coupangProduct', newCoupangProduct);
  };

  const updateCoupangTitle = (title: string) => {
    const currentProduct = changedData?.coupangProduct || modifiedProduct.coupangProduct;
    const currentProducts = currentProduct?.products || [];
    const newCoupangProduct = {
      title: title,
      products: currentProducts,
    };
    onChangeProperty('coupangProduct', newCoupangProduct);
  };

  const reorderCoupangProducts = (newProducts: CoupangPreparationItem[]) => {
    const currentProduct = changedData?.coupangProduct || modifiedProduct.coupangProduct;
    const newCoupangProduct = {
      title: currentProduct?.title || '쿠팡에서 준비물 구매하기',
      products: newProducts,
    };
    onChangeProperty('coupangProduct', newCoupangProduct);
  };

  const onChangeClassDetailImage = async (files: FileList) => {
    const { fileName } = extractFileName(files[0].name);
    const newFiles = await Promise.all(
      Array.from(files)
        .slice(0, 10)
        .map(async (file) => {
          const result = await resizeImage(file, { maxWidth: 1200 });

          return result;
        }),
    );
    const contentTypes = newFiles[0].type.split('/');
    const uploadFileName = `${fileName}.${contentTypes[1]}`;
    const newImageFile = new File([newFiles[0].image], `${fileName}.${contentTypes[1]}`) as RcFile;
    const tempUrl = URL.createObjectURL(newFiles[0].image);
    onChangeProperty('classIntroductionImageTemp', tempUrl);
    onChangeProperty('classIntroductionImage', uploadFileName);
    setFiles((value) => {
      const newValue = { ...value };
      newValue['classIntroductionImage'] = newImageFile;
      return newValue;
    });
  };

  const onChangeTemplateDetailImage = async (files: FileList) => {
    const { fileName } = extractFileName(files[0].name);
    const newFiles = await Promise.all(
      Array.from(files)
        .slice(0, 10)
        .map(async (file) => {
          const result = await resizeImage(file, { maxWidth: 1200 });

          return result;
        }),
    );
    const contentTypes = newFiles[0].type.split('/');
    const uploadFileName = `${fileName}.${contentTypes[1]}`;
    const newImageFile = new File([newFiles[0].image], `${fileName}.${contentTypes[1]}`) as RcFile;
    const tempUrl = URL.createObjectURL(newFiles[0].image);
    onChangeProperty('details.detailImageTemp', tempUrl);
    onChangeProperty('details.detailImage', uploadFileName);
    setFiles((value) => {
      const newValue = { ...value };
      newValue['details.detailImage'] = newImageFile;
      return newValue;
    });
  };

  const onChangeBottomTextBannerIconFile = async (files: FileList) => {
    const { fileName } = extractFileName(files[0].name);
    const newFiles = await Promise.all(
      Array.from(files)
        .slice(0, 10)
        .map(async (file) => {
          const result = await resizeImage(file, { maxWidth: 512 });

          return result;
        }),
    );
    const contentTypes = newFiles[0].type.split('/');
    const uploadFileName = `${fileName}.${contentTypes[1]}`;
    const newImageFile = new File([newFiles[0].image], `${fileName}.${contentTypes[1]}`) as RcFile;
    const tempUrl = URL.createObjectURL(newFiles[0].image);
    onChangeProperty('bottomTextBanner.iconTemp', tempUrl);
    onChangeProperty('bottomTextBanner.icon', uploadFileName);
    setFiles((value) => {
      const newValue = { ...value };
      newValue['bottomTextBanner.icon'] = newImageFile;
      return newValue;
    });
  };

  const onChangeThumbnailFile = async (files: FileList) => {
    const { fileName } = extractFileName(files[0].name);
    const newFiles = await Promise.all(
      Array.from(files)
        .slice(0, 10)
        .map(async (file) => {
          const result = await resizeImage(file, { maxWidth: 512 });

          return result;
        }),
    );
    const contentTypes = newFiles[0].type.split('/');
    const uploadFileName = `${fileName}.thumb.${contentTypes[1]}`;
    const newThumbnailFile = new File([newFiles[0].image], `${fileName}.thumb.${contentTypes[1]}`) as RcFile;
    const tempUrl = URL.createObjectURL(newFiles[0].image);
    onChangeProperty('thumbnailImageTemp', tempUrl);
    onChangeProperty('thumbnailImage', uploadFileName);
    setFiles((value) => {
      const newValue = { ...value };
      newValue['thumbnailImage'] = newThumbnailFile;
      return newValue;
    });
  };

  const onChangeDetailImageFiles = async (files: FileList) => {
    const imageFiles: any = {};
    const newFiles = await Promise.all(
      Array.from(files).map(async (file) => {
        const result = await resizeImage(file, { maxWidth: 1000 });
        const { fileName } = extractFileName(file.name);
        const contentTypes = result.type.split('/');
        const imageFile = new File([result.image], `${fileName}.${contentTypes[1]}`) as RcFile;
        const tempUrl = URL.createObjectURL(result.image);

        imageFiles[file.name] = imageFile;

        return { result, fileName: `${fileName}.${contentTypes[1]}`, imageFile, tempUrl };
      }),
    );

    setFiles((value) => {
      const newValue = { ...value, ...imageFiles };
      return newValue;
    });

    const tempUrls: string[] = [];
    const fileNames: string[] = [];

    newFiles.forEach((newFile) => {
      tempUrls.push(newFile.tempUrl);
      fileNames.push(newFile.fileName);
    });

    onChangeProperty('detailImagesTemp', [
      ...(modifiedProduct.detailImagesTemp || modifiedProduct.detailImages),
      ...tempUrls,
    ]);
    onChangeProperty('detailImages', [...modifiedProduct.detailImages, ...fileNames]);
  };

  const onChangeDetailBodyImageFiles = async (files: FileList) => {
    try {
      const imageFiles: any = {};
      const newFiles = await Promise.all(
        Array.from(files).map(async (file) => {
          const result = await resizeImage(file, { maxWidth: 1200, format: 'image/webp' });
          const { fileName } = extractFileName(file.name);
          const contentTypes = result.type.split('/');
          const imageFile = new File([result.image], `${fileName}.${contentTypes[1]}`) as RcFile;
          const tempUrl = URL.createObjectURL(result.image);

          imageFiles[file.name] = imageFile;

          return { result, fileName: `${fileName}.${contentTypes[1]}`, imageFile, tempUrl };
        }),
      );

      setFiles((value) => {
        const newValue = { ...value, ...imageFiles };
        return newValue;
      });

      const tempUrls: string[] = [];
      const fileNames: string[] = [];

      newFiles.forEach((newFile) => {
        tempUrls.push(newFile.tempUrl);
        fileNames.push(newFile.fileName);
      });

      onChangeProperty('detailBodyImagesTemp', [
        ...(modifiedProduct.detailBodyImagesTemp || modifiedProduct.detailBodyImages || []),
        ...tempUrls,
      ]);
      onChangeProperty('detailBodyImages', [...(modifiedProduct.detailBodyImages || []), ...fileNames]);
    } catch (e: any) {
      if (e.code === 'TOO_LARGE_IMAGE') {
        const type = e.property === 'width' ? '넓이' : '높이';

        dialog.open({
          title: '이미지 크기 오류',
          content: `이미지가 너무 큽니다. 이미지를 나누어서 업로드해주세요.\n[${type}]\n현재 크기: ${e.originSize}px\n기준 크기: ${e.expectedSize}px`,
          type: 'ok',
        });
      } else {
        dialog.open({
          title: '알수없는 오류',
          content: `새로고침 후 다시 시도해주시거나 그래도 안되시면 개발팀에 문의해주세요.`,
          type: 'ok',
        });
      }
    }
  };

  const onChangeNaverDetailBodyImageFiles = async (files: FileList) => {
    try {
      const imageFiles: any = {};
      const newFiles = await Promise.all(
        Array.from(files).map(async (file) => {
          const result = await resizeImage(file, { maxWidth: 1200 });
          const { fileName } = extractFileName(file.name);
          const contentTypes = result.type.split('/');
          const imageFile = new File([result.image], `${fileName}.${contentTypes[1]}`) as RcFile;
          const tempUrl = URL.createObjectURL(result.image);

          imageFiles[file.name] = imageFile;

          return { result, fileName: `${fileName}.${contentTypes[1]}`, imageFile, tempUrl };
        }),
      );

      setFiles((value) => {
        const newValue = { ...value, ...imageFiles };
        return newValue;
      });

      const tempUrls: string[] = [];
      const fileNames: string[] = [];

      newFiles.forEach((newFile) => {
        tempUrls.push(newFile.tempUrl);
        fileNames.push(newFile.fileName);
      });

      onChangeProperty('naverDetailBodyImagesTemp', [
        ...(modifiedProduct.naverDetailBodyImagesTemp || modifiedProduct.naverDetailBodyImages || []),
        ...tempUrls,
      ]);
      onChangeProperty('naverDetailBodyImages', [...(modifiedProduct.naverDetailBodyImages || []), ...fileNames]);
    } catch (e: any) {
      if (e.code === 'TOO_LARGE_IMAGE') {
        const type = e.property === 'width' ? '넓이' : '높이';

        dialog.open({
          title: '이미지 크기 오류',
          content: `이미지가 너무 큽니다. 이미지를 나누어서 업로드해주세요.\n[${type}]\n현재 크기: ${e.originSize}px\n기준 크기: ${e.expectedSize}px`,
          type: 'ok',
        });
      } else {
        dialog.open({
          title: '알수없는 오류',
          content: `새로고침 후 다시 시도해주시거나 그래도 안되시면 개발팀에 문의해주세요.`,
          type: 'ok',
        });
      }
    }
  };

  const onChangeNaverTopLinkBannerFiles = async (fileList: FileList) => {
    const current = getNaverTopLinkBanners(modifiedProduct);
    const remain = NAVER_TOP_LINK_BANNER_MAX - current.length;
    if (remain <= 0) {
      dialog.open({
        title: '업로드 제한',
        content: `상단 링크 이미지는 최대 ${NAVER_TOP_LINK_BANNER_MAX}개까지 등록할 수 있습니다.`,
        type: 'ok',
      });
      return;
    }

    try {
      const selected = Array.from(fileList).slice(0, remain);
      const imageFiles: any = {};
      const newFiles = await Promise.all(
        selected.map(async (file) => {
          const result = await resizeImage(file, { maxWidth: 1200 });
          const { fileName } = extractFileName(file.name);
          const contentTypes = result.type.split('/');
          const imageFile = new File([result.image], `${fileName}.${contentTypes[1]}`) as RcFile;
          const tempUrl = URL.createObjectURL(result.image);
          imageFiles[imageFile.name] = imageFile;
          return { fileName: imageFile.name, tempUrl };
        }),
      );

      setFiles((value) => ({ ...value, ...imageFiles }));

      const nextTemp = [...current, ...newFiles.map((item) => ({ image: item.tempUrl, url: '' }))];
      const nextSaved = [
        ...(modifiedProduct.naverTopLinkBanners || []),
        ...newFiles.map((item) => ({ image: item.fileName, url: '' })),
      ];
      onChangeProperty('naverTopLinkBannersTemp', nextTemp);
      onChangeProperty('naverTopLinkBanners', nextSaved);
    } catch (e: any) {
      if (e.code === 'TOO_LARGE_IMAGE') {
        const type = e.property === 'width' ? '넓이' : '높이';
        dialog.open({
          title: '이미지 크기 오류',
          content: `이미지가 너무 큽니다. 이미지를 나누어서 업로드해주세요.\n[${type}]\n현재 크기: ${e.originSize}px\n기준 크기: ${e.expectedSize}px`,
          type: 'ok',
        });
      } else {
        dialog.open({
          title: '알수없는 오류',
          content: `새로고침 후 다시 시도해주시거나 그래도 안되시면 개발팀에 문의해주세요.`,
          type: 'ok',
        });
      }
    }
  };

  useEffect(() => {
    if (draftProductQuery.data?.data) {
      const product = {
        ...draftProductQuery.data?.data,
        productOptions: (draftProductQuery.data?.data.productOptions || []).sort((lv, rv) => {
          return (lv.index || 0) - (rv.index || 0);
        }),
      };

      setModifiedProduct(removeTempProperties(product));
      if (product.naverStoreProduct) {
        setNaverStoreProduct(product.naverStoreProduct);

        (product.productOptions || []).forEach((option) => {
          const key = option.step.map((step) => step.trim()).join(' / ');
          if (key) {
            optionsDictRef.current[key] = option.inventory || 0;
          }
        });
      }
    }
  }, [draftProductQuery.data?.data]);

  useEffect(() => {
    setModifiedProduct((value) => {
      return { ...value, ...changedData };
    });
  }, [changedData]);

  const onDeleteDetailImage = (index: number) => {
    const newDetailImages = [...modifiedProduct.detailImages];
    const newDetailImagesTemp = [...(modifiedProduct.detailImagesTemp || modifiedProduct.detailImages)];

    newDetailImages.splice(index, 1);
    newDetailImagesTemp.splice(index, 1);
    onChangeProperty('detailImagesTemp', newDetailImagesTemp);
    onChangeProperty('detailImages', newDetailImages);
  };

  const onDetailImagesSortEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = (modifiedProduct.detailImagesTemp || modifiedProduct.detailImages).findIndex(
        (item) => item === active.id,
      );
      const newIndex = (modifiedProduct.detailImagesTemp || modifiedProduct.detailImages).findIndex(
        (item) => item === over?.id,
      );
      const newDetailImages = arrayMove(modifiedProduct.detailImages, oldIndex, newIndex);
      const newDetailImagesTemp = arrayMove(
        modifiedProduct.detailImagesTemp || modifiedProduct.detailImages,
        oldIndex,
        newIndex,
      );

      onChangeProperty('detailImages', newDetailImages);
      onChangeProperty('detailImagesTemp', newDetailImagesTemp);
    }
  };

  const onDeleteDetailBodyImage = (index: number) => {
    const newDetailImages = [...(modifiedProduct.detailBodyImages || [])];
    const newDetailImagesTemp = [...(modifiedProduct.detailBodyImagesTemp || modifiedProduct.detailBodyImages || [])];

    newDetailImages.splice(index, 1);
    newDetailImagesTemp.splice(index, 1);
    onChangeProperty('detailBodyImagesTemp', newDetailImagesTemp);
    onChangeProperty('detailBodyImages', newDetailImages);
  };

  const onDeleteNaverDetailBodyImage = (index: number) => {
    const newDetailImages = [...(modifiedProduct.naverDetailBodyImages || [])];
    const newDetailImagesTemp = [
      ...(modifiedProduct.naverDetailBodyImagesTemp || modifiedProduct.naverDetailBodyImages || []),
    ];

    newDetailImages.splice(index, 1);
    newDetailImagesTemp.splice(index, 1);
    onChangeProperty('naverDetailBodyImagesTemp', newDetailImagesTemp);
    onChangeProperty('naverDetailBodyImages', newDetailImages);
  };

  const onChangeEzwelDetailTopImageFiles = async (fileList: FileList) => {
    try {
      const imageFiles: any = {};
      const newFiles = await Promise.all(
        Array.from(fileList).map(async (file) => {
          const result = await resizeImage(file, { maxWidth: 1200 });
          const { fileName } = extractFileName(file.name);
          const contentTypes = result.type.split('/');
          const imageFile = new File([result.image], `${fileName}.${contentTypes[1]}`) as RcFile;
          const tempUrl = URL.createObjectURL(result.image);
          imageFiles[imageFile.name] = imageFile;
          return { fileName: imageFile.name, tempUrl };
        }),
      );
      setFiles((value) => ({ ...value, ...imageFiles }));
      onChangeProperty('ezwelDetailTopImagesTemp', [
        ...(modifiedProduct.ezwelDetailTopImagesTemp || modifiedProduct.ezwelDetailTopImages || []),
        ...newFiles.map((item) => item.tempUrl),
      ]);
      onChangeProperty('ezwelDetailTopImages', [
        ...(modifiedProduct.ezwelDetailTopImages || []),
        ...newFiles.map((item) => item.fileName),
      ]);
    } catch (e: any) {
      dialog.open({
        title: '이미지 업로드 오류',
        content:
          e?.code === 'TOO_LARGE_IMAGE'
            ? '이미지가 너무 큽니다. 나누어서 업로드해주세요.'
            : '새로고침 후 다시 시도해주세요.',
        type: 'ok',
      });
    }
  };

  const onDeleteEzwelDetailTopImage = (index: number) => {
    const saved = [...(modifiedProduct.ezwelDetailTopImages || [])];
    const temps = [...(modifiedProduct.ezwelDetailTopImagesTemp || modifiedProduct.ezwelDetailTopImages || [])];
    saved.splice(index, 1);
    temps.splice(index, 1);
    onChangeProperty('ezwelDetailTopImages', saved);
    onChangeProperty('ezwelDetailTopImagesTemp', temps);
  };

  const onEzwelDetailTopImagesSortEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const items = modifiedProduct.ezwelDetailTopImagesTemp || modifiedProduct.ezwelDetailTopImages || [];
      const oldIndex = items.findIndex((item) => item === active.id);
      const newIndex = items.findIndex((item) => item === over?.id);
      onChangeProperty(
        'ezwelDetailTopImages',
        arrayMove(modifiedProduct.ezwelDetailTopImages || [], oldIndex, newIndex),
      );
      onChangeProperty('ezwelDetailTopImagesTemp', arrayMove(items, oldIndex, newIndex));
    }
  };

  const onDetailBodyImagesSortEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = (modifiedProduct.detailBodyImagesTemp || modifiedProduct.detailBodyImages || []).findIndex(
        (item) => item === active.id,
      );
      const newIndex = (modifiedProduct.detailBodyImagesTemp || modifiedProduct.detailBodyImages || []).findIndex(
        (item) => item === over?.id,
      );
      const newDetailImages = arrayMove(modifiedProduct.detailBodyImages || [], oldIndex, newIndex);
      const newDetailImagesTemp = arrayMove(
        modifiedProduct.detailBodyImagesTemp || modifiedProduct.detailBodyImages || [],
        oldIndex,
        newIndex,
      );

      onChangeProperty('detailBodyImages', newDetailImages);
      onChangeProperty('detailBodyImagesTemp', newDetailImagesTemp);
    }
  };

  const onNaverDetailBodyImagesSortEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = (
        modifiedProduct.naverDetailBodyImagesTemp ||
        modifiedProduct.naverDetailBodyImages ||
        []
      ).findIndex((item) => item === active.id);
      const newIndex = (
        modifiedProduct.naverDetailBodyImagesTemp ||
        modifiedProduct.naverDetailBodyImages ||
        []
      ).findIndex((item) => item === over?.id);
      const newDetailImages = arrayMove(modifiedProduct.naverDetailBodyImages || [], oldIndex, newIndex);
      const newDetailImagesTemp = arrayMove(
        modifiedProduct.naverDetailBodyImagesTemp || modifiedProduct.naverDetailBodyImages || [],
        oldIndex,
        newIndex,
      );

      onChangeProperty('naverDetailBodyImages', newDetailImages);
      onChangeProperty('naverDetailBodyImagesTemp', newDetailImagesTemp);
    }
  };

  const onDeleteNaverTopLinkBanner = (index: number) => {
    const nextSaved = [...(modifiedProduct.naverTopLinkBanners || [])];
    const nextTemp = [...getNaverTopLinkBanners(modifiedProduct)];
    nextSaved.splice(index, 1);
    nextTemp.splice(index, 1);
    onChangeProperty('naverTopLinkBanners', nextSaved);
    onChangeProperty('naverTopLinkBannersTemp', nextTemp);
  };

  const onChangeNaverTopLinkBannerUrl = (index: number, url: string) => {
    const nextSaved = [...(modifiedProduct.naverTopLinkBanners || getNaverTopLinkBanners(modifiedProduct))];
    const nextTemp = [...getNaverTopLinkBanners(modifiedProduct)];
    if (nextSaved[index]) {
      nextSaved[index] = { ...nextSaved[index], url };
    }
    if (nextTemp[index]) {
      nextTemp[index] = { ...nextTemp[index], url };
    }
    onChangeProperty('naverTopLinkBanners', nextSaved);
    onChangeProperty('naverTopLinkBannersTemp', nextTemp);
  };

  const onNaverTopLinkBannersSortEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const items = getNaverTopLinkBanners(modifiedProduct);
      const oldIndex = items.findIndex((item, index) => `${item.image}__${index}` === active.id);
      const newIndex = items.findIndex((item, index) => `${item.image}__${index}` === over?.id);
      if (oldIndex < 0 || newIndex < 0) {
        return;
      }
      onChangeProperty(
        'naverTopLinkBanners',
        arrayMove(modifiedProduct.naverTopLinkBanners || items, oldIndex, newIndex),
      );
      onChangeProperty('naverTopLinkBannersTemp', arrayMove(items, oldIndex, newIndex));
    }
  };

  const handleLabelsDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = parseInt(active.id.toString().replace('label-', ''));
      const newIndex = parseInt(over?.id?.toString().replace('label-', '') || '0');
      const newLabels = arrayMove(currentLabels, oldIndex, newIndex);
      onChangeProperty('labels', newLabels);
    }
  };

  const handleAddLabel = (labelName: string) => {
    const newLabel = {
      name: labelName,
      source: 'admin',
      createdAt: new Date(),
    };
    const newLabels = [...currentLabels, newLabel];
    onChangeProperty('labels', newLabels);
    setShowAddLabelModal(false);
  };

  const handleDeleteLabel = (index: number) => {
    const newLabels = currentLabels.filter((_, i) => i !== index);
    onChangeProperty('labels', newLabels);
  };

  const handleEditLabel = (index: number, updatedLabel: LabelInfo) => {
    const newLabels = [...currentLabels];
    newLabels[index] = updatedLabel;
    onChangeProperty('labels', newLabels);
  };

  const refundPolicyText =
    changedData?.refundPolicyText !== undefined
      ? changedData?.refundPolicyText
      : modifiedProduct?.refundPolicyText ||
        changedData?.classTermsText ||
        modifiedProduct?.classTermsText ||
        (changedData?.refundPolicies || modifiedProduct.refundPolicies || []).join('\n');

  const discountInfo = changedData?.discountInfo || modifiedProduct.discountInfo;
  const periodDiscountExcludeChannels =
    changedData?.periodDiscountExcludeChannels ?? modifiedProduct.periodDiscountExcludeChannels ?? [];
  const currentLabels = changedData?.labels !== undefined ? changedData.labels : modifiedProduct.labels || [];
  const naverStoreProductStatus =
    changedData?.linkedSalesChannel?.naverStore?.status || modifiedProduct?.linkedSalesChannel?.naverStore?.status;
  const ezwelInfo = {
    ...(modifiedProduct?.linkedSalesChannel?.ezwel || {}),
    ...(changedData?.linkedSalesChannel?.ezwel || {}),
  };
  const benepiaInfo = {
    ...(modifiedProduct?.linkedSalesChannel?.benepia || {}),
    ...(changedData?.linkedSalesChannel?.benepia || {}),
  };
  const ezwelPreviewSellPrice = ezwelInfo?.useCustomPrice
    ? Number(ezwelInfo?.sellPrice) || 0
    : Number(modifiedProduct?.fee ?? ezwelInfo?.sellPrice) || 0;
  const ezwelPreviewMarginRate =
    ezwelInfo?.marginRate != null && Number.isFinite(Number(ezwelInfo.marginRate))
      ? Number(ezwelInfo.marginRate)
      : EZWEL_DEFAULT_MARGIN_RATE;
  const ezwelPreviewPurchasePrice =
    ezwelInfo?.purchasePrice != null && Number(ezwelInfo.purchasePrice) > 0
      ? Number(ezwelInfo.purchasePrice)
      : calcEzwelPurchasePrice(ezwelPreviewSellPrice, ezwelPreviewMarginRate);

  const onChangeEzwelPricing = (field: 'sellPrice' | 'purchasePrice' | 'marginRate', value: number | '') => {
    if (value === '') {
      onChangeProperty(`linkedSalesChannel.ezwel.${field}`, '');
      return;
    }
    if (field === 'sellPrice') {
      const margin =
        ezwelInfo?.marginRate != null && Number.isFinite(Number(ezwelInfo.marginRate))
          ? Number(ezwelInfo.marginRate)
          : EZWEL_DEFAULT_MARGIN_RATE;
      onChangeProperty('linkedSalesChannel.ezwel.sellPrice', value);
      onChangeProperty('linkedSalesChannel.ezwel.purchasePrice', calcEzwelPurchasePrice(value, margin));
      onChangeProperty('linkedSalesChannel.ezwel.marginRate', margin);
      return;
    }
    if (field === 'marginRate') {
      onChangeProperty('linkedSalesChannel.ezwel.marginRate', value);
      onChangeProperty('linkedSalesChannel.ezwel.purchasePrice', calcEzwelPurchasePrice(ezwelPreviewSellPrice, value));
      return;
    }
    onChangeProperty('linkedSalesChannel.ezwel.purchasePrice', value);
    onChangeProperty('linkedSalesChannel.ezwel.marginRate', calcEzwelMarginRate(value, ezwelPreviewSellPrice));
  };

  const onChangeEzwelSalePeriod = (patch: { useSalePeriod?: boolean; sellStrtDtm?: string; sellEndDtm?: string }) => {
    onChangeProperty('linkedSalesChannel.ezwel', {
      ...ezwelInfo,
      ...patch,
    });
  };
  // const kakaotalkReservationOptionNamePrices =
  //   changedData?.linkedSalesChannel?.kakaoReservation?.optionNamePrices ||
  //   modifiedProduct?.linkedSalesChannel?.kakaoReservation?.optionNamePrices ||
  //   [];

  const durations =
    changedData?.durations !== undefined
      ? changedData?.durations
      : modifiedProduct.durations || [
          {
            duration: modifiedProduct?.classDuration || 0,
            hour: Math.floor((modifiedProduct?.classDuration || 0) / 60),
            minute: (modifiedProduct?.classDuration || 0) % 60,
          },
        ];

  const templateDefaultActiveKeys = [
    'detailImage',
    'classIntroduction',
    'recommendText',
    'concepts',
    'preparationText',
  ].filter((key) => {
    const disableKey = `disable${key.charAt(0).toUpperCase() + key.slice(1)}` as
      | 'disableDetailImage'
      | 'disableClassIntroduction'
      | 'disableRecommendText'
      | 'disableConcepts'
      | 'disablePreparationText';
    return modifiedProduct.details?.[disableKey] !== true;
  });

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100dvh',
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'white',
        zIndex: 900,
        borderLeft: '1px solid #eaeaea',
      }}>
      <div style={{ display: 'flex', height: '100%' }}>
        <DaumPostcodeModal
          open={showPostcodeModal}
          onCancel={() => {
            setShowPostcodeModal(false);
          }}
          onComplete={(address) => {
            setShowPostcodeModal(false);
            onChangeProperty('location.specificAddress', address);
          }}
        />
        <AddLabelModal
          visible={showAddLabelModal}
          onCancel={() => setShowAddLabelModal(false)}
          onAdd={handleAddLabel}
        />
        <Modal
          open={showOptionUploadModal}
          onCancel={() => {
            setShowOptionUploadModal(false);
          }}
          title="상품옵션 엑셀파일 업로드 선택">
          <div style={{ display: 'flex', gap: '24px', marginTop: '24px' }}>
            <FileUploadButton
              className="ant-btn ant-btn-default"
              style={{
                fontSize: '14px',
                height: '32px',
                padding: '4px 15px',
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                border: '1px solid #d9d9d9',
                color: 'rgba(0, 0, 0, 0.88)',
                boxShadow: '0 2px 0 rgba(0, 0, 0, 0.02)',
                cursor: 'pointer',
              }}
              onChange={(e) => {
                if (e.target.files && isAdmin(authStore.user?.roles)) {
                  const files = e.target.files;
                  loader.on();
                  appSocket.uploadExcelFile({
                    productId: props.productId,
                    sender: {
                      userId: authStore.user?.id,
                      role: authStore.user?.role,
                    },
                    data: { type: 'product_options', file: files[0] },
                  });
                }
              }}>
              {'상품옵션 교체'}
            </FileUploadButton>
            <FileUploadButton
              className="ant-btn ant-btn-default"
              style={{
                fontSize: '14px',
                height: '32px',
                padding: '4px 15px',
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                border: '1px solid #d9d9d9',
                color: 'rgba(0, 0, 0, 0.88)',
                boxShadow: '0 2px 0 rgba(0, 0, 0, 0.02)',
                cursor: 'pointer',
              }}
              onChange={(e) => {
                if (e.target.files && isAdmin(authStore.user?.roles)) {
                  const files = e.target.files;
                  loader.on();
                  appSocket.uploadExcelFile({
                    productId: props.productId,
                    sender: {
                      userId: authStore.user?.id,
                      role: authStore.user?.role,
                    },
                    data: { type: 'product_options_add', file: files[0] },
                  });
                }
              }}>
              {'상품옵션 추가'}
            </FileUploadButton>
          </div>
        </Modal>
        <Modal
          width={'calc(100dvw - 400px)'}
          open={showOptionEdit}
          footer={
            <div>
              <Button
                onClick={() => {
                  const formData: FormData = new FormData();
                  const json = removeTempProperties(changedData);
                  formData.append('productId', props.productId);
                  formData.append('json', JSON.stringify(json));

                  Object.values(files).forEach((file) => {
                    formData.append('files', file);
                  });
                  mutationUpdateProduct.mutate(formData);
                }}>
                저장
              </Button>
              <Button
                onClick={() => {
                  setShowOptionEdit(false);
                }}>
                닫기
              </Button>
            </div>
          }
          onCancel={() => {
            setShowOptionEdit(false);
          }}
          onOk={() => {
            setShowOptionEdit(false);
          }}>
          <ProductOptionEditView
            productId={props.productId}
            productOptions={changedData?.productOptions || modifiedProduct.productOptions}
            optionLabels={changedData?.optionLabels || modifiedProduct.optionLabels}
            purchaseQuantityOptions={changedData?.purchaseQuantityOptions || modifiedProduct.purchaseQuantityOptions}
            onChangeProductOptions={(value) => {
              onChangeProperty('productOptions', value);
            }}
            onChangeOptionLabels={(value) => {
              onChangeProperty('optionLabels', value);
            }}
            onChangePurchaseQuantityOptions={(value) => {
              onChangeProperty('purchaseQuantityOptions', value);
            }}
          />
        </Modal>
        <div style={{ flex: 1 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              height: '56px',
              borderBottom: '1px solid #eff1f4',
              fontSize: '17px',
              fontWeight: 'bold',
              color: '#242424',
              paddingLeft: '24px',
            }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{ color: 'red' }}>{draftProductQuery.data?.data.isTemp ? '[임시데이터]' : ''}</span>
              <span>{`${modifiedProduct.title || '이름 없음'} 상품 편집`}</span>
              <Button
                onClick={() => {
                  window.open(`${WEB_URL}/product/${props.productId}`, '_blank');
                }}>
                바로가기
              </Button>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(props.productId);
                  toast.dark('상품 ID를 클립보드에 복사했습니다', {
                    position: 'top-center',
                    autoClose: 3000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    pauseOnFocusLoss: false,
                    draggable: true,
                    progress: undefined,
                    bodyStyle: {
                      color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                      textAlign: 'center',
                      fontFamily: 'Pretendard',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: '20px',
                    },
                    theme: 'dark',
                  });
                }}>
                ID 복사
              </Button>
            </div>
            {modifiedProduct.saleType === 'commerce' && (
              <div
                style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', marginRight: '8px' }}>
                <Button
                  onClick={async () => {
                    setShowOptionEdit((value) => !value);
                  }}>
                  옵션 수정
                </Button>
                <Button
                  onClick={() => {
                    setShowOptionUploadModal(true);
                  }}>
                  상품옵션 업로드
                </Button>
                <Button
                  onClick={async () => {
                    const fileName = `igogo-product-options-${moment().format('YYYYMMDD-HHmmss')}-${
                      props.productId
                    }.xlsx`;
                    const blob = await api.getProductOptionsExcelFile(props.productId);
                    if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
                      // IE variant
                      (window.navigator as any).msSaveOrOpenBlob(new Blob(blob.data), fileName);
                    } else {
                      const url = window.URL.createObjectURL(blob.data);
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', fileName);
                      document.body.appendChild(link);
                      link.click();
                      link.parentNode && link.parentNode.removeChild(link);
                    }
                  }}>
                  상품옵션 다운로드
                </Button>
                <Button
                  onClick={async () => {
                    const imageUrl = cdnImageUrl(modifiedProduct.thumbnailImageTemp || modifiedProduct.thumbnailImage);
                    if (imageUrl) {
                      const response = await fetch(imageUrl);
                      response.blob().then((blob) => {
                        resizeImage(blob, {
                          format: 'image/png',
                        }).then((result) => {
                          const url = window.URL.createObjectURL(result.image);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${modifiedProduct.sellerName}_${modifiedProduct.title.slice(0, 10)}_썸네일`;
                          a.click();
                        });
                      });
                    }

                    const coverImages = modifiedProduct.detailImagesTemp || modifiedProduct.detailImages || [];
                    for (let i = 0; i < coverImages.length; i++) {
                      const imageUrl = cdnImageUrl(coverImages[i]);
                      if (imageUrl) {
                        const response = await fetch(imageUrl);
                        response.blob().then((blob) => {
                          resizeImage(blob, {
                            format: 'image/png',
                          }).then((result) => {
                            const url = window.URL.createObjectURL(result.image);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `${modifiedProduct.sellerName}_${modifiedProduct.title.slice(
                              0,
                              10,
                            )}_커버_${i}`;
                            a.click();
                          });
                        });
                      }
                    }

                    const bodyImages = modifiedProduct.detailBodyImagesTemp || modifiedProduct.detailBodyImages || [];
                    for (let i = 0; i < bodyImages.length; i++) {
                      const imageUrl = cdnImageUrl(bodyImages[i]);
                      if (imageUrl) {
                        const response = await fetch(imageUrl);
                        response.blob().then((blob) => {
                          resizeImage(blob, {
                            format: 'image/png',
                          }).then((result) => {
                            const url = window.URL.createObjectURL(result.image);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `${modifiedProduct.sellerName}_${modifiedProduct.title.slice(
                              0,
                              10,
                            )}_상세_${i}`;
                            a.click();
                          });
                        });
                      }
                    }

                    const naverBodyImages =
                      modifiedProduct.naverDetailBodyImagesTemp || modifiedProduct.naverDetailBodyImages || [];
                    for (let i = 0; i < naverBodyImages.length; i++) {
                      const imageUrl = cdnImageUrl(naverBodyImages[i]);
                      if (imageUrl) {
                        const response = await fetch(imageUrl);
                        response.blob().then((blob) => {
                          resizeImage(blob, {
                            format: 'image/png',
                          }).then((result) => {
                            const url = window.URL.createObjectURL(result.image);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `${modifiedProduct.sellerName}_${modifiedProduct.title.slice(
                              0,
                              10,
                            )}_네이버상세_${i}`;
                            a.click();
                          });
                        });
                      }
                    }
                  }}>
                  모든 이미지 다운로드
                </Button>
              </div>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              padding: '12px',
              height: 'calc(100% - 100px)',
              overflow: 'auto',
            }}>
            <div style={{ borderRadius: '12px', border: '1px solid #eaeaea', padding: '16px' }}>
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  borderBottom: '1px solid #eaeaea',
                  height: '32px',
                }}>
                승인 노출 상태
              </div>
              <div style={{ marginTop: '12px', marginLeft: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: '140px' }}>승인상태 변경</th>
                        <th style={{ width: '100px' }}>노출설정</th>
                        <th style={{ width: '100px' }}>판매여부</th>
                        <th style={{ width: '100px' }}>
                          프라이빗 여부
                          <Tooltip title="url을 통해서만 구매 가능">
                            <QuestionCircleOutlined style={{ marginLeft: '4px' }} />
                          </Tooltip>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ textAlign: 'center' }}>
                          <Select
                            value={modifiedProduct.approveStatus}
                            style={{ width: 120 }}
                            onChange={(value) => {
                              onChangeProperty('approveStatus', value);
                            }}
                            options={[
                              { value: ProductApproveStatus.ACCEPTED, label: '승인' },
                              { value: ProductApproveStatus.REQUESTED, label: '승인 요청' },
                              { value: ProductApproveStatus.DENIED, label: '승인 반려' },
                              { value: ProductApproveStatus.NOT_READY, label: '수정중' },
                              { value: ProductApproveStatus.READY, label: '수정완료' },
                            ]}
                          />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <Switch
                            checked={modifiedProduct.display}
                            onChange={(value) => {
                              onChangeProperty('display', value);
                            }}
                          />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <Switch
                            checked={
                              modifiedProduct.status === ProductStatus.OPEN ||
                              modifiedProduct.status === ProductStatus.PRIVATE
                            }
                            onChange={(value) => {
                              onChangeProperty('status', value ? ProductStatus.OPEN : ProductStatus.CLOSE);
                            }}
                          />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <Switch
                            checked={modifiedProduct.status === ProductStatus.PRIVATE}
                            onChange={(value) => {
                              onChangeProperty('status', value ? ProductStatus.PRIVATE : ProductStatus.CLOSE);
                            }}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: '12px' }}>
                  {modifiedProduct.approveStatus === ProductApproveStatus.DENIED && (
                    <div style={{ marginTop: '24px' }}>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>승인 반려 사유</div>
                      <TextInput
                        style={{ maxWidth: '428px' }}
                        value={
                          changedData?.deniedReason !== undefined
                            ? changedData?.deniedReason
                            : modifiedProduct.deniedReason
                        }
                        onChangeValue={(value) => {
                          onChangeProperty('deniedReason', value);
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div style={{ borderRadius: '12px', border: '1px solid #eaeaea', padding: '16px' }}>
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  borderBottom: '1px solid #eaeaea',
                  height: '32px',
                }}>
                기본 정보
              </div>
              <div style={{ marginTop: '12px', marginLeft: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: '120px' }}>판매타입 변경</th>
                        <th style={{ width: '100px' }}>그룹할인</th>
                        <th style={{ width: '120px' }}>
                          검색 우선 순위
                          <Tooltip title="높은 숫자가 우선순위 높음">
                            <QuestionCircleOutlined style={{ marginLeft: '4px' }} />
                          </Tooltip>
                        </th>
                        <th style={{ width: '100px' }}>앱 전용상품</th>
                        <th style={{ width: '140px' }}>
                          옵션모달 강제
                          <Tooltip title="켜면 캘린더 옵션 UI 대신 기존 옵션 선택 모달을 사용합니다">
                            <QuestionCircleOutlined style={{ marginLeft: '4px' }} />
                          </Tooltip>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ textAlign: 'center' }}>
                          <Select
                            value={modifiedProduct.saleType}
                            style={{ width: 120 }}
                            onChange={(value) => {
                              onChangeProperty('saleType', value);
                            }}
                            options={[
                              { value: 'commerce', label: '커머스' },
                              { value: 'class', label: '클래스' },
                            ]}
                          />
                          {modifiedProduct.originData?.saleType && (
                            <Select
                              disabled
                              value={modifiedProduct.originData?.saleType}
                              style={{ width: 120 }}
                              onChange={(value) => {
                                onChangeProperty('saleType', value);
                              }}
                              options={[
                                { value: 'commerce', label: '커머스' },
                                { value: 'class', label: '클래스' },
                              ]}
                            />
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <Switch
                            checked={modifiedProduct.applyGroupDiscount}
                            onChange={(value) => {
                              onChangeProperty('applyGroupDiscount', value);
                            }}
                          />
                          {modifiedProduct.originData?.applyGroupDiscount && (
                            <Switch disabled checked={modifiedProduct.originData?.applyGroupDiscount} />
                          )}
                        </td>
                        <td>
                          <TextInput
                            style={{ maxWidth: '428px' }}
                            value={
                              changedData?.priority !== undefined ? changedData?.priority : modifiedProduct.priority
                            }
                            onChangeValue={(value) => {
                              onChangeProperty('priority', parseInt(value) || 0);
                            }}
                            onChange={(e) => {
                              onChangeProperty('priority', parseInt(e.target.value) || 0);
                            }}></TextInput>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <Switch
                            checked={modifiedProduct?.appOnly}
                            onChange={(value) => {
                              onChangeProperty('appOnly', value);
                            }}
                          />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <Switch
                            checked={
                              changedData?.forceOptionModal !== undefined
                                ? changedData.forceOptionModal
                                : !!modifiedProduct?.forceOptionModal
                            }
                            onChange={(value) => {
                              onChangeProperty('forceOptionModal', value);
                            }}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: '120px' }}>수업 방식</th>
                        <th style={{ width: '120px' }}>수강 연령 최소</th>
                        <th style={{ width: '120px' }}>수강 연령 최대</th>
                        <th style={{ width: '120px' }}>수업 인원 최소(-1: 제한없음)</th>
                        <th style={{ width: '120px' }}>수업 인원 최대(-1: 제한없음)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <Select
                            value={changedData?.activityType || modifiedProduct.activityType}
                            style={{ width: 120 }}
                            onChange={(value) => {
                              onChangeProperty('activityType', value);
                              onChangeProperty('shippingProduct', value === ProductActivityType.DELIVERY);
                            }}
                            options={Object.keys(PRODUCT_ACTIVITY_TYPE_TEXT).map((key) => {
                              const item = PRODUCT_ACTIVITY_TYPE_TEXT[key as ProductActivityType];
                              return { value: key, label: item.title };
                            })}
                          />
                          {modifiedProduct.originData?.activityType && (
                            <Select
                              disabled
                              value={modifiedProduct.originData?.activityType}
                              style={{ width: 120 }}
                              options={Object.keys(PRODUCT_ACTIVITY_TYPE_TEXT).map((key) => {
                                const item = PRODUCT_ACTIVITY_TYPE_TEXT[key as ProductActivityType];
                                return { value: key, label: item.title };
                              })}
                            />
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span>만</span>
                          <TextInput
                            style={{ width: '60px', textAlign: 'right' }}
                            value={changedData?.ageMin !== undefined ? changedData?.ageMin : modifiedProduct.ageMin}
                            onChangeValue={(value) => {
                              onChangeProperty('ageMin', parseInt(value) || 0);
                            }}
                            onChange={(e) => {
                              onChangeProperty('ageMin', parseInt(e.target.value) || 0);
                            }}></TextInput>
                          <span>세</span>
                          {modifiedProduct.originData?.ageMin && (
                            <div>
                              <span>만</span>
                              <TextInput
                                disabled
                                style={{ width: '60px', textAlign: 'right' }}
                                value={modifiedProduct.originData?.ageMin}></TextInput>
                              <span>세</span>
                            </div>
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span>만</span>
                          <TextInput
                            style={{ width: '60px', textAlign: 'right' }}
                            value={changedData?.ageMax !== undefined ? changedData?.ageMax : modifiedProduct.ageMax}
                            onChangeValue={(value) => {
                              onChangeProperty('ageMax', parseInt(value) || 0);
                            }}
                            onChange={(e) => {
                              onChangeProperty('ageMax', parseInt(e.target.value) || 0);
                            }}></TextInput>
                          <span>세</span>
                          {modifiedProduct.originData?.ageMax && (
                            <div>
                              <span>만</span>
                              <TextInput
                                disabled
                                style={{ width: '60px', textAlign: 'right' }}
                                value={modifiedProduct.originData?.ageMax}></TextInput>
                              <span>세</span>
                            </div>
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <TextInput
                            style={{ width: '60px', textAlign: 'right' }}
                            value={
                              changedData?.studentsMin !== undefined
                                ? changedData?.studentsMin
                                : modifiedProduct.studentsMin
                            }
                            onChangeValue={(value) => {
                              onChangeProperty('studentsMin', Number(value) || 0);
                            }}
                            onChange={(e) => {
                              onChangeProperty('studentsMin', Number(e.target.value) || 0);
                            }}></TextInput>
                          <span>명</span>
                          {modifiedProduct.originData?.studentsMin && (
                            <div>
                              <TextInput
                                disabled
                                style={{ width: '60px', textAlign: 'right' }}
                                value={modifiedProduct.originData?.studentsMin}></TextInput>
                              <span>명</span>
                            </div>
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <TextInput
                            style={{ width: '60px', textAlign: 'right' }}
                            value={
                              changedData?.studentsMax !== undefined
                                ? changedData?.studentsMax
                                : modifiedProduct.studentsMax
                            }
                            onChangeValue={(value) => {
                              onChangeProperty('studentsMax', Number(value) || 0);
                            }}
                            onChange={(e) => {
                              onChangeProperty('studentsMax', Number(e.target.value) || 0);
                            }}></TextInput>
                          <span>명</span>
                          {modifiedProduct.originData?.studentsMax && (
                            <div>
                              <TextInput
                                disabled
                                style={{ width: '60px', textAlign: 'right' }}
                                value={modifiedProduct.originData?.studentsMax}></TextInput>
                              <span>명</span>
                            </div>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div style={{ marginTop: '24px' }}>
                    <div
                      style={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                      <div>구매 수량 제한</div>
                      <Checkbox
                        checked={
                          changedData?.enableLimitPurchase !== undefined
                            ? changedData?.enableLimitPurchase
                            : modifiedProduct.enableLimitPurchase
                        }
                        onChange={(e) => {
                          onChangeProperty('enableLimitPurchase', e.target.checked);
                        }}>
                        사용함
                      </Checkbox>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div>인당</div>
                      <TextInput
                        style={{ width: '60px', textAlign: 'right' }}
                        value={
                          changedData?.limitPurchaseCount !== undefined
                            ? changedData?.limitPurchaseCount
                            : modifiedProduct.limitPurchaseCount
                        }
                        onChangeValue={(value) => {
                          onChangeProperty('limitPurchaseCount', Number(value) || 0);
                        }}></TextInput>
                      <div>개 구매 가능</div>
                    </div>
                    {modifiedProduct.originData?.limitPurchaseCount && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div>인당</div>
                        <TextInput
                          disabled
                          style={{ width: '60px', textAlign: 'right' }}
                          value={modifiedProduct.originData?.limitPurchaseCount}></TextInput>
                        <div>개 구매 가능</div>
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: '24px' }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>배송상품 판매자 멘트</div>
                    <TextInput
                      style={{ maxWidth: '428px' }}
                      value={
                        changedData?.sellerShortComment !== undefined
                          ? changedData?.sellerShortComment
                          : modifiedProduct.sellerShortComment
                      }
                      onChangeValue={(value) => {
                        onChangeProperty('sellerShortComment', value);
                      }}
                      onChange={(e) => {
                        onChangeProperty('sellerShortComment', e.target.value);
                      }}></TextInput>
                    {modifiedProduct.originData?.sellerShortComment && (
                      <div>
                        <TextInput
                          disabled
                          style={{ maxWidth: '428px' }}
                          value={modifiedProduct.originData?.sellerShortComment}></TextInput>
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: '24px' }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>수업시간</div>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                      {(durations || []).map((duration, index) => {
                        return (
                          <div>
                            <TextInput
                              style={{ width: '60px', textAlign: 'right' }}
                              value={duration?.duration}
                              onChangeValue={(value) => {
                                onChangeProperty(`durations.${index}.duration`, parseInt(value) || 0);
                                onChangeProperty(`durations.${index}.hour`, Math.floor((parseInt(value) || 0) / 60));
                                onChangeProperty(`durations.${index}.minute`, (parseInt(value) || 0) % 60);
                              }}></TextInput>
                            <span>분</span>
                            <button
                              className="link-button"
                              style={{ marginLeft: '4px' }}
                              onClick={() => {
                                onChangeProperty(
                                  `durations`,
                                  durations.filter((_, i) => i !== index),
                                );
                              }}>
                              <DeleteOutlined style={{ color: 'red' }} size={48} />
                            </button>
                          </div>
                        );
                      })}
                      <Button
                        onClick={() => {
                          onChangeProperty('durations', [...durations, { duration: 0, hour: 0, minute: 0 }]);
                        }}>
                        추가
                      </Button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                      {(modifiedProduct.originData?.durations || [])?.map((duration, index) => {
                        return (
                          <div>
                            <TextInput
                              disabled
                              style={{ width: '60px', textAlign: 'right' }}
                              value={duration?.duration}
                              onChangeValue={(value) => {
                                onChangeProperty(`durations.${index}.duration`, parseInt(value) || 0);
                              }}
                              onChange={(e) => {
                                onChangeProperty(`durations.${index}.duration`, parseInt(e.target.value) || 0);
                              }}></TextInput>
                            <span>분</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{ marginTop: '24px' }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>구매안내 멘트</div>
                    <TextArea
                      rows={4}
                      style={{ maxWidth: '428px' }}
                      value={
                        changedData?.sellerComment !== undefined
                          ? changedData?.sellerComment
                          : modifiedProduct.sellerComment
                      }
                      onChangeValue={(value) => {
                        onChangeProperty('sellerComment', value);
                      }}
                      onChange={(e) => {
                        onChangeProperty('sellerComment', e.target.value);
                      }}></TextArea>
                    {modifiedProduct.originData?.sellerComment && (
                      <div>
                        <TextArea
                          disabled
                          rows={4}
                          style={{ maxWidth: '428px' }}
                          value={modifiedProduct.originData?.sellerComment}></TextArea>
                      </div>
                    )}
                  </div>
                  {(changedData?.activityType || modifiedProduct.activityType) === ProductActivityType.HOME && (
                    <div style={{ width: '540px' }}>
                      <DistrictSelect
                        defaultDistricts={
                          changedData?.location?.availableDistricts || modifiedProduct.location?.availableDistricts
                        }
                        onUpdate={(districts) => {
                          onChangeProperty('location.availableDistricts', districts);
                        }}></DistrictSelect>

                      {modifiedProduct.originData?.location?.availableDistricts && (
                        <DistrictSelect
                          disabled
                          defaultDistricts={modifiedProduct.originData?.location?.availableDistricts}></DistrictSelect>
                      )}
                    </div>
                  )}
                  {[ProductActivityType.STUDIO, ProductActivityType.EXPERIENTIAL, ProductActivityType.TICKET].includes(
                    changedData?.activityType || modifiedProduct.activityType,
                  ) && (
                    <div style={{ alignItems: 'center', marginTop: '12px' }}>
                      <div style={{ fontSize: '16px', fontWeight: 'bold' }}>수업 장소</div>
                      <div style={{ display: 'flex', flexDirection: 'column', width: '480px' }}>
                        <div
                          style={{
                            marginTop: '8px',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                          }}>
                          <div
                            style={{ width: '100%', cursor: 'pointer' }}
                            onClick={() => {
                              setShowPostcodeModal(true);
                            }}>
                            <div
                              style={{
                                display: 'flex',
                                height: '48px',
                                borderRadius: '8px',
                                border: '1px solid #dadcdf',
                                paddingLeft: '16px',
                                paddingRight: '16px',
                                alignItems: 'center',
                              }}>
                              <div
                                style={{
                                  fontSize: '16px',
                                  lineHeight: '20px',
                                  color: '#242424',
                                }}>
                                {changedData?.location?.specificAddress?.address ||
                                  modifiedProduct.location?.specificAddress?.address}
                              </div>
                            </div>
                          </div>
                          <button
                            className="link-button"
                            style={{
                              display: 'flex',
                              height: '48px',
                              width: '88px',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '8px',
                              backgroundColor: '#66686b',
                              marginLeft: '8px',
                            }}
                            onClick={() => {
                              setShowPostcodeModal(true);
                            }}>
                            <div
                              style={{
                                fontSize: '16px',
                                lineHeight: '19px',
                                fontWeight: '500',
                                color: '#ffffff',
                              }}>
                              주소검색
                            </div>
                          </button>
                        </div>
                        <input
                          style={{
                            height: '48px',
                            borderRadius: '8px',
                            border: '1px solid #dadcdf',
                            paddingLeft: '16px',
                            paddingRight: '16px',
                            marginTop: '12px',
                            fontSize: '16px',
                            lineHeight: '20px',
                            color: '#242424',
                          }}
                          value={
                            changedData?.location?.specificAddress?.address2 ||
                            modifiedProduct.location?.specificAddress?.address2
                          }
                          onChange={(e) => {
                            onChangeProperty('location.specificAddress.address2', e.target.value);
                          }}
                          placeholder={'상세주소를 입력해 주세요'}
                        />
                      </div>
                      {modifiedProduct.originData?.location?.specificAddress && (
                        <TextInput
                          style={{ maxWidth: '800px' }}
                          disabled
                          value={`${modifiedProduct.originData?.location?.specificAddress.address} ${modifiedProduct.originData?.location?.specificAddress.address2}`}></TextInput>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 'bold' }}>상품명</div>
                  <TextInput
                    style={{ maxWidth: '800px' }}
                    value={changedData?.title !== undefined ? changedData?.title : modifiedProduct.title}
                    onChangeValue={(value) => {
                      onChangeProperty('title', value);
                    }}
                    onChange={(e) => {
                      onChangeProperty('title', e.target.value);
                    }}></TextInput>
                  {modifiedProduct.originData?.title && (
                    <TextInput
                      style={{ maxWidth: '800px' }}
                      disabled
                      value={modifiedProduct.originData?.title}></TextInput>
                  )}
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div
                    style={{ fontSize: '14px', fontWeight: 'bold' }}>{`파트너 (${modifiedProduct?.sellerName})`}</div>
                  <SelectUser
                    style={{ width: '528px' }}
                    selectedUserId={changedData?.sellerId || modifiedProduct.sellerId}
                    onSelect={(partner) => {
                      onChangeProperty('sellerId', partner._id);
                      onChangeProperty('sellerName', partner.profile?.nickname);
                    }}></SelectUser>
                </div>
                <div style={{ marginTop: '24px' }}>
                  <div
                    style={{ fontSize: '16px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div>썸네일 이미지</div>{' '}
                    {modifiedProduct.thumbnailImageTemp ||
                      (modifiedProduct.thumbnailImage && (
                        <Button
                          onClick={async () => {
                            const imageUrl = cdnImageUrl(
                              modifiedProduct.thumbnailImageTemp || modifiedProduct.thumbnailImage,
                            );
                            if (imageUrl) {
                              const response = await fetch(imageUrl);
                              response.blob().then((blob) => {
                                resizeImage(blob, {
                                  format: 'image/png',
                                }).then((result) => {
                                  const url = window.URL.createObjectURL(result.image);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `${modifiedProduct.sellerName}_${modifiedProduct.title.slice(
                                    0,
                                    10,
                                  )}_썸네일`;
                                  a.click();
                                });
                              });
                            }
                          }}>
                          다운로드
                        </Button>
                      ))}
                  </div>
                  <div
                    style={{
                      width: '184px',
                      height: '184px',
                      border: '1px solid #eaeaea',
                      overflow: 'hidden',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRadius: '8px',
                    }}>
                    {modifiedProduct.thumbnailImageTemp || modifiedProduct.thumbnailImage ? (
                      <img
                        src={cdnImageUrl(modifiedProduct.thumbnailImageTemp || modifiedProduct.thumbnailImage)}
                        style={{ width: '184px', height: '184px', objectFit: 'cover' }}
                      />
                    ) : (
                      <div>{'NO IMAGE'}</div>
                    )}
                  </div>
                  <FileUploadButton
                    accept={['jpg', 'jpeg', 'png', 'tif', 'tiff', 'webp', 'svg']
                      .map((ext) => {
                        return '.' + ext;
                      })
                      .join(',')}
                    className="link-button"
                    style={{
                      display: 'flex',
                      alignSelf: 'center',
                      justifyContent: 'center',
                      height: '36px',
                      width: '184px',
                      border: '1px solid #aaaaaa',
                      borderRadius: '8px',
                      lineHeight: '36px',
                      marginTop: '4px',
                    }}
                    onChange={(e) => {
                      if (e.target.files) {
                        const files = e.target.files;
                        onChangeThumbnailFile(files);
                      }
                    }}>
                    썸네일 변경
                  </FileUploadButton>
                  {modifiedProduct.originData?.thumbnailImage && (
                    <div
                      style={{
                        width: '184px',
                        height: '184px',
                        border: '4px solid #eaeaea',
                        overflow: 'hidden',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: '8px',
                      }}>
                      <img
                        src={cdnImageUrl(modifiedProduct.originData?.thumbnailImage)}
                        style={{ width: '184px', height: '184px', objectFit: 'cover' }}
                      />
                    </div>
                  )}
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <div
                      style={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                      <div>커버 이미지</div>
                      <Button
                        onClick={async () => {
                          const images = modifiedProduct.detailImagesTemp || modifiedProduct.detailImages || [];
                          for (let i = 0; i < images.length; i++) {
                            const imageUrl = cdnImageUrl(images[i]);
                            if (imageUrl) {
                              const response = await fetch(imageUrl);
                              response.blob().then((blob) => {
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `${modifiedProduct.sellerName}_${modifiedProduct.title.slice(
                                  0,
                                  10,
                                )}_커버_${i}`;
                                a.click();
                              });
                            }
                          }
                        }}>
                        전체 다운로드
                      </Button>
                    </div>
                    <FileUploadButton
                      multiple
                      accept={['jpg', 'jpeg', 'png', 'tif', 'tiff', 'webp', 'svg']
                        .map((ext) => {
                          return '.' + ext;
                        })
                        .join(',')}
                      className="link-button"
                      style={{
                        fontSize: '14px',
                        height: '32px',
                        padding: '4px 15px',
                        borderRadius: '6px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #d9d9d9',
                        color: 'rgba(0, 0, 0, 0.88)',
                        boxShadow: '0 2px 0 rgba(0, 0, 0, 0.02)',
                        cursor: 'pointer',
                        marginLeft: '12px',
                      }}
                      onChange={(e) => {
                        if (e.target.files) {
                          const files = e.target.files;
                          onChangeDetailImageFiles(files);
                        }
                      }}>
                      커버 이미지 추가
                    </FileUploadButton>
                  </div>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDetailImagesSortEnd}>
                    <SortableContext
                      items={(modifiedProduct.detailImagesTemp || modifiedProduct.detailImages || []).map(
                        (item) => item,
                      )}
                      strategy={horizontalListSortingStrategy}>
                      <div style={{ display: 'flex', gap: '4px', overflow: 'auto', maxWidth: '600px' }}>
                        {(modifiedProduct.detailImagesTemp || modifiedProduct.detailImages || []).map(
                          (imageUrl: string, index: number) => {
                            return (
                              <SortableDetailImage
                                key={`image-${imageUrl}-${index}`}
                                id={imageUrl}
                                onDeleteDetailImage={() => {
                                  onDeleteDetailImage(index);
                                }}
                                imageUrl={imageUrl}
                              />
                            );
                          },
                        )}
                      </div>
                    </SortableContext>
                  </DndContext>
                  {modifiedProduct.originData?.detailImages && (
                    <div style={{ display: 'flex', gap: '4px', overflow: 'auto', maxWidth: '600px' }}>
                      {(modifiedProduct.originData?.detailImages || []).map((imageUrl: string, index: number) => {
                        return (
                          <div
                            key={`origin-cover-image-${index}`}
                            style={{
                              width: '184px',
                              height: '184px',
                              position: 'relative',
                              borderRadius: '8px',
                              overflow: 'hidden',
                            }}>
                            <img
                              src={cdnImageUrl(imageUrl)}
                              style={{ width: '184px', height: '184px', objectFit: 'cover' }}></img>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {/* <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 'bold' }}>카테고리</div>
                  <div style={{ fontSize: '13px', marginTop: '8px' }}>
                    {Object.keys(PRODUCT_CATEGORIES).map((key) => {
                      return (
                        <div key={key} style={{ display: 'flex' }}>
                          <div
                            style={{
                              marginRight: '20px',
                              fontWeight: 'bold',
                              width: '52px',
                              textAlign: 'right',
                            }}>
                            {key}
                          </div>
                          <div>
                            {PRODUCT_CATEGORIES[key as PRODUCT_CATEGORIES_KEYS].map((item) => {
                              return (
                                <Checkbox
                                  key={item}
                                  style={{ width: '100px' }}
                                  checked={(modifiedProduct.categories || []).includes(item)}
                                  onChange={(e) => {
                                    let categories = [...(changedData?.categories || modifiedProduct.categories || [])];

                                    if (e.target.checked) {
                                      categories.push(item);
                                    } else {
                                      categories = categories.filter((cate) => cate !== item);
                                    }
                                    onChangeProperty('categories', categories.slice(0, 4));
                                  }}>
                                  {item}
                                </Checkbox>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {modifiedProduct.originData?.categories && (
                    <div style={{ fontSize: '13px', marginTop: '8px' }}>
                      {Object.keys(PRODUCT_CATEGORIES).map((key) => {
                        return (
                          <div key={key} style={{ display: 'flex' }}>
                            <div
                              style={{
                                marginRight: '20px',
                                fontWeight: 'bold',
                                width: '52px',
                                textAlign: 'right',
                              }}>
                              {key}
                            </div>
                            <div>
                              {PRODUCT_CATEGORIES[key as PRODUCT_CATEGORIES_KEYS].map((item) => {
                                return (
                                  <Checkbox
                                    disabled
                                    key={item}
                                    style={{ width: '100px' }}
                                    checked={(modifiedProduct.originData?.categories || []).includes(item)}
                                    onChange={(e) => {
                                      let categories = [...(modifiedProduct.originData?.categories || [])];

                                      if (e.target.checked) {
                                        categories.push(item);
                                      } else {
                                        categories = categories.filter((cate) => cate !== item);
                                      }
                                      onChangeProperty('categories', categories.slice(0, 4));
                                    }}>
                                    {item}
                                  </Checkbox>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div> */}
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 'bold' }}>대분류 카테고리</div>
                  <div style={{ fontSize: '13px', marginTop: '8px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                      {(productRootCategories?.data?.data || []).map((item) => {
                        return (
                          <Checkbox
                            key={item.type}
                            style={{ width: '150px' }}
                            checked={(modifiedProduct.rootCategories || []).includes(item.type as any)}
                            onChange={(e) => {
                              let rootCategories = [
                                ...(changedData?.rootCategories || modifiedProduct.rootCategories || []),
                              ];

                              if (e.target.checked) {
                                rootCategories.push(item.type as any);
                              } else {
                                rootCategories = rootCategories.filter((cate) => cate !== item.type);
                              }
                              onChangeProperty('rootCategories', rootCategories);
                            }}>
                            {item.label?.ko}
                          </Checkbox>
                        );
                      })}
                    </div>
                  </div>
                  {modifiedProduct.originData?.rootCategories && (
                    <div style={{ fontSize: '13px', marginTop: '8px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        {(productRootCategories?.data?.data || []).map((item) => {
                          return (
                            <Checkbox
                              disabled
                              key={item.type}
                              style={{ width: '150px' }}
                              checked={(modifiedProduct.originData?.rootCategories || []).includes(item.type as any)}
                              onChange={(e) => {
                                let rootCategories = [...(modifiedProduct.originData?.rootCategories || [])];

                                if (e.target.checked) {
                                  rootCategories.push(item.type as any);
                                } else {
                                  rootCategories = rootCategories.filter((cate) => cate !== item.type);
                                }
                                onChangeProperty('rootCategories', rootCategories);
                              }}>
                              {item.label?.ko}
                            </Checkbox>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ marginTop: '24px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                    <div>리포트 작성 여부</div>
                    <div style={{ marginLeft: '24px', marginRight: '4px' }}>필수</div>
                    <Switch
                      checked={modifiedProduct.programReportAvailable}
                      onChange={(value) => {
                        onChangeProperty('programReportAvailable', value);
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {Object.values(IntelligenceType).map((value) => (
                      <Checkbox
                        key={value}
                        checked={modifiedProduct.programReportItems?.includes(value)}
                        onChange={(e) => {
                          let programReportItems = [...(modifiedProduct.programReportItems || [])];

                          if (e.target.checked) {
                            programReportItems.push(value);
                          } else {
                            programReportItems = programReportItems.filter((type) => type !== value);
                          }
                          onChangeProperty('programReportItems', programReportItems);
                        }}
                        style={{ width: '110px', flexShrink: 0 }}>
                        {INTELLIGENCE_TYPE_LABEL[value]}
                      </Checkbox>
                    ))}
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
                    라벨
                    <Tooltip title="상품에 표시될 라벨들을 관리합니다. 드래그하여 순서를 변경할 수 있습니다.">
                      <QuestionCircleOutlined style={{ marginLeft: '4px' }} />
                    </Tooltip>
                  </div>

                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleLabelsDragEnd}>
                    <SortableContext
                      items={currentLabels.map((_, index) => `label-${index}`)}
                      strategy={horizontalListSortingStrategy}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                        {currentLabels.map((label, index) => (
                          <SortableLabelItem
                            key={`label-${index}`}
                            id={`label-${index}`}
                            label={label}
                            onDelete={() => handleDeleteLabel(index)}
                            onEdit={(updatedLabel) => handleEditLabel(index, updatedLabel)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>

                  <Button type="dashed" onClick={() => setShowAddLabelModal(true)} style={{ marginTop: '8px' }}>
                    + 라벨 추가
                  </Button>

                  {modifiedProduct.originData?.labels && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>원본 라벨:</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {(modifiedProduct.originData?.labels || []).map((label, index) => (
                          <div
                            key={index}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: label.source === 'admin' ? '#fff2f0' : '#f5f5f5',
                              borderRadius: '4px',
                              fontSize: '12px',
                              color: '#666',
                              border: `1px solid ${label.source === 'admin' ? '#ff4d4f' : '#d9d9d9'}`,
                            }}>
                            {label.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ marginTop: '24px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                    태그
                    <Tooltip title="쉼표로 구분, 앞 2개만 노출">
                      <QuestionCircleOutlined style={{ marginLeft: '4px' }} />
                    </Tooltip>
                  </div>
                  <TextArea
                    rows={4}
                    style={{ maxWidth: '800px', marginTop: '8px' }}
                    value={((changedData?.tags !== undefined ? changedData?.tags : modifiedProduct.tags) || []).join(
                      ',',
                    )}
                    onChangeValue={(value) => {
                      onChangeProperty(
                        'tags',
                        value.split(',').map((v) => v.trim()),
                      );
                    }}
                    onChange={(e) => {
                      onChangeProperty(
                        'tags',
                        e.target.value.split(',').map((value) => value.trim()),
                      );
                    }}></TextArea>
                  {modifiedProduct.originData?.tags && (
                    <TextArea
                      disabled
                      rows={4}
                      style={{ maxWidth: '800px', marginTop: '8px' }}
                      value={(modifiedProduct.originData?.tags || []).join(',')}
                      onChangeValue={(value) => {
                        onChangeProperty(
                          'tags',
                          value.split(',').map((v) => v.trim()),
                        );
                      }}
                      onChange={(e) => {
                        onChangeProperty(
                          'tags',
                          e.target.value.split(',').map((value) => value.trim()),
                        );
                      }}></TextArea>
                  )}
                </div>
              </div>
            </div>
            <div style={{ borderRadius: '12px', border: '1px solid #eaeaea', padding: '16px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'bottom',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  borderBottom: '1px solid #eaeaea',
                  paddingBottom: '16px',
                }}>
                <div>상세 정보</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ fontSize: '14px' }}>이미지 다운로드</div>
                  <LoadingButton
                    loading={downloading}
                    onClick={async () => {
                      setDownloading(true);
                      const productInfo = document.getElementById('product-information-preview');

                      if (productInfo) {
                        const detailContentImageUrl = await domtoimage.toPng(productInfo, {
                          width: productInfo.offsetWidth * 3,
                          height: productInfo.offsetHeight * 3,
                          style: {
                            transform: `scale(3)`,
                            transformOrigin: 'top left',
                          },
                        });
                        const link = document.createElement('a');
                        link.href = detailContentImageUrl;
                        link.download = `${modifiedProduct.sellerName}_${modifiedProduct.title.slice(
                          0,
                          10,
                        )}_수업정보.png`;
                        link.click();
                        setDownloading(false);
                      }
                    }}>
                    수업 정보
                  </LoadingButton>
                  {modifiedProduct.details?.detailImageTemp ||
                    (modifiedProduct.details?.detailImage && (
                      <LoadingButton
                        loading={downloading}
                        onClick={async () => {
                          setDownloading(true);
                          const templateDetailImage =
                            modifiedProduct.details?.detailImageTemp || modifiedProduct.details?.detailImage;
                          if (templateDetailImage) {
                            try {
                              const imageUrl = cdnImageUrl(templateDetailImage);
                              if (imageUrl) {
                                const response = await fetch(imageUrl);
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = `${modifiedProduct.sellerName}_${modifiedProduct.title.slice(
                                  0,
                                  10,
                                )}_템플릿_상품소개.${templateDetailImage.split('.').pop()}`;
                                link.click();
                                window.URL.revokeObjectURL(url);
                              }
                            } catch (error) {
                              console.error('이미지 다운로드 실패:', error);
                            } finally {
                              setDownloading(false);
                            }
                          }
                        }}>
                        템플릿 상단 상품 소개
                      </LoadingButton>
                    ))}
                  <LoadingButton
                    loading={downloading}
                    onClick={async () => {
                      setDownloading(true);
                      const node = document.getElementById('product-detail-preview');
                      if (node) {
                        const detailContentImageUrl = await domtoimage.toPng(node, {
                          width: node.offsetWidth * 5,
                          height: node.offsetHeight * 5,
                          style: {
                            transform: `scale(5)`,
                            transformOrigin: 'top left',
                          },
                        });
                        const link = document.createElement('a');
                        link.href = detailContentImageUrl;
                        link.download = `${modifiedProduct.sellerName}_${modifiedProduct.title.slice(
                          0,
                          10,
                        )}_템플릿이미지.png`;
                        link.click();
                        setDownloading(false);
                      }
                    }}>
                    상품 상세 정보(본문)
                  </LoadingButton>
                  <LoadingButton
                    loading={downloading}
                    onClick={async () => {
                      setDownloading(true);
                      const refundPolicy = document.getElementById('product-refund-policy-preview');

                      if (refundPolicy) {
                        const detailContentImageUrl = await domtoimage.toPng(refundPolicy, {
                          width: refundPolicy.offsetWidth * 3,
                          height: refundPolicy.offsetHeight * 3,
                          style: {
                            transform: `scale(3)`,
                            transformOrigin: 'top left',
                          },
                        });
                        const link = document.createElement('a');
                        link.href = detailContentImageUrl;
                        link.download = `${modifiedProduct.sellerName}_${modifiedProduct.title.slice(
                          0,
                          10,
                        )}_환불정책.png`;
                        link.click();
                        setDownloading(false);
                      }
                    }}>
                    환불 정책
                  </LoadingButton>
                </div>
              </div>
              <div style={{ marginTop: '12px', marginLeft: '12px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
                  <div style={{ flexShrink: 0, fontSize: '14px', fontWeight: 'bold' }}>유튜브 비디오 ID</div>
                  <Input
                    style={{ width: '240px' }}
                    value={changedData?.details?.youtubeId || modifiedProduct?.details?.youtubeId}
                    onChange={(e) => {
                      let youtubeId = e.target.value;
                      if (youtubeId.includes('https://www.youtube.com/watch?v=')) {
                        youtubeId = youtubeId.split('https://www.youtube.com/watch?v=')[1];
                        youtubeId = youtubeId.split('&')[0];
                      } else if (youtubeId.includes('https://youtu.be/')) {
                        youtubeId = youtubeId.split('https://youtu.be/')[1];
                        youtubeId = youtubeId.split('?')[0];
                      } else if (youtubeId.includes('https://youtube.com/shorts/')) {
                        youtubeId = youtubeId.split('https://youtube.com/shorts/')[1];
                        youtubeId = youtubeId.split('?')[0];
                      } else if (youtubeId.includes('https://www.youtube.com/shorts/')) {
                        youtubeId = youtubeId.split('https://www.youtube.com/shorts/')[1];
                        youtubeId = youtubeId.split('?')[0];
                      }
                      onChangeProperty('details.youtubeId', youtubeId);
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <div style={{ fontSize: '14px', fontWeight: 'bold' }}>상세정보 타입</div>
                  <Select
                    value={changedData?.detailType || modifiedProduct.detailType}
                    style={{ width: '240px', marginLeft: '12px' }}
                    onChange={(value) => {
                      onChangeProperty('detailType', value);
                    }}
                    options={[
                      { value: 'template', label: '템플릿' },
                      { value: 'images', label: '이미지' },
                      { value: 'html', label: 'HTML(구 커머스)' },
                    ]}
                  />
                  {modifiedProduct.originData?.detailType && (
                    <Select
                      disabled
                      value={modifiedProduct.originData?.detailType}
                      style={{ width: '240px', marginLeft: '12px' }}
                      options={[
                        { value: 'template', label: '템플릿' },
                        { value: 'images', label: '이미지' },
                        { value: 'html', label: 'HTML(구 커머스)' },
                      ]}
                    />
                  )}
                </div>
                <div style={{ marginTop: '12px' }}>
                  {modifiedProduct.detailType === 'html' && (
                    <div style={{ width: '640px' }}>
                      {loadingEditorImageUpload && '이미지를 업로드 중 입니다...'}
                      <CKEditor
                        config={{ extraPlugins: [CKEditorIgogoImageUploader] }}
                        editor={ClassicEditor}
                        data={changedData?.detailHtml || modifiedProduct.detailHtml}
                        onChange={(event, editor) => {
                          const htmlString = editor.ui.editor.getData();
                          if (!htmlString.includes('<img>')) {
                            const dom = cheerio.load(htmlString);
                            dom('figure').attr('style', 'margin:0;');
                            dom('img').attr('width', '100%');
                            dom('img').removeAttr('height');
                            const result = dom
                              .html()
                              .replace('<html>', '')
                              .replace('</html>', '')
                              .replace('<head>', '')
                              .replace('</head>', '')
                              .replace('<body>', '')
                              .replace('</body>', '');

                            if (editorTimerRef.current) {
                              clearTimeout(editorTimerRef.current);
                            }

                            editorTimerRef.current = setTimeout(() => {
                              onChangeProperty('detailHtml', result);
                              setLoadingEditorImageUpload(false);
                            }, 500);
                          } else {
                            setLoadingEditorImageUpload(true);
                          }
                        }}
                      />
                    </div>
                  )}
                  {modifiedProduct.detailType === 'images' && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <div
                          style={{
                            fontSize: '16px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}>
                          <div>상세 소개 이미지</div>
                          <Button
                            onClick={async () => {
                              const images =
                                modifiedProduct.detailBodyImagesTemp || modifiedProduct.detailBodyImages || [];
                              for (let i = 0; i < images.length; i++) {
                                const imageUrl = cdnImageUrl(images[i]);
                                if (imageUrl) {
                                  const response = await fetch(imageUrl);
                                  response.blob().then((blob) => {
                                    resizeImage(blob, {
                                      format: 'image/png',
                                    }).then((result) => {
                                      const url = window.URL.createObjectURL(result.image);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = `${modifiedProduct.sellerName}_${modifiedProduct.title.slice(
                                        0,
                                        10,
                                      )}_상세_${i}`;
                                      a.click();
                                    });
                                  });
                                }
                              }
                            }}>
                            전체 다운로드
                          </Button>
                        </div>
                        <FileUploadButton
                          multiple
                          accept={['jpg', 'jpeg', 'png', 'tif', 'tiff', 'webp', 'svg']
                            .map((ext) => {
                              return '.' + ext;
                            })
                            .join(',')}
                          className="link-button"
                          style={{
                            fontSize: '14px',
                            height: '32px',
                            padding: '4px 15px',
                            borderRadius: '6px',
                            backgroundColor: '#ffffff',
                            border: '1px solid #d9d9d9',
                            color: 'rgba(0, 0, 0, 0.88)',
                            boxShadow: '0 2px 0 rgba(0, 0, 0, 0.02)',
                            cursor: 'pointer',
                            marginLeft: '12px',
                          }}
                          onChange={(e) => {
                            if (e.target.files) {
                              const files = e.target.files;
                              onChangeDetailBodyImageFiles(files);
                            }
                          }}>
                          상세 소개 이미지 추가
                        </FileUploadButton>
                      </div>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={onDetailBodyImagesSortEnd}>
                        <SortableContext
                          items={(modifiedProduct.detailBodyImagesTemp || modifiedProduct.detailBodyImages || []).map(
                            (item) => item,
                          )}
                          strategy={horizontalListSortingStrategy}>
                          <div style={{ display: 'flex', gap: '4px', overflow: 'auto', maxWidth: '600px' }}>
                            {(modifiedProduct.detailBodyImagesTemp || modifiedProduct.detailBodyImages || []).map(
                              (imageUrl: string, index: number) => {
                                return (
                                  <SortableDetailImage
                                    key={`image-${imageUrl}-${index}`}
                                    id={imageUrl}
                                    onDeleteDetailImage={() => {
                                      onDeleteDetailBodyImage(index);
                                    }}
                                    imageUrl={imageUrl}
                                  />
                                );
                              },
                            )}
                          </div>
                        </SortableContext>
                      </DndContext>
                      {modifiedProduct.originData?.detailBodyImages && (
                        <div
                          style={{
                            display: 'flex',
                            gap: '4px',
                            overflow: 'auto',
                            minWidth: 0,
                            maxWidth: '600px',
                            flexWrap: 'nowrap',
                          }}>
                          {(modifiedProduct.originData?.detailBodyImages || []).map(
                            (imageUrl: string, index: number) => {
                              return (
                                <div
                                  key={`origin-detail-body-${index}`}
                                  style={{
                                    width: '184px',
                                    height: '184px',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    flex: '0 0 184px',
                                  }}>
                                  <img
                                    src={cdnImageUrl(imageUrl)}
                                    style={{ width: '184px', height: '184px', objectFit: 'cover' }}></img>
                                </div>
                              );
                            },
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {modifiedProduct.detailType === 'template' && (
                    <Collapse
                      defaultActiveKey={templateDefaultActiveKeys}
                      style={{ backgroundColor: 'transparent', border: 'none' }}
                      size="small">
                      <Collapse.Panel
                        header={
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              width: '100%',
                            }}>
                            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>템플릿 상단 상품 소개 이미지</span>
                            <Switch
                              checked={
                                !(changedData?.details?.disableDetailImage !== undefined
                                  ? changedData.details.disableDetailImage
                                  : modifiedProduct.details?.disableDetailImage)
                              }
                              onChange={(checked, event) => {
                                event.stopPropagation();
                                onChangeTemplateSectionUsage('detailImage', checked);
                              }}
                              size="small"
                            />
                          </div>
                        }
                        key="detailImage"
                        style={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #eaeaea',
                          borderRadius: '8px',
                          marginBottom: '12px',
                        }}>
                        <div>
                          <img
                            style={{
                              width: '480px',
                              objectFit: 'cover',
                              marginTop: '4px',
                              display: 'block',
                            }}
                            src={cdnImageUrl(
                              modifiedProduct?.details?.detailImageTemp || modifiedProduct.details?.detailImage,
                            )}></img>
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: '4px',
                              width: '480px',
                            }}>
                            <FileUploadButton
                              accept={['jpg', 'jpeg', 'png', 'tif', 'tiff', 'webp', 'svg']
                                .map((ext) => {
                                  return '.' + ext;
                                })
                                .join(',')}
                              className="link-button"
                              style={{
                                display: 'flex',
                                alignSelf: 'center',
                                justifyContent: 'center',
                                height: '36px',
                                flex: 1,
                                border: '1px solid #aaaaaa',
                                borderRadius: '8px',
                                lineHeight: '36px',
                                marginTop: '4px',
                              }}
                              onChange={(e) => {
                                if (e.target.files) {
                                  const files = e.target.files;
                                  onChangeTemplateDetailImage(files);
                                }
                              }}>
                              템플릿 상단 상품 소개 이미지 변경
                            </FileUploadButton>
                            <Button
                              onClick={() => {
                                onChangeProperty('details.detailImageTemp', '');
                                onChangeProperty('details.detailImage', '');
                                setFiles((value) => {
                                  const newValue = { ...value };
                                  delete newValue['details.detailImage'];
                                  return newValue;
                                });
                              }}>
                              <DeleteOutlined style={{ color: 'red' }} />
                            </Button>
                          </div>
                        </div>
                      </Collapse.Panel>
                      <Collapse.Panel
                        header={
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              width: '100%',
                            }}>
                            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>무엇을 경험하나요?</span>
                            <Switch
                              checked={
                                !(changedData?.details?.disableClassIntroduction !== undefined
                                  ? changedData.details.disableClassIntroduction
                                  : modifiedProduct.details?.disableClassIntroduction)
                              }
                              onChange={(checked, event) => {
                                event.stopPropagation();
                                onChangeTemplateSectionUsage('classIntroduction', checked);
                              }}
                              size="small"
                            />
                          </div>
                        }
                        key="classIntroduction"
                        style={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #eaeaea',
                          borderRadius: '8px',
                          marginBottom: '12px',
                        }}>
                        <div>
                          {(changedData?.classIntroductionImageTemp || modifiedProduct.classIntroductionImage) && (
                            <img
                              style={{
                                width: '480px',
                                height: '240px',
                                objectFit: 'cover',
                                marginTop: '4px',
                                display: 'block',
                              }}
                              src={cdnImageUrl(
                                changedData?.classIntroductionImageTemp || modifiedProduct.classIntroductionImage,
                              )}></img>
                          )}
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: '4px',
                              width: '480px',
                            }}>
                            <FileUploadButton
                              accept={['jpg', 'jpeg', 'png', 'tif', 'tiff', 'webp', 'svg']
                                .map((ext) => {
                                  return '.' + ext;
                                })
                                .join(',')}
                              className="link-button"
                              style={{
                                display: 'flex',
                                alignSelf: 'center',
                                justifyContent: 'center',
                                height: '36px',
                                flex: 1,
                                border: '1px solid #aaaaaa',
                                borderRadius: '8px',
                                lineHeight: '36px',
                                marginTop: '4px',
                              }}
                              onChange={(e) => {
                                if (e.target.files) {
                                  const files = e.target.files;
                                  onChangeClassDetailImage(files);
                                }
                              }}>
                              클래스 소개 이미지 변경
                            </FileUploadButton>
                            <Button
                              onClick={() => {
                                onChangeProperty('classIntroductionImageTemp', null);
                                onChangeProperty('classIntroductionImage', null);
                                setFiles((value) => {
                                  const newValue = { ...value };
                                  delete newValue['classIntroductionImage'];
                                  return newValue;
                                });
                              }}>
                              <DeleteOutlined style={{ color: 'red' }} />
                            </Button>
                          </div>
                        </div>
                        <ClassDetailEdit
                          items={(
                            changedData?.details?.introductions ||
                            modifiedProduct.details?.introductions ||
                            []
                          ).map((item: { idTemp?: string; title?: string; description?: string }) => {
                            if (!item.idTemp) {
                              item.idTemp = v4();
                            }
                            return { title: item.title, description: item.description, idTemp: item?.idTemp };
                          })}
                          onChange={(items) => {
                            onChangeProperty(
                              'details.introductions',
                              items.map((item) => {
                                if (item?.files) {
                                  delete item['files'];
                                }
                                return item;
                              }),
                            );
                          }}></ClassDetailEdit>
                      </Collapse.Panel>
                      <Collapse.Panel
                        header={
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              width: '100%',
                            }}>
                            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>어떤 아이에게 추천하나요</span>
                            <Switch
                              checked={
                                !(changedData?.details?.disableRecommendText !== undefined
                                  ? changedData.details.disableRecommendText
                                  : modifiedProduct.details?.disableRecommendText)
                              }
                              onChange={(checked, event) => {
                                event.stopPropagation();
                                onChangeTemplateSectionUsage('recommendText', checked);
                              }}
                              size="small"
                            />
                          </div>
                        }
                        key="recommendText"
                        style={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #eaeaea',
                          borderRadius: '8px',
                          marginBottom: '12px',
                        }}>
                        <TextArea
                          rows={4}
                          style={{ maxWidth: '428px' }}
                          value={
                            changedData?.details?.recommendText !== undefined
                              ? changedData?.details?.recommendText
                              : modifiedProduct.details?.recommendText
                          }
                          onChangeValue={(value) => {
                            onChangeProperty('details.recommendText', value);
                          }}
                          onChange={(e) => {
                            onChangeProperty('details.recommendText', e.target.value);
                          }}></TextArea>
                      </Collapse.Panel>
                      <Collapse.Panel
                        header={
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              width: '100%',
                            }}>
                            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>어떻게 진행되나요</span>
                            <Switch
                              checked={
                                !(changedData?.details?.disableConcepts !== undefined
                                  ? changedData.details.disableConcepts
                                  : modifiedProduct.details?.disableConcepts)
                              }
                              onChange={(checked, event) => {
                                event.stopPropagation();
                                onChangeTemplateSectionUsage('concepts', checked);
                              }}
                              size="small"
                            />
                          </div>
                        }
                        key="concepts"
                        style={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #eaeaea',
                          borderRadius: '8px',
                          marginBottom: '12px',
                        }}>
                        <ClassDetailEdit
                          editImage
                          items={(changedData?.details?.concepts || modifiedProduct.details?.concepts || []).map(
                            (item: Concept) => {
                              if (!item.idTemp) {
                                item.idTemp = v4();
                              }
                              return {
                                title: item.conceptTitle,
                                description: item.conceptData,
                                images: item.images as string[],
                                imagesTemp: item.imagesTemp as string[],
                                idTemp: item?.idTemp,
                              };
                            },
                          )}
                          onChange={(items) => {
                            onChangeProperty(
                              'details.concepts',
                              items.map((item, index) => {
                                if (item.files) {
                                  item.files.forEach((file, innerIndex) => {
                                    setFiles((value) => {
                                      const newValue = { ...value };
                                      newValue[`details.concepts.${index}.images.${innerIndex}`] = file;
                                      return newValue;
                                    });
                                  });
                                }
                                return {
                                  conceptTitle: item.title,
                                  conceptData: item.description,
                                  images: item.images,
                                  imagesTemp: item.imagesTemp,
                                  idTemp: item?.idTemp,
                                };
                              }),
                            );
                          }}></ClassDetailEdit>
                      </Collapse.Panel>
                      <Collapse.Panel
                        header={
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              width: '100%',
                            }}>
                            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>준비물 안내</span>
                            <Switch
                              checked={
                                !(changedData?.details?.disablePreparationText !== undefined
                                  ? changedData.details.disablePreparationText
                                  : modifiedProduct.details?.disablePreparationText)
                              }
                              onChange={(checked, event) => {
                                event.stopPropagation();
                                onChangeTemplateSectionUsage('preparationText', checked);
                              }}
                              size="small"
                            />
                          </div>
                        }
                        key="preparationText"
                        style={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #eaeaea',
                          borderRadius: '8px',
                          marginBottom: '12px',
                        }}>
                        <TextArea
                          rows={4}
                          style={{ maxWidth: '428px' }}
                          value={
                            changedData?.details?.preparationText !== undefined
                              ? changedData?.details?.preparationText
                              : modifiedProduct.details?.preparationText
                          }
                          onChangeValue={(value) => {
                            onChangeProperty('details.preparationText', value);
                          }}
                          onChange={(e) => {
                            onChangeProperty('details.preparationText', e.target.value);
                          }}></TextArea>
                      </Collapse.Panel>
                    </Collapse>
                  )}
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 'bold' }}>문의하기</div>
                  {partnerData && (
                    <div style={{ width: '520px' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ width: '120px' }}>대표고객센터</div>
                        <Select
                          placeholder={'대표고객센터 선택'}
                          value={changeCustomerCenter.mainCustomerService || partnerData.profile?.mainCustomerService}
                          options={[
                            { value: 'chat', label: '아이고고 앱 채팅' },
                            { value: 'phone', label: '전화번호' },
                            { value: 'url', label: 'URL' },
                          ]}
                          onSelect={(value) => {
                            setChangeCustomerCenter((prev: Partial<UserProfile>) => {
                              return { ...prev, mainCustomerService: value };
                            });
                          }}></Select>
                        <div style={{ marginLeft: 'auto' }}>
                          <Button
                            disabled={Object.keys(changeCustomerCenter).length === 0}
                            onClick={() => {
                              mutaionUpdatePartnerProfile.mutate({
                                userId: partnerData._id,
                                body: { ...partnerData.profile, ...changeCustomerCenter },
                              });
                            }}>
                            변경사항 적용하기
                          </Button>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                        <div style={{ width: '120px' }}>전화번호</div>
                        <TextInput
                          value={
                            changeCustomerCenter?.customerServicePhone !== undefined
                              ? changeCustomerCenter?.customerServicePhone
                              : partnerData.profile?.customerServicePhone
                          }
                          onChangeValue={(value) => {
                            setChangeCustomerCenter((prev: Partial<UserProfile>) => {
                              return { ...prev, customerServicePhone: value };
                            });
                          }}
                          onChange={(e) => {
                            setChangeCustomerCenter((prev: Partial<UserProfile>) => {
                              return { ...prev, customerServicePhone: e.target.value };
                            });
                          }}></TextInput>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                        <div style={{ width: '120px' }}>URL</div>
                        <TextInput
                          value={
                            changeCustomerCenter?.customerServiceUrl !== undefined
                              ? changeCustomerCenter?.customerServiceUrl
                              : partnerData.profile?.customerServiceUrl
                          }
                          onChangeValue={(value) => {
                            setChangeCustomerCenter((prev: Partial<UserProfile>) => {
                              return { ...prev, customerServiceUrl: value };
                            });
                          }}
                          onChange={(e) => {
                            setChangeCustomerCenter((prev: Partial<UserProfile>) => {
                              return { ...prev, customerServiceUrl: e.target.value };
                            });
                          }}></TextInput>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div style={{ borderRadius: '12px', border: '1px solid #eaeaea', padding: '16px' }}>
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  borderBottom: '1px solid #eaeaea',
                  height: '32px',
                }}>
                가격 정보
              </div>
              <div style={{ marginTop: '12px', marginLeft: '12px' }}>
                <div style={{ marginTop: '24px' }}>
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: '120px' }}>판매가(필수)</th>
                        <th style={{ width: '120px' }}>정가</th>
                        <th style={{ width: '160px' }}>할인율(자동계산)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <TextInput
                            style={{ width: '100px', textAlign: 'right' }}
                            value={changedData?.fee !== undefined ? changedData?.fee : modifiedProduct.fee}
                            onChangeValue={(value) => {
                              const numberValue = parseInt(value) || 0;
                              onChangeProperty('fee', numberValue);
                              const originFee = changedData?.originFee || modifiedProduct.originFee;
                              if (originFee) {
                                const numberOriginFee = Number(originFee) || 0;
                                if (!numberOriginFee || !numberValue) {
                                  onChangeProperty('discount', null);
                                } else {
                                  onChangeProperty(
                                    'discount',
                                    `${100 - Math.round((numberValue / numberOriginFee) * 100)}%`,
                                  );
                                }
                              }
                            }}></TextInput>
                          <span>원</span>
                          {modifiedProduct.originData?.fee && (
                            <div>
                              <TextInput
                                disabled
                                style={{ width: '100px', textAlign: 'right' }}
                                value={modifiedProduct.originData?.fee}></TextInput>
                              <span>원</span>
                            </div>
                          )}
                        </td>
                        <td>
                          <TextInput
                            style={{ width: '100px', textAlign: 'right' }}
                            value={
                              changedData?.originFee !== undefined ? changedData?.originFee : modifiedProduct.originFee
                            }
                            onChangeValue={(value) => {
                              const numberValue = parseInt(value) || 0;
                              onChangeProperty('originFee', numberValue);
                              const fee = changedData?.fee || modifiedProduct.fee;
                              if (fee) {
                                const numberFee = Number(fee) || 0;
                                if (!numberFee || !numberValue) {
                                  onChangeProperty('discount', null);
                                } else {
                                  onChangeProperty('discount', `${100 - Math.round((numberFee / numberValue) * 100)}%`);
                                }
                              }
                            }}></TextInput>
                          <span>원</span>
                          {modifiedProduct.originData?.originFee && (
                            <div>
                              <TextInput
                                disabled
                                style={{ width: '100px', textAlign: 'right' }}
                                value={modifiedProduct.originData?.originFee}></TextInput>
                              <span>원</span>
                            </div>
                          )}
                        </td>
                        <td style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <TextInput
                            disabled
                            style={{ width: '100px', textAlign: 'right' }}
                            value={changedData?.discount || modifiedProduct.discount}></TextInput>
                          {modifiedProduct.originData?.discount && (
                            <div>
                              <TextInput
                                disabled
                                style={{ width: '100px', textAlign: 'right' }}
                                value={modifiedProduct.originData?.discount}></TextInput>
                            </div>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div>
                    <Checkbox
                      checked={changedData?.applyPeriodDiscount || modifiedProduct.applyPeriodDiscount}
                      onChange={(e) => {
                        onChangeProperty('applyPeriodDiscount', e.target.checked);
                      }}>
                      기간 한정 할인
                    </Checkbox>
                    {modifiedProduct.originData?.applyPeriodDiscount !== undefined && (
                      <div>
                        <Checkbox disabled checked={modifiedProduct.originData?.applyPeriodDiscount}>
                          기간 한정 할인
                        </Checkbox>
                      </div>
                    )}
                  </div>
                  {modifiedProduct.applyPeriodDiscount && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                      <div
                        style={{
                          fontSize: '14px',
                          fontWeight: 700,
                          color: '#424242',
                          fontFamily: 'Pretendard',
                          marginTop: '0',
                        }}>
                        기간 한정 할인 적용 제외 채널
                      </div>
                      <div style={{ fontSize: '12px', color: '#757575', lineHeight: 1.5 }}>
                        선택한 채널에서는 기간 한정 할인가가 적용되지 않습니다.
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 20px' }}>
                        {PERIOD_DISCOUNT_EXCLUDE_CHANNEL_OPTIONS.map(({ value, label }) => (
                          <Checkbox
                            key={value}
                            checked={periodDiscountExcludeChannels.includes(value)}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? periodDiscountExcludeChannels.includes(value)
                                  ? periodDiscountExcludeChannels
                                  : [...periodDiscountExcludeChannels, value]
                                : periodDiscountExcludeChannels.filter((c) => c !== value);
                              onChangeProperty('periodDiscountExcludeChannels', next);
                            }}>
                            {label}
                          </Checkbox>
                        ))}
                      </div>
                      {modifiedProduct.originData?.periodDiscountExcludeChannels !== undefined && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 20px' }}>
                          {PERIOD_DISCOUNT_EXCLUDE_CHANNEL_OPTIONS.map(({ value, label }) => (
                            <Checkbox
                              key={`origin-${value}`}
                              disabled
                              checked={(modifiedProduct.originData?.periodDiscountExcludeChannels ?? []).includes(
                                value,
                              )}>
                              {label}
                            </Checkbox>
                          ))}
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#424242',
                          fontFamily: 'Pretendard',
                          marginTop: '12px',
                        }}>
                        시작일
                      </div>
                      <DatePicker
                        allowClear={false}
                        style={{
                          height: '40px',
                          borderRadius: 0,
                          paddingRight: 0,
                          cursor: 'pointer',
                          maxWidth: '400px',
                        }}
                        placeholder={'시작일 선택'}
                        popupStyle={{ zIndex: 99999 }}
                        value={discountInfo?.discountStartDate ? dayjs(discountInfo?.discountStartDate) : undefined}
                        showTime={{ showSecond: false }}
                        onChange={(value, dateString) => {
                          onChangeProperty(
                            'discountInfo.discountStartDate',
                            moment(dateString, 'YYYY-MM-DD HH:mm:00').toDate(),
                          );
                        }}
                      />
                      {modifiedProduct.originData?.discountInfo?.discountStartDate && (
                        <div>
                          <DatePicker
                            disabled
                            allowClear={false}
                            style={{
                              height: '40px',
                              borderRadius: 0,
                              paddingRight: 0,
                              cursor: 'pointer',
                              maxWidth: '400px',
                            }}
                            placeholder={'시작일 선택'}
                            popupStyle={{ zIndex: 99999 }}
                            value={dayjs(modifiedProduct.originData?.discountInfo?.discountStartDate)}
                            showTime={{ showSecond: false }}
                          />
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#424242',
                          fontFamily: 'Pretendard',
                          marginTop: '12px',
                        }}>
                        종료일
                      </div>
                      <DatePicker
                        allowClear={false}
                        style={{
                          height: '40px',
                          borderRadius: 0,
                          paddingRight: 0,
                          cursor: 'pointer',
                          maxWidth: '400px',
                        }}
                        placeholder={'종료일 선택'}
                        popupStyle={{ zIndex: 99999 }}
                        value={discountInfo?.discountEndDate ? dayjs(discountInfo?.discountEndDate) : undefined}
                        showTime={{ showSecond: false }}
                        onChange={(value, dateString) => {
                          onChangeProperty(
                            'discountInfo.discountEndDate',
                            moment(dateString, 'YYYY-MM-DD HH:mm:00').toDate(),
                          );
                        }}></DatePicker>
                      {modifiedProduct.originData?.discountInfo?.discountEndDate && (
                        <div>
                          <DatePicker
                            disabled
                            allowClear={false}
                            style={{
                              height: '40px',
                              borderRadius: 0,
                              paddingRight: 0,
                              cursor: 'pointer',
                              maxWidth: '400px',
                            }}
                            placeholder={'종료일 선택'}
                            popupStyle={{ zIndex: 99999 }}
                            value={dayjs(modifiedProduct.originData?.discountInfo?.discountEndDate)}
                            showTime={{ showSecond: false }}
                          />
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#424242',
                          fontFamily: 'Pretendard',
                          marginTop: '12px',
                        }}>
                        판매가
                      </div>
                      <div>
                        <TextInput
                          type="number"
                          style={{ width: '400px' }}
                          value={discountInfo?.discountedFee || 0}
                          onChangeValue={(value) => {
                            onChangeProperty('discountInfo.discountedFee', parseInt(value) || 0);
                          }}></TextInput>
                        <span>원</span>
                      </div>
                      {modifiedProduct.originData?.discountInfo?.discountedFee && (
                        <div>
                          <TextInput
                            disabled
                            type="number"
                            style={{ width: '400px' }}
                            value={modifiedProduct.originData?.discountInfo?.discountedFee || 0}></TextInput>
                          <span>원</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ marginTop: '24px' }}>
                  {modifiedProduct.activityType === ProductActivityType.DELIVERY && (
                    <div>
                      <div
                        style={{
                          fontSize: '13px',
                          fontWeight: 'bold',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}>
                        <div>배송비</div>
                        <TextInput
                          style={{ width: '100px', textAlign: 'right' }}
                          value={
                            changedData?.shippingFee !== undefined
                              ? changedData?.shippingFee
                              : modifiedProduct.shippingFee
                          }
                          onChangeValue={(value) => {
                            onChangeProperty('shippingFee', parseInt(value) || 0);
                          }}
                          onChange={(e) => {
                            onChangeProperty('shippingFee', parseInt(e.target.value) || 0);
                          }}></TextInput>
                        <span>원</span>
                        {modifiedProduct.originData?.shippingFee && (
                          <div>
                            <TextInput
                              disabled
                              type="number"
                              style={{ width: '400px' }}
                              value={modifiedProduct.originData?.shippingFee || 0}></TextInput>
                            <span>원</span>
                          </div>
                        )}
                      </div>
                      <div style={{ marginTop: '24px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>배송 정책</div>
                        <TextArea
                          rows={4}
                          style={{ maxWidth: '428px' }}
                          value={
                            changedData?.shippingPolicyText !== undefined
                              ? changedData?.shippingPolicyText
                              : modifiedProduct.shippingPolicyText
                          }
                          onChangeValue={(value) => {
                            onChangeProperty('shippingPolicyText', value);
                          }}
                          onChange={(e) => {
                            onChangeProperty('shippingPolicyText', e.target.value);
                          }}></TextArea>
                        {modifiedProduct.originData?.shippingPolicyText && (
                          <div>
                            <TextArea
                              disabled
                              rows={4}
                              style={{ maxWidth: '428px' }}
                              value={modifiedProduct.originData?.shippingPolicyText}></TextArea>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ marginTop: '24px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 'bold' }}>환불 정책</div>
                  <TextArea
                    rows={4}
                    style={{ maxWidth: '428px' }}
                    value={refundPolicyText}
                    onChangeValue={(value) => {
                      onChangeProperty('refundPolicyText', value);
                    }}
                    onChange={(e) => {
                      onChangeProperty('refundPolicyText', e.target.value);
                    }}></TextArea>
                  {modifiedProduct.originData?.refundPolicyText && (
                    <div>
                      <TextArea
                        disabled
                        rows={4}
                        style={{ maxWidth: '428px' }}
                        value={modifiedProduct.originData?.refundPolicyText}></TextArea>
                    </div>
                  )}
                </div>
                <div style={{ marginTop: '24px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 'bold' }}>판매 완료 시 노출 문구(4자)</div>
                  <TextArea
                    rows={4}
                    style={{ maxWidth: '428px' }}
                    value={changedData?.closeText !== undefined ? changedData?.closeText : modifiedProduct.closeText}
                    onChangeValue={(value) => {
                      onChangeProperty('closeText', value);
                    }}
                    onChange={(e) => {
                      onChangeProperty('closeText', e.target.value);
                    }}></TextArea>
                  {modifiedProduct.originData?.closeText && (
                    <div>
                      <TextArea
                        disabled
                        rows={4}
                        style={{ maxWidth: '428px' }}
                        value={modifiedProduct.originData?.closeText}></TextArea>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div style={{ borderRadius: '12px', border: '1px solid #eaeaea', padding: '16px' }}>
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  borderBottom: '1px solid #eaeaea',
                  height: '32px',
                }}>
                부가 정보 ({modifiedProduct.saleType})
              </div>
              <div style={{ marginTop: '12px', marginLeft: '12px' }}>
                <div style={{ marginTop: '12px' }}>
                  {modifiedProduct.saleType === 'commerce' && (
                    <div>
                      <div>
                        <div style={{ marginTop: '24px' }}>
                          <div
                            style={{
                              fontSize: '14px',
                              fontWeight: 'bold',
                              display: 'flex',
                              flexDirection: 'column',
                              marginRight: '10px',
                            }}>
                            <div>입력 옵션(최대 4개)</div>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              {(changedData?.questionOptions || modifiedProduct?.questionOptions || []).map(
                                (option: QuestionOption, index: number) => {
                                  const isPreferredSchedule = option?.questionType === 'preferredSchedule';
                                  return (
                                    <div
                                      key={`question-option-${index}`}
                                      style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px',
                                        marginBottom: '12px',
                                        paddingBottom: '12px',
                                        borderBottom: '1px solid #eff1f4',
                                      }}>
                                      <div
                                        style={{
                                          display: 'flex',
                                          flexDirection: 'row',
                                          alignItems: 'center',
                                          gap: '5px',
                                        }}>
                                        <Select
                                          style={{ width: '160px' }}
                                          value={option?.questionType || 'none'}
                                          placeholder={'입력 옵션 타입 설정'}
                                          options={Object.keys(QUESTION_OPTION_LABELS).map((key) => {
                                            return { label: QUESTION_OPTION_LABELS[key], value: key };
                                          })}
                                          onChange={(value) => {
                                            const newValue = [
                                              ...(changedData?.questionOptions ||
                                                modifiedProduct?.questionOptions ||
                                                []),
                                            ];
                                            if (value === 'preferredSchedule') {
                                              newValue[index] = {
                                                questionType: value,
                                                question: QUESTION_OPTION_DEFAULT_QUESTION.preferredSchedule,
                                                preferredSchedule: newValue[index]?.preferredSchedule || {
                                                  ...DEFAULT_PREFERRED_SCHEDULE,
                                                },
                                              };
                                            } else {
                                              newValue[index] = {
                                                questionType: value,
                                                question: QUESTION_OPTION_DEFAULT_QUESTION[value] || '',
                                              };
                                            }
                                            onChangeProperty('questionOptions', newValue);
                                          }}></Select>
                                        {isPreferredSchedule ? (
                                          <Input
                                            style={{ maxWidth: '428px', background: '#F5F7FB', color: '#B9BBBE' }}
                                            value="희망 일정"
                                            disabled
                                          />
                                        ) : (
                                          <TextInput
                                            style={{ maxWidth: '428px' }}
                                            value={option?.question}
                                            onChangeValue={(value) => {
                                              const newValue = [
                                                ...(changedData?.questionOptions ||
                                                  modifiedProduct?.questionOptions ||
                                                  []),
                                              ];
                                              newValue[index] = {
                                                ...newValue[index],
                                                question: value,
                                                questionType: option?.questionType || 'none',
                                              };
                                              onChangeProperty('questionOptions', newValue);
                                            }}
                                            onChange={(e) => {
                                              const newValue = [
                                                ...(changedData?.questionOptions ||
                                                  modifiedProduct?.questionOptions ||
                                                  []),
                                              ];
                                              newValue[index] = {
                                                ...newValue[index],
                                                question: e.target.value,
                                                questionType: option?.questionType || 'none',
                                              };
                                              onChangeProperty('questionOptions', newValue);
                                            }}></TextInput>
                                        )}
                                        <Button
                                          style={{ color: 'red' }}
                                          onClick={() => {
                                            const newValue: QuestionOption[] = [
                                              ...(changedData?.questionOptions ||
                                                modifiedProduct?.questionOptions ||
                                                []),
                                            ];
                                            newValue.splice(index, 1);
                                            onChangeProperty('questionOptions', newValue);
                                          }}>
                                          삭제
                                        </Button>
                                        {!isPreferredSchedule && (
                                          <span
                                            style={{
                                              color: (option?.question?.length || 0) > 25 ? 'red' : '#424242',
                                            }}>
                                            {option?.question?.length || 0}
                                          </span>
                                        )}
                                      </div>
                                      {isPreferredSchedule && (
                                        <PreferredScheduleConfigPanel
                                          value={option.preferredSchedule}
                                          onChange={(preferredSchedule) => {
                                            const newValue = [
                                              ...(changedData?.questionOptions ||
                                                modifiedProduct?.questionOptions ||
                                                []),
                                            ];
                                            newValue[index] = {
                                              questionType: 'preferredSchedule',
                                              question: '희망 일정',
                                              preferredSchedule,
                                            };
                                            onChangeProperty('questionOptions', newValue);
                                          }}
                                        />
                                      )}
                                    </div>
                                  );
                                },
                              )}
                            </div>
                            {(changedData?.questionOptions || modifiedProduct?.questionOptions || []).length < 4 && (
                              <Button
                                style={{ width: '60px' }}
                                onClick={() => {
                                  const newValue = [
                                    ...(changedData?.questionOptions || modifiedProduct?.questionOptions || []),
                                  ];
                                  newValue.push({ question: '', questionType: 'none' });
                                  onChangeProperty('questionOptions', newValue);
                                }}>
                                추가
                              </Button>
                            )}
                            {modifiedProduct.originData?.questionOptions && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                {(modifiedProduct.originData?.questionOptions || []).map((item, originIndex) => {
                                  return <div key={`origin-question-${originIndex}`}>{item.question}</div>;
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                        <div style={{ marginTop: '24px' }}>
                          <div style={{ fontSize: '14px', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                            <div>구매 시 입력 요청 멘트</div>
                            <div style={{ marginLeft: '24px', marginRight: '4px' }}>필수입력</div>
                            <Switch
                              checked={modifiedProduct.memoRequired}
                              onChange={(value) => {
                                onChangeProperty('memoRequired', value);
                              }}
                            />
                            {modifiedProduct.originData?.memoRequired !== undefined && (
                              <Switch disabled checked={modifiedProduct.memoRequired} />
                            )}
                          </div>
                          <TextInput
                            style={{ maxWidth: '428px' }}
                            value={
                              changedData?.memoComment !== undefined
                                ? changedData?.memoComment
                                : modifiedProduct.memoComment
                            }
                            onChangeValue={(value) => {
                              onChangeProperty('memoComment', value);
                            }}
                            onChange={(e) => {
                              onChangeProperty('memoComment', e.target.value);
                            }}></TextInput>
                          {modifiedProduct.originData?.memoComment && (
                            <TextInput
                              disabled
                              style={{ maxWidth: '428px' }}
                              value={modifiedProduct.originData?.memoComment}></TextInput>
                          )}
                        </div>
                        <div
                          style={{
                            marginTop: '24px',
                            borderRadius: '8px',
                            border: '1px solid #eaeaea',
                            padding: '16px',
                          }}>
                          <OptionDescriptionSetting
                            value={
                              Object.prototype.hasOwnProperty.call(changedData, 'optionDescription')
                                ? changedData.optionDescription
                                : modifiedProduct.optionDescription || ''
                            }
                            previewLabel={
                              [...(changedData?.productOptions || modifiedProduct.productOptions || [])]
                                .filter((option) => option.type === 'sequential')
                                .sort((lv, rv) => (lv.index || 0) - (rv.index || 0))[0]
                                ?.step?.filter(Boolean)
                                .join(' / ') || '1/12(목) / 1:30 / 고구려'
                            }
                            previewPrice={changedData?.fee !== undefined ? changedData.fee : modifiedProduct.fee || 0}
                            onChange={(value) => {
                              onChangeProperty('optionDescription', value);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {modifiedProduct.saleType === 'class' && (
                    <div>
                      <div style={{ marginTop: '12px', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <div style={{ fontSize: '14px', fontWeight: 'bold' }}>하단 텍스트 배너 설정(CTA)</div>
                          <div>
                            <Switch
                              checked={modifiedProduct.bottomTextBanner?.show}
                              onChange={(value) => {
                                onChangeProperty('bottomTextBanner.show', value);
                              }}
                            />
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <FileUploadButton
                            accept={['jpg', 'jpeg', 'png', 'tif', 'tiff', 'webp', 'svg']
                              .map((ext) => {
                                return '.' + ext;
                              })
                              .join(',')}
                            className="link-button"
                            style={{
                              display: 'flex',
                              alignSelf: 'center',
                              justifyContent: 'center',
                              height: '36px',
                              width: '184px',
                              border: '1px solid #aaaaaa',
                              borderRadius: '8px',
                              lineHeight: '36px',
                              marginTop: '4px',
                            }}
                            onChange={(e) => {
                              if (e.target.files) {
                                const files = e.target.files;
                                onChangeBottomTextBannerIconFile(files);
                              }
                            }}>
                            아이콘 변경
                          </FileUploadButton>
                          <img
                            style={{ width: '20px', height: '20px', objectFit: 'cover' }}
                            src={cdnImageUrl(
                              changedData?.bottomTextBanner?.iconTemp || modifiedProduct.bottomTextBanner?.icon,
                            )}></img>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            width: '528px',
                            marginTop: '8px',
                            alignItems: 'center',
                          }}>
                          <div style={{ flexShrink: 0, fontWeight: 'bold' }}>강조 텍스트</div>
                          <TextInput
                            value={
                              changedData?.bottomTextBanner?.highlightText !== undefined
                                ? changedData?.bottomTextBanner?.highlightText
                                : modifiedProduct.bottomTextBanner?.highlightText
                            }
                            onChangeValue={(value) => {
                              onChangeProperty('bottomTextBanner.highlightText', value);
                            }}></TextInput>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            width: '528px',
                            marginTop: '8px',
                            alignItems: 'center',
                          }}>
                          <div style={{ flexShrink: 0, fontWeight: 'bold' }}>일반 텍스트</div>
                          <TextInput
                            value={
                              changedData?.bottomTextBanner?.normalText !== undefined
                                ? changedData?.bottomTextBanner?.normalText
                                : modifiedProduct.bottomTextBanner?.normalText
                            }
                            onChangeValue={(value) => {
                              onChangeProperty('bottomTextBanner.normalText', value);
                            }}></TextInput>
                        </div>
                      </div>
                      <div style={{ alignItems: 'center', marginTop: '12px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>뱃지</div>
                        <div style={{ display: 'flex', marginTop: '8px', gap: '12px' }}>
                          {Object.keys(BADGES).map((key) => {
                            return (
                              <Checkbox
                                key={key}
                                checked={
                                  !!(changedData?.badges || modifiedProduct.badges || []).find(
                                    (badge: ClassBadge) => badge.value === key,
                                  )
                                }
                                onChange={(event) => {
                                  onChangeBadgeChecked(key as any, event.target.checked);
                                }}>
                                {key}
                              </Checkbox>
                            );
                          })}
                        </div>

                        <div style={{ fontSize: '13px' }}>
                          <div>{'* 뱃지 최대 2개 적용'}</div>
                          <div>{'* 우선순위 : 이벤트 > 신청마감 > 업데이트 > NEW > 혜택 > 그룹할인'}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div style={{ borderRadius: '12px', border: '1px solid #eaeaea', padding: '16px' }}>
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  borderBottom: '1px solid #eaeaea',
                  height: '32px',
                }}>
                쿠팡 상품 설정
              </div>
              <div style={{ marginTop: '12px', marginLeft: '12px' }}>
                <CoupangProductsManager
                  coupangProduct={changedData?.coupangProduct || modifiedProduct.coupangProduct}
                  onAddPreparation={addCoupangPreparation}
                  onRemovePreparation={removeCoupangPreparation}
                  onUpdateTitle={updateCoupangTitle}
                  onReorderProducts={reorderCoupangProducts}
                />
              </div>
            </div>
            <div style={{ borderRadius: '12px', border: '1px solid #eaeaea', padding: '16px' }}>
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  borderBottom: '1px solid #eaeaea',
                  height: '32px',
                }}>
                재고 마감 설정
              </div>
              <div style={{ marginTop: '12px', marginLeft: '12px' }}>
                <Checkbox
                  style={{ width: '140px' }}
                  checked={changedData.autoCloseEnabled || modifiedProduct.autoCloseEnabled}
                  onChange={(e) => {
                    onChangeProperty('autoCloseEnabled', e.target.checked);
                  }}>
                  자동 마감 사용
                </Checkbox>
                {modifiedProduct.originData?.autoCloseEnabled !== undefined && (
                  <Checkbox disabled style={{ width: '140px' }} checked={modifiedProduct.originData?.autoCloseEnabled}>
                    자동 마감 사용
                  </Checkbox>
                )}
                <div style={{ fontWeight: 'bold', marginTop: '8px' }}>
                  <div>자동 마감 기간</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                  D+
                  <TextInput
                    style={{ width: '100px' }}
                    value={
                      changedData?.autoCloseDuration !== undefined
                        ? changedData?.autoCloseDuration
                        : modifiedProduct.autoCloseDuration
                    }
                    onChangeValue={(value) => {
                      onChangeProperty('autoCloseDuration', Number(value) || 0);
                    }}></TextInput>
                  일
                </div>
                {modifiedProduct.originData?.autoCloseDuration !== undefined && (
                  <div style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                    D+
                    <TextInput
                      disabled
                      style={{ width: '100px' }}
                      value={modifiedProduct.originData?.autoCloseDuration}></TextInput>
                    일
                  </div>
                )}
                <div style={{ fontWeight: 'normal' }}>
                  예시: 위 값이 2이면, 12월 17일에 재고마감 시 12월 19일 23시 59분 59초까지 모든 일정을 마감
                </div>
              </div>
            </div>
            <div style={{ borderRadius: '12px', border: '1px solid #eaeaea', padding: '16px' }}>
              <div style={{ width: '440px', position: 'fixed', top: 0, right: '-440px' }}>
                <ProductDetailPreview product={modifiedProduct} />
              </div>
              <div style={{ width: '440px', position: 'fixed', top: 0, right: '-440px' }}>
                <ProductInformation data={modifiedProduct} />
              </div>
              <div style={{ width: '440px', position: 'fixed', top: 0, right: '-440px' }}>
                <ProductRefundPolicy data={modifiedProduct} />
              </div>
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  borderBottom: '1px solid #eaeaea',
                  height: '32px',
                }}>
                외부 판매 채널
              </div>
              <div style={{ marginTop: '12px', marginLeft: '12px' }}>
                {/* 베네피아 */}
                <div>
                  <div
                    style={{
                      fontSize: '14px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '8px',
                      gap: '8px',
                    }}>
                    <div
                      style={{
                        color: '#fff',
                        backgroundColor: '#FF3D8F',
                        fontSize: '12px',
                        padding: '4px 8px',
                        borderRadius: '4px',
                      }}>
                      베네피아
                    </div>
                    <Select
                      value={
                        changedData?.linkedSalesChannel?.benepia?.status ||
                        modifiedProduct?.linkedSalesChannel?.benepia?.status
                      }
                      style={{ width: 120 }}
                      onChange={(value) => {
                        onChangeProperty('linkedSalesChannel.benepia.status', value);
                      }}
                      options={[
                        { value: LinkedSalesChannelStatus.OPEN, label: '노출' },
                        { value: LinkedSalesChannelStatus.CLOSE, label: '노출중지' },
                      ]}
                    />
                    <Checkbox
                      style={{ width: '140px' }}
                      checked={benepiaInfo?.exclusive ?? false}
                      onChange={(e) => {
                        onChangeProperty('linkedSalesChannel.benepia.exclusive', e.target.checked);
                      }}>
                      베네피아에만 노출
                    </Checkbox>
                    <Checkbox
                      style={{ width: '160px' }}
                      checked={benepiaInfo?.useCustomPrice ?? false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        onChangeProperty('linkedSalesChannel.benepia.useCustomPrice', checked);
                        if (checked && !(Number(benepiaInfo?.sellPrice) > 0)) {
                          onChangeProperty('linkedSalesChannel.benepia.sellPrice', Number(modifiedProduct?.fee) || 0);
                        }
                      }}>
                      베네피아 전용 가격
                    </Checkbox>
                  </div>
                  {benepiaInfo?.useCustomPrice ? (
                    <div style={{ marginTop: '8px', marginLeft: '8px', fontSize: '13px' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>베네피아 판매가</div>
                      <TextInput
                        type="number"
                        style={{ width: '140px' }}
                        value={benepiaInfo?.sellPrice ?? ''}
                        onChangeValue={(value) => {
                          const next = value === '' ? '' : Number(value);
                          onChangeProperty(
                            'linkedSalesChannel.benepia.sellPrice',
                            next === '' ? '' : Number.isFinite(next) ? next : 0,
                          );
                        }}
                      />
                    </div>
                  ) : null}
                </div>
                {/* 카카오 */}
                <div style={{ marginTop: '12px' }}>
                  <div
                    style={{
                      fontSize: '14px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '8px',
                      gap: '8px',
                    }}>
                    <div
                      style={{
                        color: '#3c1e1e',
                        backgroundColor: '#FEE500',
                        fontSize: '12px',
                        padding: '4px 8px',
                        borderRadius: '4px',
                      }}>
                      카카오톡 예약하기
                    </div>
                    <Select
                      value={
                        changedData?.linkedSalesChannel?.kakaoReservation?.status ||
                        modifiedProduct?.linkedSalesChannel?.kakaoReservation?.status
                      }
                      style={{ width: 120 }}
                      onChange={(value) => {
                        onChangeProperty('linkedSalesChannel.kakaoReservation.status', value);
                      }}
                      options={[
                        { value: LinkedSalesChannelStatus.REQUESTED, label: '생성 요청' },
                        { value: LinkedSalesChannelStatus.ACCEPTED, label: '생성 승인' },
                        { value: LinkedSalesChannelStatus.REJECTED, label: '생성 거부' },
                        { value: LinkedSalesChannelStatus.OPEN, label: '판매중' },
                        { value: LinkedSalesChannelStatus.CLOSE, label: '판매중지' },
                      ]}
                    />
                    <Button
                      disabled={
                        !(
                          changedData?.linkedSalesChannel?.kakaoReservation?.productUrl ||
                          modifiedProduct?.linkedSalesChannel?.kakaoReservation?.productUrl
                        )
                      }
                      onClick={() => {
                        window.open(
                          changedData?.linkedSalesChannel?.kakaoReservation?.productUrl ||
                            modifiedProduct?.linkedSalesChannel?.kakaoReservation?.productUrl,
                          '_blank',
                        );
                      }}>
                      이동하기
                    </Button>
                  </div>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 'bold' }}>카카오톡 예약하기 상품명</div>
                  <TextInput
                    style={{ maxWidth: '428px' }}
                    value={
                      changedData?.linkedSalesChannel?.kakaoReservation?.productName ||
                      modifiedProduct?.linkedSalesChannel?.kakaoReservation?.productName ||
                      ''
                    }
                    onChangeValue={(value) => {
                      onChangeProperty('linkedSalesChannel.kakaoReservation.productName', value);
                    }}></TextInput>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 'bold' }}>카카오톡 예약하기 상품 링크</div>
                  <TextInput
                    style={{ maxWidth: '428px' }}
                    value={
                      changedData?.linkedSalesChannel?.kakaoReservation?.productUrl ||
                      modifiedProduct?.linkedSalesChannel?.kakaoReservation?.productUrl
                    }
                    onChangeValue={(value) => {
                      onChangeProperty('linkedSalesChannel.kakaoReservation.productUrl', value);
                    }}></TextInput>
                </div>
                {/* <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 'bold' }}>옵션별 정산대상금액 설정</div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <div style={{ width: '320px' }}>옵션명</div>
                    <div style={{ width: '120px' }}>가격</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
                    {kakaotalkReservationOptionNamePrices.map((item: { key: string; value: number }, index: number) => {
                      return (
                        <div key={`optionNamePrices-${index}`} style={{ display: 'flex', gap: '4px' }}>
                          <TextInput
                            style={{ width: '320px' }}
                            value={item.key}
                            onChangeValue={(value) => {
                              onChangeProperty(
                                `linkedSalesChannel.kakaoReservation.optionNamePrices.${index}.key`,
                                value,
                              );
                            }}></TextInput>
                          <Input
                            type={'number'}
                            style={{
                              width: '120px',
                            }}
                            value={item.value}
                            onChange={(e) => {
                              onChangeProperty(
                                `linkedSalesChannel.kakaoReservation.optionNamePrices.${index}.value`,
                                Number(e.target.value),
                              );
                            }}
                          />
                          <Button
                            style={{ color: 'red' }}
                            onClick={() => {
                              const newValue = [...kakaotalkReservationOptionNamePrices];
                              newValue.splice(index, 1);
                              onChangeProperty('linkedSalesChannel.kakaoReservation.optionNamePrices', newValue);
                            }}>
                            삭제
                          </Button>
                        </div>
                      );
                    })}
                  </div>

                  <Button
                    onClick={() => {
                      const newValue = [...kakaotalkReservationOptionNamePrices];
                      newValue.push({ key: '', value: 0 });
                      onChangeProperty('linkedSalesChannel.kakaoReservation.optionNamePrices', newValue);
                    }}>
                    + 옵션별 정산대상금액 항목 추가
                  </Button>
                </div> */}
                {/* 이지웰 */}
                <div style={{ marginTop: '12px' }}>
                  <div
                    style={{
                      fontSize: '14px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '8px',
                      gap: '8px',
                      flexWrap: 'wrap',
                    }}>
                    <div
                      style={{
                        color: '#fff',
                        backgroundColor: '#0D6EFD',
                        fontSize: '12px',
                        padding: '4px 8px',
                        borderRadius: '4px',
                      }}>
                      이지웰
                    </div>
                    <Select
                      value={ezwelInfo?.status}
                      style={{ width: 120 }}
                      onChange={(value) => {
                        onChangeProperty('linkedSalesChannel.ezwel.status', value);
                        if (value === LinkedSalesChannelStatus.OPEN || value === LinkedSalesChannelStatus.CLOSE) {
                          if (ezwelInfo?.gdsCd) {
                            setEzwelProductInprogress(true);
                            updateEzwelProductStatusMutation.mutate({
                              productId: props.productId,
                              status: value as 'open' | 'close',
                            });
                          }
                        }
                      }}
                      options={[
                        { value: LinkedSalesChannelStatus.REQUESTED, label: '생성 요청' },
                        { value: LinkedSalesChannelStatus.ACCEPTED, label: '생성 승인' },
                        { value: LinkedSalesChannelStatus.REJECTED, label: '생성 거부' },
                        { value: LinkedSalesChannelStatus.OPEN, label: '판매중' },
                        { value: LinkedSalesChannelStatus.CLOSE, label: '판매중지' },
                      ]}
                    />
                    <Checkbox
                      style={{ width: '140px' }}
                      checked={ezwelInfo?.exclusive ?? false}
                      onChange={(e) => {
                        onChangeProperty('linkedSalesChannel.ezwel.exclusive', e.target.checked);
                      }}>
                      이지웰에만 노출
                    </Checkbox>
                    <Checkbox
                      style={{ width: '160px' }}
                      checked={ezwelInfo?.useCustomPrice ?? false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        onChangeProperty('linkedSalesChannel.ezwel.useCustomPrice', checked);
                        if (checked && !(Number(ezwelInfo?.sellPrice) > 0)) {
                          const sell = Number(modifiedProduct?.fee) || 0;
                          const margin =
                            ezwelInfo?.marginRate != null && Number.isFinite(Number(ezwelInfo.marginRate))
                              ? Number(ezwelInfo.marginRate)
                              : EZWEL_DEFAULT_MARGIN_RATE;
                          onChangeProperty('linkedSalesChannel.ezwel.sellPrice', sell);
                          onChangeProperty('linkedSalesChannel.ezwel.marginRate', margin);
                          onChangeProperty(
                            'linkedSalesChannel.ezwel.purchasePrice',
                            calcEzwelPurchasePrice(sell, margin),
                          );
                        }
                      }}>
                      이지웰 전용 가격
                    </Checkbox>
                    <Checkbox
                      style={{ width: '160px' }}
                      checked={!!ezwelInfo?.useSalePeriod}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          onChangeEzwelSalePeriod({
                            useSalePeriod: true,
                            sellStrtDtm: ezwelInfo?.sellStrtDtm || kstTodayYyyymmdd(),
                            sellEndDtm: ezwelInfo?.sellEndDtm || EZWEL_DEFAULT_SELL_END_DTM,
                          });
                          return;
                        }
                        onChangeEzwelSalePeriod({
                          useSalePeriod: false,
                          sellStrtDtm: kstTodayYyyymmdd(),
                          sellEndDtm: EZWEL_DEFAULT_SELL_END_DTM,
                        });
                      }}>
                      이지웰 특가기간
                    </Checkbox>
                    <LoadingButton
                      isLoading={ezwelProductInprogress}
                      disabled={!!ezwelInfo?.gdsCd}
                      onClick={async () => {
                        setEzwelProductInprogress(true);
                        const formData = new FormData();
                        formData.append('productId', props.productId);
                        formData.append('ezwel', JSON.stringify(buildEzwelFormPayload(ezwelInfo, props.productId)));
                        try {
                          appendEzwelExclusiveImagesToFormData(
                            formData,
                            files,
                            modifiedProduct.ezwelDetailTopImages || [],
                          );
                          await appendEzwelSandwichImagesToFormData(formData);
                        } catch (e) {
                          console.log(e);
                        }
                        createEzwelProductMutation.mutate(formData);
                      }}>
                      상품 등록
                    </LoadingButton>
                    <LoadingButton
                      isLoading={ezwelProductInprogress}
                      disabled={
                        !ezwelInfo?.gdsCd || ezwelInfo?.gdsApvStCd === 'REQS' || ezwelInfo?.gdsApvStCd === 'COMP'
                      }
                      onClick={() => {
                        setEzwelProductInprogress(true);
                        requestEzwelProductApprovalMutation.mutate(props.productId);
                      }}>
                      상품 승인 요청
                    </LoadingButton>
                    <LoadingButton
                      isLoading={ezwelProductInprogress}
                      disabled={!ezwelInfo?.gdsCd}
                      onClick={async () => {
                        setEzwelProductInprogress(true);
                        const formData = new FormData();
                        formData.append('productId', props.productId);
                        formData.append('ezwel', JSON.stringify(buildEzwelFormPayload(ezwelInfo, props.productId)));
                        try {
                          appendEzwelExclusiveImagesToFormData(
                            formData,
                            files,
                            modifiedProduct.ezwelDetailTopImages || [],
                          );
                          await appendEzwelSandwichImagesToFormData(formData);
                        } catch (e) {
                          console.log(e);
                        }
                        syncEzwelProductMutation.mutate(formData);
                      }}>
                      동기화
                    </LoadingButton>
                    <LoadingButton
                      isLoading={ezwelProductInprogress}
                      danger
                      disabled={!ezwelInfo?.gdsCd}
                      onClick={() => {
                        dialog.open({
                          title: '이지웰 연동 해제',
                          content: `우리 상품에서 이지웰 연동 정보만 제거합니다. 이지웰(HPAS) 상품은 삭제되지 않습니다.\n상품코드: ${ezwelInfo?.gdsCd}\n\n해제 후 협력사상품코드를 바꾸고 '상품 등록'하면 이지웰에 신규 등록됩니다.`,
                          type: 'ok_cancel',
                          confirmText: '연동 해제',
                          confirmColor: '#ff4d4f',
                          onConfirm: () => {
                            setEzwelProductInprogress(true);
                            unlinkEzwelProductMutation.mutate(props.productId);
                          },
                        });
                      }}>
                      연동 해제
                    </LoadingButton>
                    <Button
                      disabled={!ezwelInfo?.gdsCd}
                      onClick={() => {
                        window.open(`https://ez1-stg.ezwel.com/pc/product/detail/${ezwelInfo.gdsCd}`, '_blank');
                      }}>
                      이동하기
                    </Button>
                  </div>
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>이지웰 전용 상세 이미지</div>
                      <FileUploadButton
                        multiple
                        accept=".jpg,.jpeg,.png,.webp"
                        className="link-button"
                        style={{
                          fontSize: '14px',
                          height: '32px',
                          padding: '4px 15px',
                          borderRadius: '6px',
                          backgroundColor: '#ffffff',
                          border: '1px solid #d9d9d9',
                          marginLeft: '12px',
                        }}
                        onChange={(e) => {
                          if (e.target.files) {
                            onChangeEzwelDetailTopImageFiles(e.target.files);
                          }
                        }}>
                        이미지 추가
                      </FileUploadButton>
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', lineHeight: 1.5, marginBottom: '8px' }}>
                      취소·환불안내(첨부 1)가 항상 최상단에 자동 삽입됩니다. 여기 이미지는 그 다음,
                      기본정보·구매안내보다 위에 들어갑니다. 링크 URL은 없습니다. 등록/동기화 후에도 아래에 현재 반영된
                      이미지가 보입니다.
                    </div>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={onEzwelDetailTopImagesSortEnd}>
                      <SortableContext
                        items={(
                          modifiedProduct.ezwelDetailTopImagesTemp ||
                          modifiedProduct.ezwelDetailTopImages ||
                          []
                        ).map((item) => item)}
                        strategy={horizontalListSortingStrategy}>
                        <div style={{ display: 'flex', gap: '4px', overflow: 'auto', maxWidth: '600px' }}>
                          {(modifiedProduct.ezwelDetailTopImagesTemp || modifiedProduct.ezwelDetailTopImages || []).map(
                            (imageUrl: string, index: number) => (
                              <SortableDetailImage
                                key={`ezwel-top-${imageUrl}-${index}`}
                                id={imageUrl}
                                onDeleteDetailImage={() => onDeleteEzwelDetailTopImage(index)}
                                imageUrl={imageUrl}
                              />
                            ),
                          )}
                        </div>
                      </SortableContext>
                    </DndContext>
                    <div style={{ marginTop: '12px', padding: '8px', background: '#fafafa', borderRadius: '8px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '4px' }}>
                        현재 이지웰 상세에 반영된 이미지
                      </div>
                      <EzwelAppliedImageThumbs title="첨부 1 환불안내 (고정)" urls={[EZWEL_FIXED_TOP_IMAGE_URL]} />
                      <EzwelAppliedImageThumbs
                        title="추가 전용 이미지"
                        urls={(modifiedProduct.ezwelDetailTopImages || []).filter(
                          (url) => url.startsWith('/') || url.startsWith('http'),
                        )}
                      />
                      <EzwelAppliedImageThumbs
                        title="기본정보·구매안내 (등록/동기화 시 갱신)"
                        urls={modifiedProduct.ezwelProductInfoImages || []}
                      />
                      <EzwelAppliedImageThumbs
                        title="환불정책 (등록/동기화 시 갱신)"
                        urls={modifiedProduct.ezwelRefundPolicyImages || []}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '8px' }}>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>상품코드 (gdsCd)</div>
                      <div style={{ marginTop: '4px', minWidth: '160px' }}>
                        {ezwelInfo?.gdsCd ? (
                          <a
                            href={`https://ez1-stg.ezwel.com/pc/product/detail/${ezwelInfo.gdsCd}`}
                            target="_blank"
                            rel="noreferrer">
                            {ezwelInfo.gdsCd}
                          </a>
                        ) : (
                          '-'
                        )}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>협력사상품코드</div>
                      {ezwelInfo?.gdsCd ? (
                        <div style={{ marginTop: '4px', minWidth: '160px' }}>
                          {ezwelInfo?.cspGdsCd || props.productId || '-'}
                        </div>
                      ) : (
                        <div style={{ marginTop: '4px' }}>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <TextInput
                              style={{ width: '280px' }}
                              value={ezwelInfo?.cspGdsCd ?? props.productId ?? ''}
                              onChangeValue={(value) => onChangeProperty('linkedSalesChannel.ezwel.cspGdsCd', value)}
                            />
                            <LoadingButton
                              isLoading={ezwelProductInprogress}
                              onClick={() => {
                                setEzwelProductInprogress(true);
                                rotateEzwelCspGdsCdMutation.mutate(props.productId);
                              }}>
                              코드 변경
                            </LoadingButton>
                          </div>
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            기본은 아이고고 상품 ID. 코드 변경 후 상품 등록하면 이지웰에 신규 등록됩니다.
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>판매상태</div>
                      <div style={{ marginTop: '4px' }}>
                        {ezwelInfo?.gdsSellStCd
                          ? `${ezwelInfo.gdsSellStCd} (${
                              {
                                '1001': '등록대기',
                                '1002': '판매',
                                '1003': '변경대기',
                                '1004': '품절',
                                '1005': '판매중지',
                                '1021': '난수매칭대기',
                              }[ezwelInfo.gdsSellStCd] || '-'
                            })`
                          : '-'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>승인상태</div>
                      <div style={{ marginTop: '4px' }}>
                        {ezwelInfo?.gdsApvStCd
                          ? `${ezwelInfo.gdsApvStCd} (${
                              {
                                WAIT: '승인대기',
                                REQS: '승인요청',
                                CANL: '승인반려',
                                COMP: '승인완료',
                              }[ezwelInfo.gdsApvStCd] || '-'
                            })`
                          : '-'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>상품유형</div>
                      <div style={{ marginTop: '4px' }}>
                        {ezwelInfo?.gdsTypDtlCd
                          ? `${ezwelInfo.gdsTypDtlCd} (${
                              {
                                '2001': '모바일상품권',
                                '2002': '모바일E쿠폰',
                                '2003': '모바일이용권',
                              }[ezwelInfo.gdsTypDtlCd] || '-'
                            })`
                          : '-'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>발송/예약</div>
                      <div style={{ marginTop: '4px' }}>
                        {ezwelInfo?.intgGdsTypCd
                          ? `${ezwelInfo.intgGdsTypCd} (${
                              { SND: '발송상품', RSV: '예약상품' }[ezwelInfo.intgGdsTypCd] || '-'
                            })`
                          : '-'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>고시분류</div>
                      <div style={{ marginTop: '4px' }}>
                        {ezwelInfo?.gdsAnmtClsCd
                          ? `${ezwelInfo.gdsAnmtClsCd}${
                              ezwelInfo.gdsAnmtClsCd === '1038'
                                ? ' (모바일쿠폰)'
                                : ezwelInfo.gdsAnmtClsCd === '1037'
                                  ? ' (기타용역)'
                                  : ''
                            }`
                          : '-'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>난수설정</div>
                      <div style={{ marginTop: '4px' }}>
                        {ezwelInfo?.rdnoStupCd
                          ? `${ezwelInfo.rdnoStupCd} (${
                              {
                                '01': '협력사/실시간',
                                '02': '선발행·이지웰발송',
                                '03': '선발행·이지웰인증',
                              }[ezwelInfo.rdnoStupCd] || '-'
                            })`
                          : '-'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>브랜드</div>
                      <div style={{ marginTop: '4px' }}>
                        {ezwelInfo?.brandNm
                          ? `${ezwelInfo.brandNm} (${ezwelInfo.brandCd || '-'})`
                          : ezwelInfo?.brandCd || '-'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>관리카테고리</div>
                      <div style={{ marginTop: '4px' }}>{ezwelInfo?.mngStdCtgrCd || '-'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>전시카테고리</div>
                      <div style={{ marginTop: '4px' }}>{(ezwelInfo?.dspStdCtgrCdList || []).join(', ') || '-'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>판매기간</div>
                      {ezwelInfo?.useSalePeriod ? (
                        <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <DatePicker
                            allowClear={false}
                            style={{ width: '140px' }}
                            placeholder="시작일"
                            popupStyle={{ zIndex: 99999 }}
                            value={ezwelYyyymmddToDayjs(ezwelInfo?.sellStrtDtm)}
                            onChange={(value) => {
                              onChangeEzwelSalePeriod({
                                useSalePeriod: true,
                                sellStrtDtm: value ? value.format('YYYYMMDD') : '',
                              });
                            }}
                          />
                          <span>~</span>
                          <DatePicker
                            style={{ width: '140px' }}
                            placeholder="종료일"
                            popupStyle={{ zIndex: 99999 }}
                            value={ezwelYyyymmddToDayjs(ezwelInfo?.sellEndDtm)}
                            onChange={(value) => {
                              onChangeEzwelSalePeriod({
                                useSalePeriod: true,
                                sellEndDtm: value ? value.format('YYYYMMDD') : EZWEL_DEFAULT_SELL_END_DTM,
                              });
                            }}
                          />
                        </div>
                      ) : (
                        <div style={{ marginTop: '4px' }}>
                          {ezwelInfo?.sellStrtDtm || ezwelInfo?.sellEndDtm
                            ? `${formatEzwelDateLabel(ezwelInfo?.sellStrtDtm)} ~ ${formatEzwelDateLabel(
                                ezwelInfo?.sellEndDtm || EZWEL_DEFAULT_SELL_END_DTM,
                              )}`
                            : `배포 당일 ~ ${formatEzwelDateLabel(EZWEL_DEFAULT_SELL_END_DTM)}`}
                        </div>
                      )}
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>판매가</div>
                      <div style={{ marginTop: '4px' }}>
                        {ezwelInfo?.useCustomPrice ? (
                          <TextInput
                            type="number"
                            style={{ width: '140px' }}
                            value={ezwelInfo?.sellPrice ?? ''}
                            onChangeValue={(value) => {
                              onChangeEzwelPricing('sellPrice', parseEzwelPriceInput(value));
                            }}
                          />
                        ) : (
                          (ezwelPreviewSellPrice || ezwelInfo?.sellPrice || modifiedProduct?.fee)?.toLocaleString?.() ??
                          ezwelInfo?.sellPrice ??
                          modifiedProduct?.fee ??
                          '-'
                        )}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>매입가</div>
                      <div style={{ marginTop: '4px' }}>
                        <TextInput
                          type="number"
                          style={{ width: '140px' }}
                          value={
                            ezwelInfo?.purchasePrice != null
                              ? ezwelInfo.purchasePrice
                              : ezwelPreviewSellPrice > 0
                                ? ezwelPreviewPurchasePrice
                                : ''
                          }
                          onChangeValue={(value) => {
                            onChangeEzwelPricing('purchasePrice', parseEzwelPriceInput(value));
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>수익률</div>
                      <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <TextInput
                          type="number"
                          style={{ width: '80px' }}
                          value={
                            ezwelInfo?.marginRate != null
                              ? ezwelInfo.marginRate
                              : ezwelPreviewSellPrice > 0
                                ? ezwelPreviewMarginRate
                                : EZWEL_DEFAULT_MARGIN_RATE
                          }
                          onChangeValue={(value) => {
                            onChangeEzwelPricing('marginRate', parseEzwelPriceInput(value));
                          }}
                        />
                        %
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>등록시각</div>
                      <div style={{ marginTop: '4px' }}>
                        {ezwelInfo?.requestedAt ? dayjs(ezwelInfo.requestedAt).format('YYYY-MM-DD HH:mm') : '-'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>최근동기화</div>
                      <div style={{ marginTop: '4px' }}>
                        {ezwelInfo?.lastSyncedAt ? dayjs(ezwelInfo.lastSyncedAt).format('YYYY-MM-DD HH:mm') : '-'}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: '6px', fontSize: '12px', color: '#888' }}>
                    등록 시 유형=2002 모바일E쿠폰·발송(SND)·고시1038. 판매가·매입가·수익률은 자동 계산되며 등록/동기화
                    전에 수정할 수 있습니다. 전용 가격을 켜면 입력 판매가가 반영되고, 옵션가는 앱가 대비 같은 비율로
                    적용됩니다. 특가기간을 켜면 시작일~종료일이 HPAS 판매기간으로 전송됩니다. 끄면 시작일은 해제 당일,
                    종료일은 2099-12-31로 한 번 되돌아갑니다. 옵션코드=협력사상품코드_순번 자동채번. 신규 등록 시
                    승인요청(REQS)도 함께 진행되며, 승인대기·반려 상품은 승인 요청 버튼으로 재요청할 수 있습니다. STG
                    FO: <code>ez1-stg.ezwel.com/pc/product/detail/{'{gdsCd}'}</code>
                  </div>
                </div>
                {/* 네이버 */}
                <div style={{ marginTop: '12px' }}>
                  <div
                    style={{
                      fontSize: '14px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '8px',
                      gap: '8px',
                    }}>
                    <div
                      style={{
                        backgroundColor: '#03c75a',
                        color: 'white',
                        fontSize: '12px',
                        padding: '4px 8px',
                        borderRadius: '4px',
                      }}>
                      네이버스토어
                    </div>
                    <Select
                      value={naverStoreProductStatus}
                      style={{ width: 120 }}
                      onChange={(value) => {
                        onChangeProperty('linkedSalesChannel.naverStore.status', value);
                      }}
                      options={[
                        { value: LinkedSalesChannelStatus.REQUESTED, label: '생성 요청' },
                        { value: LinkedSalesChannelStatus.ACCEPTED, label: '생성 승인' },
                        { value: LinkedSalesChannelStatus.REJECTED, label: '생성 거부' },
                        { value: LinkedSalesChannelStatus.OPEN, label: '판매중' },
                        { value: LinkedSalesChannelStatus.CLOSE, label: '판매중지' },
                      ]}
                    />
                    {naverStoreProduct?.originProduct?.statusType && (
                      <div>{NaverCommerceProductStatusLabel[naverStoreProduct?.originProduct?.statusType]}</div>
                    )}
                    {naverStoreProduct?.smartstoreChannelProduct?.channelProductDisplayStatusType && (
                      <div>
                        {
                          NaverCommerceProductDisplayStatusLabel[
                            naverStoreProduct?.originProduct?.statusType === 'SUSPENSION'
                              ? 'SUSPENSION'
                              : naverStoreProduct?.smartstoreChannelProduct?.channelProductDisplayStatusType
                          ]
                        }
                      </div>
                    )}

                    <Button
                      disabled={
                        !(
                          changedData?.linkedSalesChannel?.naverStore?.channelProductNo ||
                          modifiedProduct?.linkedSalesChannel?.naverStore?.channelProductNo
                        )
                      }
                      onClick={() => {
                        const channelProductNo =
                          changedData?.linkedSalesChannel?.naverStore?.channelProductNo ||
                          modifiedProduct?.linkedSalesChannel?.naverStore?.channelProductNo;
                        window.open(`https://smartstore.naver.com/igogo/products/${channelProductNo}`, '_blank');
                      }}>
                      이동하기
                    </Button>
                    <LoadingButton
                      isLoading={naverProductInprogress}
                      onClick={async () => {
                        setNaverProductInprogress(true);
                        refreshNaverProductMutation.mutate(props.productId);
                      }}>
                      새로고침
                    </LoadingButton>
                    <LoadingButton
                      isLoading={naverProductInprogress}
                      disabled={
                        !!(
                          changedData?.linkedSalesChannel?.naverStore?.channelProductNo ||
                          modifiedProduct?.linkedSalesChannel?.naverStore?.channelProductNo
                        )
                      }
                      onClick={async () => {
                        setNaverProductInprogress(true);
                        const formData: FormData = new FormData();

                        const productInfo = document.getElementById('product-information-preview');
                        const node = document.getElementById('product-detail-preview');
                        const refundPolicy = document.getElementById('product-refund-policy-preview');

                        await appendNaverTopLinkImagesToFormData(formData, getNaverTopLinkBanners(modifiedProduct));

                        let detailBodyImages = [
                          ...(modifiedProduct.naverDetailBodyImagesTemp || modifiedProduct.naverDetailBodyImages || []),
                        ];

                        if (productInfo) {
                          const detailContentImageUrl = await domtoimage.toPng(productInfo, {
                            width: productInfo.offsetWidth * 3,
                            height: productInfo.offsetHeight * 3,
                            style: {
                              transform: `scale(3)`,
                              transformOrigin: 'top left',
                            },
                          });
                          const response = await axios.get(detailContentImageUrl!, { responseType: 'blob' });
                          const result = await resizeImage(response.data, {
                            format: 'image/png',
                            maxWidth: 1200,
                          });

                          const results = await splitImage(result.image);

                          results.forEach((item, index) => {
                            if (item.image) {
                              const file = new File(
                                [item.image],
                                `detailContentImage_${index}_product_info.png`,
                              ) as RcFile;
                              formData.append('detailContentImages', file);
                            }
                          });
                        }

                        if (modifiedProduct.detailType === 'images') {
                          detailBodyImages = [
                            ...(modifiedProduct.naverDetailBodyImagesTemp ||
                              modifiedProduct.naverDetailBodyImages ||
                              []),
                            ...(modifiedProduct.detailBodyImagesTemp || modifiedProduct.detailBodyImages || []),
                          ];
                        }

                        for (let i = 0; i < detailBodyImages.length; i++) {
                          const url = cdnImageUrl(detailBodyImages[i]);
                          const response = await axios.get(url!, { responseType: 'blob' });
                          const result = await resizeImage(response.data, {
                            maxWidth: 1200,
                            format: 'image/png',
                          });
                          const results = await splitImage(result.image);

                          results.forEach((item, index) => {
                            const file = new File([item.image], `detailContentImage_${i}_${index}.png`) as RcFile;
                            formData.append('detailContentImages', file);
                          });
                        }

                        if (modifiedProduct.detailType !== 'images' && node) {
                          const detailContentImageUrl = await domtoimage.toPng(node, {
                            width: node.offsetWidth * 3,
                            height: node.offsetHeight * 3,
                            style: {
                              transform: `scale(3)`,
                              transformOrigin: 'top left',
                            },
                          });

                          const response = await axios.get(detailContentImageUrl!, { responseType: 'blob' });
                          const result = await resizeImage(response.data, {
                            format: 'image/png',
                            maxWidth: 1440,
                          });

                          const results = await splitImage(result.image);

                          results.forEach((item, index) => {
                            if (item.image) {
                              const file = new File([item.image], `detailContentImage_${index}.png`) as RcFile;
                              formData.append('detailContentImages', file);
                            }
                          });
                        }

                        formData.append('productId', props.productId);

                        const imageUrls = modifiedProduct.detailImagesTemp || modifiedProduct.detailImages || [];

                        for (let i = 0; i < imageUrls.length; i++) {
                          const url = cdnImageUrl(imageUrls[i]);
                          const response = await axios.get(url!, { responseType: 'blob' });
                          const result = await resizeImage(response.data, {
                            format: 'image/png',
                            maxWidth: 1000,
                            maxHeight: 1000,
                          });
                          const file = new File([result.image], `image_${i}.png`) as RcFile;
                          formData.append('imageFiles', file);
                        }

                        if (refundPolicy) {
                          const detailContentImageUrl = await domtoimage.toPng(refundPolicy, {
                            width: refundPolicy.offsetWidth * 3,
                            height: refundPolicy.offsetHeight * 3,
                            style: {
                              transform: `scale(3)`,
                              transformOrigin: 'top left',
                            },
                          });
                          const response = await axios.get(detailContentImageUrl!, { responseType: 'blob' });
                          const result = await resizeImage(response.data, {
                            format: 'image/png',
                            maxWidth: 1200,
                          });

                          const results = await splitImage(result.image);

                          results.forEach((item, index) => {
                            if (item.image) {
                              const file = new File(
                                [item.image],
                                `detailContentImage_${index}_refund_policy.png`,
                              ) as RcFile;
                              formData.append('detailContentImages', file);
                            }
                          });
                        }
                        createNaverProductMutation.mutate(formData);
                      }}>
                      네이버 상품 만들기
                    </LoadingButton>
                  </div>
                </div>
                {naverStoreProductStatus === LinkedSalesChannelStatus.REJECTED && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: 'red' }}>생성 거부 사유</div>
                    <TextInput
                      style={{ maxWidth: '428px' }}
                      value={
                        changedData?.linkedSalesChannel?.naverStore?.rejectReason ||
                        modifiedProduct?.linkedSalesChannel?.naverStore?.rejectReason
                      }
                      onChangeValue={(value) => {
                        onChangeProperty('linkedSalesChannel.naverStore.rejectReason', value);
                      }}
                      onChange={(e) => {
                        onChangeProperty('linkedSalesChannel.naverStore.rejectReason', e.target.value);
                      }}></TextInput>
                  </div>
                )}
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 'bold' }}>채널 상품 번호(필수)</div>
                  <TextInput
                    style={{ maxWidth: '428px' }}
                    value={
                      changedData?.linkedSalesChannel?.naverStore?.channelProductNo ||
                      modifiedProduct?.linkedSalesChannel?.naverStore?.channelProductNo
                    }
                    onChangeValue={(value) => {
                      onChangeProperty('linkedSalesChannel.naverStore.channelProductNo', value);
                    }}
                    onChange={(e) => {
                      onChangeProperty('linkedSalesChannel.naverStore.channelProductNo', e.target.value);
                    }}></TextInput>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 'bold' }}>원 상품 번호(필수)</div>
                  <TextInput
                    style={{ maxWidth: '428px' }}
                    value={
                      changedData?.linkedSalesChannel?.naverStore?.originProductNo ||
                      modifiedProduct?.linkedSalesChannel?.naverStore?.originProductNo
                    }
                    onChangeValue={(value) => {
                      onChangeProperty('linkedSalesChannel.naverStore.originProductNo', value);
                    }}
                    onChange={(e) => {
                      onChangeProperty('linkedSalesChannel.naverStore.originProductNo', e.target.value);
                    }}></TextInput>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <div
                      style={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                      <div>상단 링크 이미지 (네이버 상세 최상단)</div>
                    </div>
                    <FileUploadButton
                      multiple
                      accept={['jpg', 'jpeg', 'png', 'tif', 'tiff', 'webp', 'svg']
                        .map((ext) => {
                          return '.' + ext;
                        })
                        .join(',')}
                      className="link-button"
                      style={{
                        fontSize: '14px',
                        height: '32px',
                        padding: '4px 15px',
                        borderRadius: '6px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #d9d9d9',
                        color: 'rgba(0, 0, 0, 0.88)',
                        boxShadow: '0 2px 0 rgba(0, 0, 0, 0.02)',
                        cursor: 'pointer',
                        marginLeft: '12px',
                      }}
                      onChange={(e) => {
                        if (e.target.files) {
                          onChangeNaverTopLinkBannerFiles(e.target.files);
                        }
                      }}>
                      {`이미지 추가 (${getNaverTopLinkBanners(modifiedProduct).length}/${NAVER_TOP_LINK_BANNER_MAX})`}
                    </FileUploadButton>
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', lineHeight: 1.5, marginBottom: '8px' }}>
                    네이버 상품 만들기/연동 시에만 상세 최상단으로 반영되며, 앱 상품 상세에는 노출되지 않습니다.
                    <br />
                    네이버가 허용하지 않는 URL은 링크와 이미지가 함께 삭제될 수 있습니다.
                    <br />
                    허용 예: smartstore.naver.com, blog.naver.com, cafe.naver.com 및 일부 SNS
                  </div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={onNaverTopLinkBannersSortEnd}>
                    <SortableContext
                      items={getNaverTopLinkBanners(modifiedProduct).map((item, index) => `${item.image}__${index}`)}
                      strategy={verticalListSortingStrategy}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '640px' }}>
                        {getNaverTopLinkBanners(modifiedProduct).map((item, index) => (
                          <SortableNaverTopLinkBanner
                            key={`${item.image}__${index}`}
                            id={`${item.image}__${index}`}
                            imageUrl={item.image}
                            linkUrl={item.url}
                            onChangeUrl={(url) => onChangeNaverTopLinkBannerUrl(index, url)}
                            onDelete={() => onDeleteNaverTopLinkBanner(index)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <div
                      style={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                      <div>상세 소개 이미지(네이버 상단 추가)</div>
                      <Button
                        onClick={async () => {
                          const images =
                            modifiedProduct.naverDetailBodyImagesTemp || modifiedProduct.naverDetailBodyImages || [];
                          for (let i = 0; i < images.length; i++) {
                            const imageUrl = cdnImageUrl(images[i]);
                            if (imageUrl) {
                              const response = await fetch(imageUrl);
                              response.blob().then((blob) => {
                                resizeImage(blob, {
                                  format: 'image/png',
                                }).then((result) => {
                                  const url = window.URL.createObjectURL(result.image);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `${modifiedProduct.sellerName}_${modifiedProduct.title.slice(
                                    0,
                                    10,
                                  )}_네이버상세_${i}`;
                                  a.click();
                                });
                              });
                            }
                          }
                        }}>
                        전체 다운로드
                      </Button>
                    </div>
                    <FileUploadButton
                      multiple
                      accept={['jpg', 'jpeg', 'png', 'tif', 'tiff', 'webp', 'svg']
                        .map((ext) => {
                          return '.' + ext;
                        })
                        .join(',')}
                      className="link-button"
                      style={{
                        fontSize: '14px',
                        height: '32px',
                        padding: '4px 15px',
                        borderRadius: '6px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #d9d9d9',
                        color: 'rgba(0, 0, 0, 0.88)',
                        boxShadow: '0 2px 0 rgba(0, 0, 0, 0.02)',
                        cursor: 'pointer',
                        marginLeft: '12px',
                      }}
                      onChange={(e) => {
                        if (e.target.files) {
                          const files = e.target.files;
                          onChangeNaverDetailBodyImageFiles(files);
                        }
                      }}>
                      상세 소개 이미지 추가(네이버 본문 상단)
                    </FileUploadButton>
                  </div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={onNaverDetailBodyImagesSortEnd}>
                    <SortableContext
                      items={(
                        modifiedProduct.naverDetailBodyImagesTemp ||
                        modifiedProduct.naverDetailBodyImages ||
                        []
                      ).map((item) => item)}
                      strategy={horizontalListSortingStrategy}>
                      <div style={{ display: 'flex', gap: '4px', overflow: 'auto', maxWidth: '600px' }}>
                        {(modifiedProduct.naverDetailBodyImagesTemp || modifiedProduct.naverDetailBodyImages || []).map(
                          (imageUrl: string, index: number) => {
                            return (
                              <SortableDetailImage
                                key={`image-${imageUrl}-${index}`}
                                id={imageUrl}
                                onDeleteDetailImage={() => {
                                  onDeleteNaverDetailBodyImage(index);
                                }}
                                imageUrl={imageUrl}
                              />
                            );
                          },
                        )}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: '120px' }}>판매가</th>
                        <th style={{ width: '120px' }}>정가</th>
                        <th style={{ width: '80px' }}>할인율</th>
                        <th style={{ width: '180px' }}>기간할인(시작)</th>
                        <th style={{ width: '180px' }}>기간할인(끝)</th>
                        <th style={{ width: '100px' }}>기간할인 판매가</th>
                        <th style={{ width: '100px' }}>기간할인 적용</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <TextInput
                            disabled
                            style={{ width: '100px', textAlign: 'right' }}
                            value={
                              (naverStoreProduct?.originProduct?.salePrice || 0) -
                              (naverStoreProduct?.originProduct?.customerBenefit?.immediateDiscountPolicy
                                ?.discountMethod?.value || 0)
                            }></TextInput>
                          <span>원</span>
                        </td>
                        <td>
                          <TextInput
                            disabled
                            style={{ width: '100px', textAlign: 'right' }}
                            value={naverStoreProduct?.originProduct?.salePrice}></TextInput>
                          <span>원</span>
                        </td>
                        <td style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {`${Math.round(
                            ((naverStoreProduct?.originProduct?.customerBenefit?.immediateDiscountPolicy?.discountMethod
                              ?.value || 0) /
                              (naverStoreProduct?.originProduct?.salePrice || 1)) *
                              100,
                          )}%`}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {`${naverStoreProduct?.originProduct?.customerBenefit?.reservedDiscountPolicy?.discountMethod?.startDate}`}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {`${naverStoreProduct?.originProduct?.customerBenefit?.reservedDiscountPolicy?.discountMethod?.endDate}`}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {`${
                            (naverStoreProduct?.originProduct?.salePrice || 0) -
                            (naverStoreProduct?.originProduct?.customerBenefit?.reservedDiscountPolicy?.discountMethod
                              ?.value || 0)
                          }원`}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {new Date(
                            naverStoreProduct?.originProduct?.customerBenefit?.reservedDiscountPolicy?.discountMethod
                              ?.endDate || '',
                          ).getTime() > new Date().getTime()
                            ? '적용됨'
                            : '-'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div
                    style={{
                      fontSize: '14px',
                      fontWeight: 'bold',
                    }}>{`옵션 (${naverStoreProduct?.originProduct?.detailAttribute?.optionInfo?.optionCombinations?.length})`}</div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      overflow: 'auto',
                      maxHeight: '480px',
                    }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <div style={{ width: '80px' }}>재고</div>
                      <div style={{ width: '240px' }}>옵션</div>
                      <div style={{ width: '120px' }}>플랫폼 재고</div>
                      <div></div>
                    </div>
                    {(naverStoreProduct?.originProduct?.detailAttribute?.optionInfo?.optionCombinations || []).map(
                      (option, index) => {
                        let platformInventory = 0;
                        const step = [option.optionName1];
                        if (option?.optionName2) {
                          step.push(option?.optionName2);
                        }
                        if (option?.optionName3) {
                          step.push(option?.optionName3);
                        }

                        const key = step.map((step) => step.trim()).join(' / ');
                        if (key) {
                          platformInventory = optionsDictRef.current[key];
                        }
                        return (
                          <div key={`naver-option-compare-${index}`} style={{ display: 'flex', gap: '8px' }}>
                            <div style={{ width: '80px' }}>{`${option.stockQuantity}`}</div>
                            <div style={{ width: '240px' }}>{`${key}`}</div>
                            <div style={{ width: '120px' }}>{platformInventory}</div>
                            <div style={{ color: 'red' }}>
                              {platformInventory !== option.stockQuantity && '재고 불일치'}
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div style={{ padding: '4px' }}></div>
          </div>
        </div>
        <div style={{ width: '480px' }}>
          <ProductDetailView
            product={{
              ...modifiedProduct,
            }}></ProductDetailView>
        </div>
      </div>
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          left: 0,
          height: '44px',
          backgroundColor: '#eaeaea',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '12px',
          paddingRight: '24px',
        }}>
        <Button
          disabled={!draftProductQuery.data?.data.isTemp}
          onClick={() => {
            mutationDeleteDraftProduct.mutate(props.productId);
          }}>
          임시 데이터 제거
        </Button>
        <LoadingButton
          isLoading={deployInprogress}
          disabled={deployInprogress}
          onClick={async () => {
            setDeployInprogress(true);
            const formData: FormData = new FormData();
            const json = removeTempProperties(changedData);
            json.isDistribution = true;
            try {
              const draftProduct = draftProductQuery.data?.data;
              const product = productQuery.data?.data;
              const naverTopLinkChanged =
                Object.prototype.hasOwnProperty.call(json, 'naverTopLinkBanners') ||
                (Boolean(product?._id) &&
                  Boolean(modifiedProduct?._id) &&
                  naverLinkBannerKey(modifiedProduct.naverTopLinkBanners) !==
                    naverLinkBannerKey(product?.naverTopLinkBanners)) ||
                (Boolean(product?._id) &&
                  Boolean(draftProduct?._id) &&
                  naverLinkBannerKey(draftProduct?.naverTopLinkBanners) !==
                    naverLinkBannerKey(product?.naverTopLinkBanners)) ||
                (Boolean(naverStoreProduct?.originProduct) &&
                  isNaverTopLinkMissingOnStore(
                    getNaverTopLinkBanners(modifiedProduct),
                    naverStoreProduct?.originProduct?.detailContent,
                  ));
              const naverDetailBodyChanged =
                (Boolean(product?._id) &&
                  Boolean(modifiedProduct?._id) &&
                  JSON.stringify(modifiedProduct.naverDetailBodyImages || []) !==
                    JSON.stringify(product?.naverDetailBodyImages || [])) ||
                (Boolean(product?._id) &&
                  Boolean(draftProduct?._id) &&
                  JSON.stringify(draftProduct?.naverDetailBodyImages || []) !==
                    JSON.stringify(product?.naverDetailBodyImages || []));
              const detailChanged =
                naverTopLinkChanged ||
                naverDetailBodyChanged ||
                Object.keys(json).find(
                  (key) =>
                    key.startsWith('detail') ||
                    key.startsWith('naverDetail') ||
                    key.startsWith('ezwelDetail') ||
                    key.startsWith('durations') ||
                    key.startsWith('tags') ||
                    key.startsWith('students') ||
                    key.startsWith('age') ||
                    key.startsWith('refundPolicy') ||
                    key.startsWith('sellerComment') ||
                    key.startsWith('location') ||
                    key.startsWith('activityType'),
                ) ||
                (draftProduct?.isTemp &&
                  (JSON.stringify(draftProduct?.detailBodyImages) !== JSON.stringify(product?.detailBodyImages) ||
                    JSON.stringify(draftProduct?.naverTopLinkBanners) !==
                      JSON.stringify(product?.naverTopLinkBanners) ||
                    JSON.stringify(draftProduct?.details) !== JSON.stringify(product?.details) ||
                    JSON.stringify(draftProduct?.durations) !== JSON.stringify(product?.durations) ||
                    JSON.stringify(draftProduct?.tags) !== JSON.stringify(product?.tags) ||
                    JSON.stringify(draftProduct?.location) !== JSON.stringify(product?.location) ||
                    draftProduct?.studentsMin !== product?.studentsMin ||
                    draftProduct?.studentsMax !== product?.studentsMax ||
                    draftProduct?.ageMin !== product?.ageMin ||
                    draftProduct?.ageMax !== product?.ageMax ||
                    draftProduct?.refundPolicyText !== product?.refundPolicyText ||
                    draftProduct?.sellerComment !== product?.sellerComment ||
                    draftProduct?.activityType !== product?.activityType));

              const detailImageChanged =
                Object.keys(json).find((key) => key.startsWith('detailImage')) ||
                Object.keys(modifiedProduct.productChanges || {}).find((key) => key.startsWith('detailImage')) ||
                (draftProduct?.isTemp &&
                  JSON.stringify(draftProduct?.detailImages) !== JSON.stringify(product?.detailImages));

              if (detailChanged && modifiedProduct?.linkedSalesChannel?.naverStore?.originProductNo) {
                const productInfo = document.getElementById('product-information-preview');
                const node = document.getElementById('product-detail-preview');
                const refundPolicy = document.getElementById('product-refund-policy-preview');

                await appendNaverTopLinkImagesToFormData(formData, getNaverTopLinkBanners(modifiedProduct));

                if (productInfo) {
                  const detailContentImageUrl = await domtoimage.toPng(productInfo, {
                    width: productInfo.offsetWidth * 3,
                    height: productInfo.offsetHeight * 3,
                    style: {
                      transform: `scale(3)`,
                      transformOrigin: 'top left',
                    },
                  });

                  const response = await axios.get(detailContentImageUrl!, { responseType: 'blob' });
                  const result = await resizeImage(response.data, {
                    format: 'image/png',
                    maxWidth: 1200,
                  });

                  const results = await splitImage(result.image);

                  results.forEach((item, index) => {
                    if (item.image) {
                      const file = new File([item.image], `detailContentImage_${index}_product_info.png`) as RcFile;
                      formData.append('detailContentImages', file);
                    }
                  });
                }

                let detailBodyImages = [
                  ...(modifiedProduct.naverDetailBodyImagesTemp || modifiedProduct.naverDetailBodyImages || []),
                ];

                if (modifiedProduct.detailType === 'images') {
                  detailBodyImages = [
                    ...(modifiedProduct.naverDetailBodyImagesTemp || modifiedProduct.naverDetailBodyImages || []),
                    ...(modifiedProduct.detailBodyImagesTemp || modifiedProduct.detailBodyImages || []),
                  ];
                }

                for (let i = 0; i < detailBodyImages.length; i++) {
                  const url = cdnImageUrl(detailBodyImages[i]);
                  const response = await axios.get(url!, { responseType: 'blob' });
                  const result = await resizeImage(response.data, {
                    maxWidth: 1200,
                    format: 'image/png',
                  });
                  const results = await splitImage(result.image);

                  results.forEach((item, index) => {
                    const file = new File([item.image], `detailContentImage_${i}_${index}.png`) as RcFile;
                    formData.append('detailContentImages', file);
                  });
                }

                if (modifiedProduct.detailType !== 'images' && node) {
                  const templateDetailImage =
                    modifiedProduct.details?.detailImageTemp || modifiedProduct.details?.detailImage;
                  if (templateDetailImage) {
                    const response = await axios.get(cdnImageUrl(templateDetailImage)!, { responseType: 'blob' });
                    const result = await resizeImage(response.data, {
                      format: 'image/png',
                      maxWidth: 1200,
                    });
                    const results = await splitImage(result.image);

                    results.forEach((item, index) => {
                      const file = new File([item.image], `templateDetailImage_${index}.png`) as RcFile;
                      formData.append('detailContentImages', file);
                    });
                  }

                  const detailContentImageUrl = await domtoimage.toPng(node, {
                    width: node.offsetWidth * 3,
                    height: node.offsetHeight * 3,
                    style: {
                      transform: `scale(3)`,
                      transformOrigin: 'top left',
                    },
                  });

                  const response = await axios.get(detailContentImageUrl!, { responseType: 'blob' });
                  const result = await resizeImage(response.data, {
                    format: 'image/png',
                    maxWidth: 1440,
                  });
                  const results = await splitImage(result.image);
                  results.forEach((item, index) => {
                    if (item.image) {
                      const file = new File([item.image], `detailContentImage_${index}.png`) as RcFile;
                      formData.append('detailContentImages', file);
                    }
                  });
                }

                if (refundPolicy) {
                  const detailContentImageUrl = await domtoimage.toPng(refundPolicy, {
                    width: refundPolicy.offsetWidth * 3,
                    height: refundPolicy.offsetHeight * 3,
                    style: {
                      transform: `scale(3)`,
                      transformOrigin: 'top left',
                    },
                  });

                  const response = await axios.get(detailContentImageUrl!, { responseType: 'blob' });
                  const result = await resizeImage(response.data, {
                    format: 'image/png',
                    maxWidth: 1200,
                  });

                  const results = await splitImage(result.image);

                  results.forEach((item, index) => {
                    if (item.image) {
                      const file = new File([item.image], `detailContentImage_${index}_refund_policy.png`) as RcFile;
                      formData.append('detailContentImages', file);
                    }
                  });
                }
              }

              if (detailImageChanged) {
                const imageUrls = modifiedProduct.detailImagesTemp || modifiedProduct.detailImages || [];

                for (let i = 0; i < imageUrls.length; i++) {
                  const url = cdnImageUrl(imageUrls[i]);
                  const response = await axios.get(url!, { responseType: 'blob' });
                  const result = await resizeImage(response.data, {
                    format: 'image/png',
                    maxWidth: 1000,
                    maxHeight: 1000,
                  });
                  const file = new File([result.image], `image_${i}.png`) as RcFile;
                  formData.append('imageFiles', file);
                }
              }
              // eslint-disable-next-line no-empty
            } catch {}
            if (modifiedProduct?.linkedSalesChannel?.ezwel?.gdsCd) {
              try {
                await appendEzwelSandwichImagesToFormData(formData);
              } catch (e) {
                console.log(e);
              }
            }
            formData.append('productId', props.productId);
            formData.append('json', JSON.stringify(json));

            Object.values(files).forEach((file) => {
              formData.append('files', file);
            });
            mutationUpdateProduct.mutate(formData);
          }}>
          저장 후 배포
        </LoadingButton>
        <Button
          disabled={loadingEditorImageUpload}
          onClick={() => {
            const formData: FormData = new FormData();
            const json = removeTempProperties(changedData);
            formData.append('productId', props.productId);
            formData.append('json', JSON.stringify(json));

            Object.values(files).forEach((file) => {
              formData.append('files', file);
            });
            mutationUpdateProduct.mutate(formData);
          }}>
          저장
        </Button>

        <Button
          onClick={() => {
            props.onClose && props.onClose();
          }}>
          닫기
        </Button>
      </div>
    </div>
  );
};

// SortableLabelItem 컴포넌트
interface SortableLabelItemProps {
  id: string;
  label: LabelInfo;
  onDelete: () => void;
  onEdit: (updatedLabel: LabelInfo) => void;
}

const SortableLabelItem: React.FC<SortableLabelItemProps> = ({ id, label, onDelete, onEdit }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(label.name);
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleSave = () => {
    if (editName.trim()) {
      onEdit({
        ...label,
        name: editName.trim(),
      });
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditName(label.name);
    setIsEditing(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        padding: '6px 12px',
        backgroundColor: label.source === 'admin' ? '#fff2f0' : '#f0f8ff',
        border: `1px solid ${label.source === 'admin' ? '#ff4d4f' : '#1890ff'}`,
        borderRadius: '16px',
        fontSize: '14px',
        cursor: 'grab',
        userSelect: 'none',
        gap: '8px',
      }}
      {...attributes}
      {...listeners}>
      {isEditing ? (
        <>
          <Input
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onPressEnter={handleSave}
            onBlur={handleSave}
            autoFocus
            style={{ width: '120px', fontSize: '12px' }}
          />
          <Button size="small" type="text" onClick={handleSave} style={{ padding: '0 4px' }}>
            ✓
          </Button>
          <Button size="small" type="text" onClick={handleCancel} style={{ padding: '0 4px' }}>
            ✕
          </Button>
        </>
      ) : (
        <>
          <span>{label.name}</span>
          <Button size="small" type="text" onClick={() => setIsEditing(true)} style={{ padding: '0 4px' }}>
            ✎
          </Button>
          <Button size="small" type="text" onClick={onDelete} style={{ padding: '0 4px', color: '#ff4d4f' }}>
            ✕
          </Button>
        </>
      )}
    </div>
  );
};

// 라벨 추가 모달 컴포넌트
interface AddLabelModalProps {
  visible: boolean;
  onCancel: () => void;
  onAdd: (labelName: string) => void;
}

const AddLabelModal: React.FC<AddLabelModalProps> = ({ visible, onCancel, onAdd }) => {
  const [labelName, setLabelName] = useState('');

  const handleAdd = () => {
    if (labelName.trim()) {
      onAdd(labelName.trim());
      setLabelName('');
    }
  };

  return (
    <Modal
      title="라벨 추가"
      open={visible}
      onOk={handleAdd}
      onCancel={() => {
        setLabelName('');
        onCancel();
      }}
      okText="추가"
      cancelText="취소">
      <div style={{ marginBottom: '16px' }}>
        <div style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}>라벨 이름</div>
        <Input
          value={labelName}
          onChange={(e) => setLabelName(e.target.value)}
          onPressEnter={handleAdd}
          placeholder="라벨 이름을 입력하세요"
          autoFocus
        />
      </div>
    </Modal>
  );
};

export default ProductEditView;
